/* eslint-disable */
import React, { useEffect, useState, useRef } from 'react';import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { uploadDocumentWithDedupe } from '../utils/uploadDocument';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { ArrowLeft, Building2, MapPin, Globe, Mail, Phone, FlaskConical, FileText, Download, Send, Plus, Brain, User, Pencil, Trash2, Search, Check, ClipboardCopy, ClipboardPaste, Package, ChevronDown, ChevronLeft, ChevronRight, X, Sparkles, Loader2, MessageSquare, Share2, ArrowRightLeft, Upload, Star, EyeOff, Eye, BarChart3, Mic, MicOff, Briefcase, Ban, Flag, GitMerge, GripVertical, Users, ArrowUpRight, Clock, Link2Off, Handshake, ShieldAlert, Lock, AlertTriangle, RotateCcw } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { CopyEmail } from '@/components/CopyEmail';
import SentEmailDialog from '@/components/SentEmailDialog';
import AssetRecoveryDialog from '@/components/AssetRecoveryDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { CountrySelect } from '@/components/CountrySelect';
import { CompanySelect } from '@/components/CompanySelect';
import { OUTREACH_STATUSES as CANONICAL_OUTREACH_STATUSES, OUTREACH_STATUS_COLORS } from '@/lib/statusColors';
import DataRoomPanel from '@/components/DataRoomPanel';
import { FundraisingTrack } from '@/components/investors/FundraisingTrack';

const API = '/api';

const STAGE_COLORS = {
  'Discovery': 'bg-zinc-100 text-zinc-700',
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'IND': 'bg-sky-100 text-sky-700',
  'Phase 1': 'bg-blue-100 text-blue-700',
  'Phase 2': 'bg-violet-100 text-violet-700',
  'Phase 3': 'bg-purple-100 text-purple-700',
  'Clinical Stage': 'bg-fuchsia-100 text-fuchsia-700',
  'Commercial': 'bg-emerald-100 text-emerald-700',
  'Platform': 'bg-amber-100 text-amber-700',
  'Drug Delivery System': 'bg-amber-100 text-amber-700',
};

const OUTREACH_STATUSES = CANONICAL_OUTREACH_STATUSES;
const OUTREACH_COLORS = OUTREACH_STATUS_COLORS;

const COMPANY_STATUS_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'Closed': 'bg-emerald-100 text-emerald-700',
  'Dropped': 'bg-rose-100 text-rose-700',
  // Outreach pipeline statuses inherit from the canonical map so a
  // company-level "Asset Under Evaluation" chip looks identical to the
  // outreach badge.
  ...OUTREACH_STATUS_COLORS,
};

/** Compact, expandable list for comma/semicolon-separated taxonomies
    (therapeutic areas, indications). Renders as plain inline text with
    subtle separators — NOT colored chips — so the actual status badges
    (Phase 1, No Contact, etc.) remain the visual anchor on each card. */
function TaChips({ value, initial = 6 }) {
  const [expanded, setExpanded] = useState(false);
  const items = (value || '')
    .split(/[,;|]+/)
    .map(s => s.trim())
    .filter(Boolean);
  if (items.length === 0) return null;
  const visible = expanded ? items : items.slice(0, initial);
  const hidden = items.length - visible.length;
  return (
    <div className="mt-1 text-xs text-slate-600 leading-relaxed" data-testid="ta-chips">
      {visible.map((t, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-500 mx-2 font-semibold">·</span>}
          <span>{t}</span>
        </React.Fragment>
      ))}
      {hidden > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-slate-400 hover:text-slate-700 ml-2 underline underline-offset-2"
          data-testid="ta-chips-more"
        >
          +{hidden} more
        </button>
      )}
      {expanded && items.length > initial && (
        <button
          onClick={() => setExpanded(false)}
          className="text-slate-400 hover:text-slate-700 ml-2 underline underline-offset-2"
          data-testid="ta-chips-less"
        >
          show fewer
        </button>
      )}
    </div>
  );
}


/**
 * DataAccessContactsButton
 * --------------------------------------------------------------------------
 * Compact "👥 N" badge that opens a popover for managing the per-CDA data
 * access roster (ad-hoc evaluators beyond the signing person). Adds via
 * POST /documents/{id}/data-access-contacts, removes via DELETE.
 * Idempotent on email (server-side) so the same person can't be added twice.
 */
function DataAccessContactsButton({ doc, onAdd, onRemove }) {
  const contacts = doc?.data_access_contacts || [];
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', designation: '', organization: '' });
  const reset = () => setForm({ name: '', email: '', designation: '', organization: '' });

  const submit = async () => {
    const { name, email } = form;
    if (!name.trim() && !email.trim()) return;
    await onAdd({ name: name.trim(), email: email.trim(), designation: form.designation.trim(), organization: form.organization.trim() });
    reset();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition shrink-0 ${
            contacts.length > 0
              ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
              : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
          }`}
          title={contacts.length > 0 ? `${contacts.length} evaluator${contacts.length !== 1 ? 's' : ''} with data access` : 'Add evaluator with data access'}
          data-testid={`cda-data-access-btn-${doc.id}`}
        >
          <Users className="w-3 h-3" />
          <span className="leading-none">+{contacts.length || ''}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0"
        align="end"
        data-testid={`cda-data-access-popover-${doc.id}`}
      >
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              Data Access — {doc.counterparty_name || 'Counterparty'}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Evaluators with data-room access (not part of CRM contacts).
            </p>
          </div>
          <button
            type="button"
            onClick={() => { reset(); setOpen(false); }}
            className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded px-2 py-1 transition shrink-0"
            data-testid={`cda-data-access-done-btn-${doc.id}`}
            title="Close — your additions are already saved"
          >
            Done
          </button>
        </div>

        {contacts.length > 0 && (
          <div className="max-h-48 overflow-y-auto py-1">
            {contacts.map(c => (
              <div
                key={c.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 group"
                data-testid={`cda-data-access-contact-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">{c.name || '—'}</p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-500">
                    {c.email && <span className="truncate">{c.email}</span>}
                    {c.designation && <span className="truncate italic">· {c.designation}</span>}
                  </div>
                  {c.organization && (
                    <p className="text-[10px] text-slate-400 truncate">{c.organization}</p>
                  )}
                </div>
                <button
                  onClick={() => onRemove(c.id)}
                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition shrink-0"
                  data-testid={`cda-data-access-remove-${c.id}`}
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-slate-100 p-3 space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <Input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Name"
              className="h-7 text-xs"
              data-testid={`cda-data-access-name-input-${doc.id}`}
            />
            <Input
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              type="email"
              className="h-7 text-xs"
              data-testid={`cda-data-access-email-input-${doc.id}`}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Input
              value={form.designation}
              onChange={e => setForm({ ...form, designation: e.target.value })}
              placeholder="Designation (optional)"
              className="h-7 text-xs"
            />
            <Input
              value={form.organization}
              onChange={e => setForm({ ...form, organization: e.target.value })}
              placeholder="Org (optional)"
              className="h-7 text-xs"
            />
          </div>
          <Button
            onClick={submit}
            size="sm"
            className="w-full h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
            disabled={!form.name.trim() && !form.email.trim()}
            data-testid={`cda-data-access-add-btn-${doc.id}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Add Evaluator
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default function CompanyDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link target asset_id (set by global search → "/biotech/<id>?asset_id=<aid>").
  // Once we've used it to position the carousel we strip the param so a
  // subsequent reload doesn't lock the user to that asset forever.
  const deepLinkAssetId = searchParams.get('asset_id');
  // Deep-link target identifiers from global search (biotech_contacts route).
  // We accept either `contact_id` (rare — IDs match between collections) or
  // `contact_email` (default — emails are the cross-walk between
  // `biotech_contacts` and `biotech_companies.contacts[]`).
  const deepLinkContactId = searchParams.get('contact_id');
  const deepLinkContactEmail = (searchParams.get('contact_email') || '').toLowerCase();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [docs, setDocs] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [allOutreach, setAllOutreach] = useState([]);
  const [bulkSendCount, setBulkSendCount] = useState(0);
  const [includeBulkSends, setIncludeBulkSends] = useState(() => localStorage.getItem('tracker_include_bulk') === '1');
  useEffect(() => {
    localStorage.setItem('tracker_include_bulk', includeBulkSends ? '1' : '0');
    if (includeBulkSends) setShowIntroOnlyGroups(true);
    if (!allOutreach.length) return;
    const isBulk = (o) => o.status === 'Introduction Made' && !(o.source_id && String(o.source_id).trim());
    const rows = includeBulkSends ? allOutreach : allOutreach.filter(o => !isBulk(o));
    setOutreach([...rows].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
  }, [includeBulkSends]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(true);
  const [outreachSearch, setOutreachSearch] = useState('');
  const [fundraisingCount, setFundraisingCount] = useState(0);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const [outreachStatusFilter, setOutreachStatusFilter] = useState('All');
  const [showIntroOnlyGroups, setShowIntroOnlyGroups] = useState(false);
  const [outreachTab, setOutreachTab] = useState('pharma');
  useEffect(() => {
    if (!id) return;
    axios.get(`${API}/fundraising/${id}`, { withCredentials: true }).then(r => setFundraisingCount((r.data?.items || []).length)).catch(() => {});
  }, [id, outreachTab]);
  // Feb 2026 (v7) — asset filter + grouped view (Pharma × Asset = 1 opportunity)
  // brought over from the standalone /outreach page. Grouped view is the
  // default so users see one row per BD opportunity, expandable to reveal
  // each contact at that pharma × asset.
  const [outreachAssetFilter, setOutreachAssetFilter] = useState('__all__');
  // Sep 2026 (Golden Biotech): a stale asset filter (asset merged/renamed →
  // canonical name changed) hid every row with a blank dropdown. Self-heal.
  useEffect(() => {
    if (outreachAssetFilter === '__all__') return;
    const names = new Set(outreach.map(o => (o.asset_name_canonical || o.asset_name || '').trim()));
    if (!names.has(outreachAssetFilter)) setOutreachAssetFilter('__all__');
  }, [outreach, outreachAssetFilter]);
  const [outreachViewMode, setOutreachViewMode] = useState('grouped');
  const [expandedOutreachGroups, setExpandedOutreachGroups] = useState(() => new Set());
  // Coerce any legacy 'investor' state → 'fundraising' (Investors re-launched Oct 2026).
  useEffect(() => {
    if (outreachTab === 'investor' || outreachTab === 'investors') setOutreachTab('fundraising');
  }, [outreachTab]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewEmailRecord, setViewEmailRecord] = useState(null);
  // Merge another biotech record into this one
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergePicked, setMergePicked] = useState(null);
  const [merging, setMerging] = useState(false);
  const [statusReasonDialog, setStatusReasonDialog] = useState({ open: false, outreachId: null, status: '' });
  // Bulk company/asset-level status update — one action updates every
  // grouped contact row (user request, Jun 2026).
  const [bulkStatusDialog, setBulkStatusDialog] = useState({ open: false, group: null, status: '' });
  const [bulkStatusNote, setBulkStatusNote] = useState('');
  const [bulkStatusApplying, setBulkStatusApplying] = useState(false);
  const [statusReason, setStatusReason] = useState('');

  // Add outreach state
  const emptyOutreachForm = { biotech_company_id: id, asset_id: '', asset_name: '', contact_name: '', contact_email: '', company_name: '', country: '', designation: '', contact_type: 'Pharma', status: 'Introduction Made', notes: '', source_id: '', status_notes: '' };
  const [outreachDialogOpen, setOutreachDialogOpen] = useState(false);
  const [outreachForm, setOutreachForm] = useState({ ...emptyOutreachForm });
  // Multi-select bucket for adding multiple pharma/investor contacts at once.
  const [outreachSelectedIds, setOutreachSelectedIds] = useState([]);
  // Multi-select bucket for tagging the outreach against one OR many assets.
  // When this set has >1 entry on submit we fan out and create one outreach
  // row PER selected asset (so each asset can be tracked through its own
  // pipeline status). Empty set means "no specific asset" (legacy behavior).
  // User request 2026-02: "i should be able to select multiple assets under
  // evaluation, if required. right now, its just one."
  const [outreachSelectedAssetIds, setOutreachSelectedAssetIds] = useState([]);
  const [outreachMode, setOutreachMode] = useState('existing');
  const [editingOutreachId, setEditingOutreachId] = useState(null);
  const [pharmaContacts, setPharmaContacts] = useState([]);
  const [investorContacts, setInvestorContacts] = useState([]);
  const [biotechContacts, setBiotechContacts] = useState([]);

  // Drag-to-reorder state for the contacts card list in the master edit form.
  // Holds the index of the row currently being dragged so onDragOver knows
  // whether to highlight + onDrop knows the source index.
  const [draggedContactIdx, setDraggedContactIdx] = useState(null);
  const [dragOverContactIdx, setDragOverContactIdx] = useState(null);

  // Fundraising profile state
  const [editingFundraising, setEditingFundraising] = useState(false);
  const [fundraisingForm, setFundraisingForm] = useState({ funding_stage: '', funding_amount: '', use_of_funds: '' });
  const [autoPopulating, setAutoPopulating] = useState(false);

  // Share NCD state
  const [shareDoc, setShareDoc] = useState(null);
  const [shareToEmail, setShareToEmail] = useState('');
  const [shareToName, setShareToName] = useState('');
  const [shareToSearch, setShareToSearch] = useState('');
  const [shareToDropdown, setShareToDropdown] = useState(false);
  const [shareCcList, setShareCcList] = useState([]);
  const [shareCcSearch, setShareCcSearch] = useState('');
  const [shareCcDropdown, setShareCcDropdown] = useState(false);
  const [shareSubject, setShareSubject] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareSending, setShareSending] = useState(false);

  // CDA state
  const [cdaUploading, setCdaUploading] = useState(false);
  const cdaInputRef = useRef(null);
  // Partnership Agreement state
  const [partnershipUploading, setPartnershipUploading] = useState(false);
  const partnershipInputRef = useRef(null);
  // Inline Documents upload state — lets the user upload an NCD/Corporate
  // Overview/Molecule-Specific Info/Other without opening Edit Master.
  // Mirrors the structure used inside the master dialog (DOC_TYPE_OPTIONS).
  const [inlineDocType, setInlineDocType] = useState('Non-Confidential Deck');
  const [inlineDocCustomLabel, setInlineDocCustomLabel] = useState('');
  const [inlineDocUploading, setInlineDocUploading] = useState(false);
  const inlineDocFileRef = useRef(null);
  // Buyer CDA state (biotech ↔ pharma buyer; one biotech can have many over time)
  const [buyerCdaUploading, setBuyerCdaUploading] = useState(false);
  const [buyerCdaCounterparty, setBuyerCdaCounterparty] = useState('');
  // Pharma company suggestion list for the Buyer CDA counterparty input.
  // Loaded once on mount; filtered client-side as the user types.
  const [pharmaNameSuggestions, setPharmaNameSuggestions] = useState([]);
  const [buyerCdaSuggestOpen, setBuyerCdaSuggestOpen] = useState(false);
  const [buyerCdaSignedDate, setBuyerCdaSignedDate] = useState('');
  const [buyerCdaStatus, setBuyerCdaStatus] = useState('Shared with Them');
  const [buyerCdaSavingTracker, setBuyerCdaSavingTracker] = useState(false);
  const buyerCdaInputRef = useRef(null);

  const BUYER_CDA_STATUS_OPTIONS = ['Shared with Them', 'Under Discussion', 'Finalised'];

  // ===== Term Sheet tracker =====
  // Sibling to CDAs. Tracks the biotech's outbound term sheet drafts shared
  // with each pharma counterparty. "Latest only" policy — uploading a new file
  // for the same counterparty replaces the previous entry (server stores the
  // new doc, frontend deletes the older Term Sheet for that counterparty).
  const TERM_SHEET_STATUS_OPTIONS = ['Draft Shared', 'Under Negotiation', 'Counterproposed', 'Finalised', 'Executed'];
  const termSheetStatusPill = (status) => {
    switch (status) {
      case 'Executed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Finalised': return 'bg-teal-100 text-teal-700 border-teal-200';
      case 'Counterproposed': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Under Negotiation': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Draft Shared': return 'bg-sky-100 text-sky-700 border-sky-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };
  const [termSheetUploading, setTermSheetUploading] = useState(false);
  const [termSheetCounterparty, setTermSheetCounterparty] = useState('');
  const [termSheetSuggestOpen, setTermSheetSuggestOpen] = useState(false);
  const [termSheetSharedDate, setTermSheetSharedDate] = useState('');
  const [termSheetStatus, setTermSheetStatus] = useState('Draft Shared');
  const [termSheetSavingTracker, setTermSheetSavingTracker] = useState(false);
  const termSheetInputRef = useRef(null);


  // Normalise legacy status values stored in MongoDB so the badge renders
  // with the trimmed dropdown options. Old values like 'Shared with Buyer',
  // 'Shared with Us', 'Did Not Insist', 'Not Started' get mapped onto the
  // closest current option. New writes already use the trimmed set.
  const normCdaStatus = (s) => {
    if (s === 'Shared with Buyer') return 'Shared with Them';
    if (s === 'Shared with Us') return 'Shared with Them';
    if (s === 'Did Not Insist') return 'Under Discussion';
    if (s === 'Not Started' || !s) return 'Shared with Them';
    return s;
  };

  const buyerCdaStatusPill = (status) => {
    switch (normCdaStatus(status)) {
      case 'Finalised': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Under Discussion': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Shared with Them': return 'bg-sky-100 text-sky-700 border-sky-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Outreach Update state
  const [outreachUpdateOpen, setOutreachUpdateOpen] = useState(false);
  const [outreachUpdateStep, setOutreachUpdateStep] = useState(1); // 1=draft, 2=preview
  const [outreachUpdateSubject, setOutreachUpdateSubject] = useState('');
  const [outreachUpdateBody, setOutreachUpdateBody] = useState('');
  const [outreachUpdateDrafting, setOutreachUpdateDrafting] = useState(false);
  const [outreachUpdateSending, setOutreachUpdateSending] = useState(false);
  const [outreachUpdateContacts, setOutreachUpdateContacts] = useState([]);
  const [outreachUpdateSelectedContacts, setOutreachUpdateSelectedContacts] = useState({});
  const [outreachUpdateContext, setOutreachUpdateContext] = useState('');
  const outreachUpdateSendingRef = useRef(false);

  // Send Introduction state
  const [introOpen, setIntroOpen] = useState(false);
  const [introStep, setIntroStep] = useState(1); // 1=pick pharma, 2=select contacts, 3=preview/send
  // Feb 2026 · Partnering Intent that ships with the outreach email.
  // Auto-derived server-side from asset deal_interests + objectives,
  // returned on the draft response, and shown as an editable textarea
  // at step 3 so the user can edit / add custom notes (e.g. "position
  // this as U.S. regional licensing", "do not mention fundraising")
  // BEFORE the outbound email is finalised. Regenerate button re-drafts
  // with the current text.
  const [introIntent, setIntroIntent] = useState('');
  const [introRegenerating, setIntroRegenerating] = useState(false);
  const [introSearch, setIntroSearch] = useState('');
  const [introSearchResults, setIntroSearchResults] = useState([]);
  const [introSearching, setIntroSearching] = useState(false);
  const [introSearchError, setIntroSearchError] = useState('');
  const [introSearchAttempted, setIntroSearchAttempted] = useState(false);
  const [introPharma, setIntroPharma] = useState('');
  const [introContacts, setIntroContacts] = useState([]);
  const [introSelected, setIntroSelected] = useState({});
  const [introLoadingContacts, setIntroLoadingContacts] = useState(false);
  const [introDrafting, setIntroDrafting] = useState(false);
  const [introSubject, setIntroSubject] = useState('');
  const [introBody, setIntroBody] = useState('');
  const [introSending, setIntroSending] = useState(false);
  // Aug 2026 · Recipient-Rule gate 403 payload (Sanofi/NOVA2003 incident).
  const [introRoutingBlock, setIntroRoutingBlock] = useState(null);
  const introSendingRef = useRef(false);
  const [introSendingTest, setIntroSendingTest] = useState(false);
  const [introAsset, setIntroAsset] = useState(null); // null = company-level, object = asset-specific
  const [introSelectedAssetIds, setIntroSelectedAssetIds] = useState({}); // multi-select assets
  const introSearchTimeout = useRef(null);
  const [introSentAssets, setIntroSentAssets] = useState({}); // email → {asset_ids, asset_names} already sent for this biotech
  const [introForceResend, setIntroForceResend] = useState(false);
  // Asset-aware "already emailed": legacy rows (no asset_id) count for every asset.
  const introAlreadySent = React.useMemo(() => {
    const sel = introAsset ? [introAsset.id] : Object.keys(introSelectedAssetIds).filter(k => introSelectedAssetIds[k]);
    const s = new Set();
    Object.entries(introSentAssets).forEach(([em, v]) => {
      const ids = v?.asset_ids || [];
      if (!sel.length || ids.includes('') || ids.some(a => sel.includes(a))) s.add(em);
    });
    return s;
  }, [introSentAssets, introAsset, introSelectedAssetIds]);

  // Assets state
  const STAGES = ['Pre-Clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Clinical Stage', 'Commercial'];
  const NCD_STATUSES = ['Not Received', 'Received', 'Shared with Pharma'];
  const AGREEMENT_STATUSES = ['Not Started', 'Sent', 'Under Review', 'Finalised', 'Signed'];
  const STATUSES = ['Pipeline', 'Active', 'In Negotiation', 'On Hold', 'Closed', 'Dropped'];
  const EMAILING_STATUSES = ['Not Started', 'In Progress', 'Completed'];
  const PARAMETER_FIELDS = ['Molecule/Product Number', 'Platform', 'Target', 'Modality', 'Indication', 'Drug Delivery Mechanism'];
  // Document categories — order matters in the dropdown. Backend
  // /enrich-from-documents already loops over EVERY uploaded doc regardless
  // of type, so adding a new category here makes it immediately participate
  // in profile enrichment (company intro + assets at all levels). New
  // additions on 2026-06-09 per user: Press Release, Analyst Report,
  // Clinical Data, Publication.
  const DOC_TYPE_OPTIONS = [
    'Non-Confidential Deck',
    'Corporate Overview',
    'Molecule-Specific Information',
    'Press Release',
    'Analyst Report',
    'Clinical Data',
    'Publication',
    'Patent',
    'Other',
  ];
  const DEAL_INTEREST_OPTIONS = [
    { value: 'Out-Licensing', label: 'Out-Licensing', color: 'bg-sky-100 text-sky-700' },
    { value: 'Co-Development & R&D', label: 'Co-Dev & R&D', color: 'bg-blue-100 text-blue-700' },
    { value: 'Investment / Fundraising', label: 'Fundraising', color: 'bg-violet-100 text-violet-700' },
  ];
  const emptyAssetForm = { company_id: id, asset_name: '', therapeutic_area: '', stage: 'Pre-Clinical', stage_notes: '', modality: '', indication: '', target: '', platform: '', drug_delivery_mechanism: '', molecule_description: '', deal_interests: [], objectives: '', ncd_status: 'Not Received', emailing_status: 'Not Started' };
  const [assets, setAssets] = useState([]);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assetForm, setAssetForm] = useState({ ...emptyAssetForm });
  const [editingAsset, setEditingAsset] = useState(null);
  const [deleteAssetTarget, setDeleteAssetTarget] = useState(null);
  // Move-asset-to-another-company dialog state
  const [moveAsset, setMoveAsset] = useState(null);
  const [moveSearch, setMoveSearch] = useState('');
  const [moveResults, setMoveResults] = useState([]);
  const [moveSearching, setMoveSearching] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [movingAsset, setMovingAsset] = useState(false);
  const moveSearchTimer = useRef(null);
  const [migrating, setMigrating] = useState(false);
  const [assetIndex, setAssetIndex] = useState(0);
  // Manual asset merge dialog state
  const [assetMergeDialogOpen, setAssetMergeDialogOpen] = useState(false);
  const [assetRecoveryOpen, setAssetRecoveryOpen] = useState(false);
  const [assetMergePrimary, setAssetMergePrimary] = useState('');
  const [assetMergeSelected, setAssetMergeSelected] = useState({}); // { [assetId]: true }
  const [assetMerging, setAssetMerging] = useState(false);
  const contactSearchInputRef = useRef(null);

  // Add/Edit Contact state
  const emptyContact = { name: '', designation: '', email: '', phone: '' };
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ ...emptyContact });
  const [editingContactIdx, setEditingContactIdx] = useState(null);

  // Master Edit state
  const [masterEditOpen, setMasterEditOpen] = useState(false);
  const [masterForm, setMasterForm] = useState({});
  const [masterContacts, setMasterContacts] = useState([]);
  const [masterAssets, setMasterAssets] = useState([]);
  const [masterDocSlots, setMasterDocSlots] = useState([{ type: 'Non-Confidential Deck', customLabel: '', file: null, fileName: '' }]);
  const masterFileRef0 = useRef(null);
  const masterFileRef1 = useRef(null);
  const masterFileRef2 = useRef(null);
  const masterFileRef3 = useRef(null);
  const masterFileRef4 = useRef(null);
  const masterFileRefs = [masterFileRef0, masterFileRef1, masterFileRef2, masterFileRef3, masterFileRef4];

  // Enrich from documents (processes all PDFs uploaded to this company's
  // Documents section via Gemini → backfills profile fields + asset rows)
  const [enrichingDeck, setEnrichingDeck] = useState(false);
  // Feb 2026 · inline progress overlay while /enrich-from-documents
  // grinds through 8+ PDFs in the background. Populated by the poll
  // loop below; rendered near the top of the profile so the user can
  // watch "3/8 · Bened_deck.pdf" progress in real time.
  const [enrichJob, setEnrichJob] = useState(null); // {status, total, completed, current_document, ...}

  // Feb 2026 · detailed per-document enrichment report shown in a modal
  // AFTER completion so the user can see per-doc what was filled, what
  // was overwritten (high-conf), and what was skipped because existing
  // CRM data was already strong. Populated from `enrich_report` on the
  // job doc.
  const [enrichReport, setEnrichReport] = useState(null);
  const [enrichReportOpen, setEnrichReportOpen] = useState(false);

  // Enrich from pasted text — same CRM extraction pipeline as docs, but
  // takes raw text (e.g. an email body the founder pasted into a dialog).
  const [enrichTextOpen, setEnrichTextOpen] = useState(false);
  const [enrichText, setEnrichText] = useState('');
  // Aug 2026 · review-before-save: extraction preview + per-item selection
  const [enrichPreview, setEnrichPreview] = useState(null);
  const [enrichSel, setEnrichSel] = useState({ company: true, contacts: [], assets: [] });
  const [enrichingFromText, setEnrichingFromText] = useState(false);

  const handleEnrichFromDocs = async (force = false) => {
    setEnrichingDeck(true);
    try {
      // Feb 2026 · endpoint now returns immediately (queued) and does the
      // Gemini extraction in a background task to avoid Cloudflare's ~100s
      // edge timeout. We poll `/enrich-status/{id}` until done.
      const res = await axios.post(
        `${API}/biotech-companies/${id}/enrich-from-documents` + (force ? '?force=true' : ''),
        {},
        { withCredentials: true, timeout: 30000 }
      );
      const r = res.data;
      if (r.already_running) {
        toast.info(r.message || 'Enrichment is already running — check back in a few minutes.');
        return;
      }
      if (r.processed === 0 && r.skipped_already_enriched > 0 && !force) {
        const ok = window.confirm(
          `All ${r.skipped_already_enriched} document(s) are marked as already enriched.\n\n` +
          `If the earlier run failed mid-way or you want to refresh the extracted fields, ` +
          `click OK to re-run with force=true (re-processes every document).\n\n` +
          `Note: existing non-empty fields are NOT overwritten — only blanks get filled.`
        );
        if (ok) {
          await handleEnrichFromDocs(true);
        } else {
          toast.info(r.message);
        }
        return;
      }
      const queued = r.queued || 0;
      if (queued === 0) {
        toast.info(r.message || 'Nothing to enrich');
        return;
      }
      toast.success(`Queued ${queued} document(s) for enrichment — this runs in the background.`);
      setEnrichJob({ status: 'running', total: queued, completed: 0, current_document: null });

      // Poll every 4s. Max ~30 min total (safely more than 8 × 3-min docs).
      const t0 = Date.now();
      let last = null;
      while (Date.now() - t0 < 30 * 60 * 1000) {
        await new Promise((res) => setTimeout(res, 4000));
        try {
          const s = await axios.get(
            `${API}/biotech-companies/${id}/enrich-status`,
            { withCredentials: true, timeout: 15000 },
          );
          last = s.data;
          setEnrichJob(last);
          if (last?.status === 'done' || last?.status === 'error' || last?.status === 'idle') break;
        } catch (_err) { /* transient — keep polling */ }
      }
      if (last?.status === 'done') {
        const fu = (last.fields_updated || []).length;
        const ac = last.assets_created || 0;
        const au = last.assets_updated || 0;
        const dc = (last.documents_processed || []).length;
        toast.success(
          `Enriched from ${dc} doc(s): ` +
          `${fu} company field(s) updated, ` +
          `${ac} asset(s) added, ` +
          `${au} asset(s) updated` +
          (last.assets_skipped_duplicate ? ` (${last.assets_skipped_duplicate} matched no-op)` : '') +
          (force ? ' [force re-run]' : '')
        );
        // Show the detailed per-doc report modal so the user can see
        // exactly what was filled, what was overwritten (high-conf),
        // and what was skipped because existing data was already strong.
        if ((last.enrich_report || []).length) {
          setEnrichReport(last.enrich_report);
          setEnrichReportOpen(true);
        }
        if ((last.errors || []).length) {
          toast.warning(`${last.errors.length} doc(s) had errors — see console for details`);
          console.warn('Enrich errors:', last.errors);
          if (last.errors[0]) {
            setTimeout(() => toast.error(`First error: ${last.errors[0]}`), 100);
          }
        }
      } else if (last?.status === 'error') {
        toast.error(`Enrich failed: ${last.error || 'unknown'}`);
      } else {
        toast.warning('Enrichment still running in background — refresh the page later to see updates.');
      }
      await fetchAll();
      // Feb 2026 · keep the overlay visible for a moment on completion so
      // the user sees the final counts, then dismiss it.
      setTimeout(() => setEnrichJob(null), 8000);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Enrich failed';
      toast.error(`Enrich failed: ${msg}`);
      setEnrichJob(null);
    } finally {
      setEnrichingDeck(false);
    }
  };

  // Enrich the biotech profile from a pasted text blob (e.g. email body
  // from a small biotech founder that mentions stage/indication/contacts).
  // Hits the same _run_crm_extraction pipeline used by document enrichment.
  const handleEnrichFromText = async () => {
    // Step 1 — extract only (preview:true), nothing is saved yet.
    const text = (enrichText || '').trim();
    if (text.length < 30) {
      toast.error('Paste at least a few sentences of email content first');
      return;
    }
    setEnrichingFromText(true);
    try {
      const res = await axios.post(
        `${API}/ai/company-chat/${id}/auto-populate`,
        { source_text: text, preview: true },
        { withCredentials: true, timeout: 300000 }
      );
      const ex = res.data?.extraction || {};
      const hasCompany = Object.values(ex.company || {}).some((v) => (v || '').toString().trim());
      const contacts = (ex.contacts || []).filter((c) => (c.name || '').trim());
      const assets = (ex.assets || []).filter((a) => (a.asset_name || '').trim());
      if (!hasCompany && contacts.length === 0 && assets.length === 0) {
        toast.info('No structured info found in the pasted text');
        return;
      }
      setEnrichPreview({ ...ex, contacts, assets });
      setEnrichSel({ company: hasCompany, contacts: contacts.map(() => true), assets: assets.map(() => true) });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Extraction failed';
      toast.error(`Extraction failed: ${msg}`);
    } finally {
      setEnrichingFromText(false);
    }
  };

  const handleEnrichApply = async () => {
    // Step 2 — persist ONLY the reviewer-approved items; the full pasted
    // text is stored as an audit interaction record on the company.
    const ex = enrichPreview;
    if (!ex) return;
    setEnrichingFromText(true);
    try {
      const pruned = {
        company: enrichSel.company ? (ex.company || {}) : {},
        contacts: (ex.contacts || []).filter((_, i) => enrichSel.contacts[i]),
        assets: (ex.assets || []).filter((_, i) => enrichSel.assets[i]),
      };
      const res = await axios.post(
        `${API}/ai/company-chat/${id}/auto-populate`,
        { source_text: (enrichText || '').trim(), extraction: pruned },
        { withCredentials: true, timeout: 300000 }
      );
      const details = (res.data?.details || []);
      toast.success(details.length ? `Saved: ${details.length} change(s) applied` : 'Saved — no field changes needed');
      setEnrichTextOpen(false);
      setEnrichText('');
      setEnrichPreview(null);
      await fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      toast.error(`Save failed: ${msg}`);
    } finally {
      setEnrichingFromText(false);
    }
  };

  // Asset-scoped "Enrich from text" — paste trial data / phase / indications
  // for a single asset; merges into THAT asset only.
  const [assetEnrichDialog, setAssetEnrichDialog] = useState(null); // {asset}
  // Aug 2026 · Deck-grounded profile regeneration (⭐ button redesign).
  // Preview-first: {asset, loading, preview, current, sources, error, applying}
  const [regenDialog, setRegenDialog] = useState(null);
  // Sep 2026 · ⭐ "Enrich all assets": runs the per-asset deck-grounded
  // regenerate → apply loop over every asset, sequentially, in the background.
  const [bulkEnrich, setBulkEnrich] = useState(null); // {done,total,current,applied,skipped,failed,running}
  const bulkEnrichStopRef = useRef(false);
  // Sep 2026 · Step 1 of Enrich all — asset inventory reconciliation review.
  const [reconcile, setReconcile] = useState(null); // {verdicts, missing_own_assets, summary, sources, company_name, selected:Set, applying}

  const enrichAssetList = async (list) => {
    bulkEnrichStopRef.current = false;
    const st = { running: true, done: 0, total: list.length, current: '', applied: 0, skipped: 0, failed: 0 };
    setBulkEnrich({ ...st });
    for (const a of list) {
      if (bulkEnrichStopRef.current) break;
      st.current = a.asset_name || a.id;
      setBulkEnrich({ ...st });
      try {
        const r = await axios.post(`${API}/assets/${a.id}/regenerate-clean-profile?apply=false`, {}, { withCredentials: true, timeout: 180000 });
        const cleaned = r.data?.preview || {};
        const hasContent = ['molecule_description', 'objectives', 'notes', 'indication_clean', 'differentiation'].some(k => cleaned[k]);
        if (r.data?.asset_found_in_docs && hasContent) {
          await axios.post(`${API}/assets/${a.id}/apply-clean-profile`, { cleaned }, { withCredentials: true, timeout: 60000 });
          st.applied += 1;
        } else {
          st.skipped += 1;
        }
      } catch (e) {
        st.failed += 1;
      }
      st.done += 1;
      setBulkEnrich({ ...st });
    }
    st.running = false; st.current = '';
    setBulkEnrich({ ...st });
    toast[st.failed ? 'warning' : 'success'](`Enrich all done — ${st.applied} updated · ${st.skipped} skipped (not found in documents)${st.failed ? ` · ${st.failed} failed` : ''}`, { duration: 10000 });
    try { fetchAll(); } catch {}
  };

  const runBulkEnrich = async () => {
    if (bulkEnrich?.running || !assets.length) return;
    if (!window.confirm(`Enrich all ${assets.length} assets from the company's uploaded documents?\n\nStep 1 — the documents are read to confirm which of these are genuinely ${company?.company_name || 'this company'}'s own programs; competitors, comparators and landscape molecules are flagged for removal, with your review.\nStep 2 — the confirmed assets are enriched. Fields typed by hand are kept.`)) return;
    setBulkEnrich({ running: true, done: 0, total: assets.length, current: 'Reconciling asset inventory from documents…', applied: 0, skipped: 0, failed: 0, reconciling: true });
    let res;
    try {
      const start = await axios.post(`${API}/biotech-companies/${id}/reconcile-assets`, {}, { withCredentials: true, timeout: 60000 });
      const jobId = start.data?.job_id;
      const t0 = Date.now();
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const j = await axios.get(`${API}/biotech-companies/${id}/reconcile-assets/${jobId}`, { withCredentials: true, timeout: 30000 });
        if (j.data?.status === 'done') { res = j.data; break; }
        if (j.data?.status === 'failed') throw new Error(j.data?.error || 'Reconciliation failed');
        if (Date.now() - t0 > 6 * 60 * 1000) throw new Error('Reconciliation timed out — please retry.');
        setBulkEnrich(s => ({ ...(s || {}), current: `Reading documents… (${Math.round((Date.now() - t0) / 1000)}s)` }));
      }
    } catch (e) {
      setBulkEnrich(null);
      toast.error(e?.response?.data?.detail || e?.message || 'Asset inventory reconciliation failed — enrichment not started.');
      return;
    }
    const verdicts = res.verdicts || [];
    const flagged = verdicts.filter(v => v.proposed_action !== 'keep');
    const hasProgramUpdates = verdicts.some(v => v.program && (v.program.aliases?.length || v.program.stage?.current));
    if (flagged.length === 0 && !(res.missing_own_assets || []).length && !hasProgramUpdates) {
      toast.success(`Inventory confirmed — all ${verdicts.length} assets are ${res.company_name || 'the company'}'s own programs.`);
      await enrichAssetList(assets);
      return;
    }
    setBulkEnrich(null);
    setReconcile({
      ...res,
      selected: new Set(flagged.filter(v => v.proposed_action === 'remove').map(v => v.asset_id)),
      selectedMerges: new Set(flagged.filter(v => v.proposed_action === 'merge').map(v => v.asset_id)),
      applying: false,
    });
  };

  const applyReconcileAndEnrich = async () => {
    if (!reconcile || reconcile.applying) return;
    const ids = [...reconcile.selected];
    const mergeIds = [...(reconcile.selectedMerges || [])];
    const merges = reconcile.verdicts.filter(v => mergeIds.includes(v.asset_id) && v.merge_into_asset_id).map(v => ({ asset_id: v.asset_id, into_asset_id: v.merge_into_asset_id }));
    const programs = Object.fromEntries(reconcile.verdicts.filter(v => v.classification === 'own' && v.program).map(v => [v.asset_id, v.program]));
    setReconcile(r => ({ ...r, applying: true }));
    try {
      if (ids.length || merges.length || Object.keys(programs).length) {
        const reasons = Object.fromEntries(reconcile.verdicts.filter(v => ids.includes(v.asset_id)).map(v => [v.asset_id, { classification: v.classification, confidence: v.confidence, evidence: v.evidence }]));
        const r = await axios.post(`${API}/biotech-companies/${id}/reconcile-assets/apply`, { remove_asset_ids: ids, reasons, merges, programs }, { withCredentials: true, timeout: 120000 });
        const d = r.data || {};
        toast.success(`Inventory reconciled — ${d.removed ?? 0} removed (archived), ${d.merged ?? 0} merged as aliases, ${d.updated ?? 0} program(s) updated.`, { duration: 8000 });
      }
      const gone = new Set([...ids, ...merges.map(m => m.asset_id)]);
      const keep = assets.filter(a => !gone.has(a.id));
      setReconcile(null);
      await enrichAssetList(keep);
    } catch (e) {
      setReconcile(r => ({ ...r, applying: false }));
      toast.error(e?.response?.data?.detail || 'Could not apply the reconciliation.');
    }
  };

  // Merge dialog helpers — group likely duplicates by normalised name.
  const normAssetName = (n) => (n || '').toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9]+/g, '').trim();
  const isPlaceholderAsset = (n) => /\b(portfolio|pipeline)\b/i.test(n || '');
  const assetCompleteness = (a) => {
    const fields = ['asset_name', 'stage', 'indication', 'therapeutic_area', 'mechanism_of_action', 'target', 'modality', 'molecule_description', 'description', 'differentiation', 'objectives'];
    const stageRank = { 'commercial': 9, 'approved': 9, 'marketed': 9, 'nda filed': 8, 'filed': 8, 'phase 3': 7, 'phase 2/3': 6, 'phase 2': 5, 'phase 1/2': 4, 'phase 1': 3, 'preclinical': 2, 'pre-clinical': 2, 'discovery': 1 };
    return fields.filter(f => (a[f] || '').toString().trim()).length * 10 + (stageRank[(a.stage || '').toLowerCase()] || 0);
  };
  const duplicateGroups = React.useMemo(() => {
    const map = {};
    assets.forEach(a => {
      if (isPlaceholderAsset(a.asset_name)) return;
      const k = normAssetName(a.asset_name);
      if (!k) return;
      (map[k] = map[k] || []).push(a);
    });
    return Object.values(map).filter(g => g.length >= 2).map(g => {
      const sorted = [...g].sort((x, y) => assetCompleteness(y) - assetCompleteness(x));
      return { key: normAssetName(sorted[0].asset_name), label: sorted[0].asset_name, members: sorted, primary: sorted[0].id };
    }).sort((a, b) => b.members.length - a.members.length);
  }, [assets]);
  const groupedAssetIds = React.useMemo(() => new Set(duplicateGroups.flatMap(g => g.members.map(m => m.id))), [duplicateGroups]);
  const [mergeShowSingles, setMergeShowSingles] = useState(false);
  const renderMergeRow = (a) => {
    const checked = !!assetMergeSelected[a.id];
    const isPrimary = assetMergePrimary === a.id;
    return (
                <div key={a.id} className={`flex items-start gap-3 p-3 rounded border ${isPrimary ? 'border-emerald-400 bg-emerald-50' : checked ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setAssetMergeSelected(prev => {
                      const next = { ...prev };
                      if (next[a.id]) {
                        delete next[a.id];
                        if (assetMergePrimary === a.id) setAssetMergePrimary('');
                      } else {
                        next[a.id] = true;
                      }
                      return next;
                    })}
                    className="mt-1 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    data-testid={`merge-select-${a.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900">{a.asset_name || '(unnamed asset)'}</p>
                      <Badge variant="secondary" className="text-[9px]">{a.stage || 'No stage'}</Badge>
                      {isPrimary && <Badge className="text-[9px] bg-emerald-600 text-white">Primary (keep)</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{a.indication || a.therapeutic_area || '—'}</p>
                  </div>
                  {checked && !isPrimary && (
                    <button
                      type="button"
                      onClick={() => setAssetMergePrimary(a.id)}
                      className="text-[10px] font-medium text-emerald-700 hover:text-emerald-900 bg-white border border-emerald-300 px-2 py-1 rounded"
                      data-testid={`merge-set-primary-${a.id}`}
                    >
                      Set as primary
                    </button>
                  )}
                </div>
    );
  };

  const selectDuplicateGroup = (g) => {
    const sel = {};
    g.members.forEach(m => { sel[m.id] = true; });
    setAssetMergeSelected(sel);
    setAssetMergePrimary(g.primary);
  };
  const runRegenGrounded = async (a) => {
    setRegenDialog({ asset: a, loading: true });
    try {
      const r = await axios.post(`${API}/assets/${a.id}/regenerate-clean-profile?apply=false`, {}, { withCredentials: true });
      setRegenDialog({ asset: a, loading: false, preview: r.data.preview, current: r.data.current, sources: r.data.sources || [], assetFound: !!r.data.asset_found_in_docs });
    } catch (e) {
      setRegenDialog({ asset: a, loading: false, error: e?.response?.data?.detail || e?.message || 'Failed to analyze documents' });
    }
  };
  const applyRegenGrounded = async () => {
    if (!regenDialog?.preview) return;
    setRegenDialog(p => ({ ...p, applying: true }));
    try {
      await axios.post(`${API}/assets/${regenDialog.asset.id}/apply-clean-profile`,
        { cleaned: regenDialog.preview, sources: regenDialog.sources }, { withCredentials: true });
      toast.success('Profile applied — grounded in your uploaded documents. Previous version backed up.', { duration: 6000 });
      setRegenDialog(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply');
      setRegenDialog(p => ({ ...p, applying: false }));
    }
  };
  const [assetEnrichText, setAssetEnrichText] = useState('');
  const [assetEnriching, setAssetEnriching] = useState(false);
  const handleEnrichAssetFromText = async () => {
    const text = (assetEnrichText || '').trim();
    if (text.length < 20) {
      toast.error('Paste at least a sentence or two about this asset first');
      return;
    }
    setAssetEnriching(true);
    try {
      const res = await axios.post(
        `${API}/assets/${assetEnrichDialog.asset.id}/enrich-from-text`,
        { source_text: text },
        { withCredentials: true, timeout: 300000 }
      );
      const details = (res.data?.details || []);
      const applied = details.filter(d => /updated asset/i.test(d));
      if (applied.length === 0) {
        toast.info('No new asset info found in the pasted text');
      } else {
        toast.success('Asset enriched from text');
        console.info('Asset enrich details:', details);
      }
      setAssetEnrichDialog(null);
      setAssetEnrichText('');
      await fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Enrich failed';
      toast.error(`Enrich failed: ${msg}`);
    } finally {
      setAssetEnriching(false);
    }
  };

  const fetchAll = async () => {
    try {
      const [compRes, docRes, outRes, assetsRes] = await Promise.all([
        axios.get(`${API}/biotech-companies/${id}`, { withCredentials: true }),
        axios.get(`${API}/documents?entity_type=biotech&entity_id=${id}`, { withCredentials: true }),
        axios.get(`${API}/outreach?biotech_company_id=${id}`, { withCredentials: true }),
        axios.get(`${API}/assets?company_id=${id}`, { withCredentials: true }),
      ]);
      setCompany(compRes.data);
      setFundraisingForm({ funding_stage: compRes.data.funding_stage || '', funding_amount: compRes.data.funding_amount || '', use_of_funds: compRes.data.use_of_funds || '' });
      setDocs(docRes.data);
      // Filter outreach: hide "Introduction Made" rows that aren't tied to
      // a specific pharma/distributor account (no source_id). Per user:
      // bulk intros shouldn't count anywhere — only intros made FROM an
      // account workflow are real "introductions" worth tracking. This
      // applies to the tracker count, tab badges, search, status filter
      // and copy-summary, since downstream code reads from `outreach`.
      const rawOutreach = outRes.data || [];
      setAllOutreach(rawOutreach);
      const isBulk = (o) => o.status === 'Introduction Made' && !(o.source_id && String(o.source_id).trim());
      setBulkSendCount(rawOutreach.filter(isBulk).length);
      const trackable = includeBulkSends ? rawOutreach : rawOutreach.filter(o => !isBulk(o));
      setOutreach(trackable.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
      const STAGE_ORDER = { 'Commercial': 0, 'Phase 3': 1, 'Phase 2': 2, 'Phase 1': 3, 'Clinical Stage': 4, 'IND': 5, 'Pre-Clinical': 6, 'Discovery': 7, 'Platform': 8 };
      // Canonicalise any long-form / variant stage string the AI may have
      // written (e.g. "commercial (FDA-approved in the U.S.)",
      // "Phase 3 (ongoing, pivotal...)", "Clinical-stage", "Phase 1a/b
      // clinical stage") into one of the canonical buckets above so the
      // sort places the most-advanced asset at the top regardless of how
      // the underlying stage value is phrased.
      const canonicalStage = (raw) => {
        if (!raw) return '';
        const s = String(raw).trim();
        if (s in STAGE_ORDER) return s;
        const low = s.toLowerCase();
        if (/\b(commercial|marketed|approved|nda\s*filed|launched|post[- ]marketing|phase\s*4)\b/.test(low)) return 'Commercial';
        if (/\bphase\s*(?:3|iii)[a-z/]*\b|\bp\s*3\b|registration(?:al)?\s*trial|pre[- ]?bla/.test(low)) return 'Phase 3';
        if (/\bphase\s*(?:2|ii)[a-z/]*\b|\bp\s*2\b/.test(low)) return 'Phase 2';
        if (/\bphase\s*(?:1|i)[a-z/]*\b|\bp\s*1\b|first[- ]in[- ]human|\bfih\b/.test(low)) return 'Phase 1';
        if (/clinical[- ]?stage/.test(low)) return 'Clinical Stage';
        if (/\bind\b/.test(low)) return 'IND';
        if (/pre[- ]?clinical|\bpoc\b|proof[- ]of[- ]concept/.test(low)) return 'Pre-Clinical';
        if (/\b(discovery|research|lead[- ]optimi[sz]ation|lead\s*candidate)\b/.test(low)) return 'Discovery';
        if (/\bplatform\b/.test(low)) return 'Platform';
        return s;
      };
      const stageRank = (a) => STAGE_ORDER[canonicalStage(a.stage)] ?? 99;
      const rawAssets = assetsRes.data?.items || assetsRes.data || [];
      setAssets(rawAssets.sort((a, b) => stageRank(a) - stageRank(b)));
    } catch {
      toast.error('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);
  // Re-attach sent emails whose asset was deleted/re-created (post enrich/merge) so they show as Sent.
  useEffect(() => {
    if (!id) return;
    axios.post(`${API}/biotech-companies/${id}/relink-outreach`, {}, { withCredentials: true }).then(r => {
      const n = (r.data?.outreach || 0) + (r.data?.ai_mappings || 0) + (r.data?.marked_completed || 0) + (r.data?.relinked?.split_moved || 0) + (r.data?.relinked?.split_unresolved || 0) + (r.data?.relinked?.split_error ? 1 : 0);
      if (n) {
        const bits = [];
        if (r.data.outreach) bits.push(`${r.data.outreach} sent email(s) re-attached`);
        if (r.data.relinked?.split_moved) bits.push(`${r.data.relinked.split_moved} email(s) moved back to the asset they were sent for`);
        if (r.data.relinked?.split_unresolved) bits.push(`${r.data.relinked.split_unresolved} email(s) could not be placed automatically — open Recover to place them`);
        if (r.data.relinked?.split_error) bits.push(`repair step hit an error: ${r.data.relinked.split_error}`);
        if (r.data.marked_completed) bits.push(`${r.data.marked_completed} asset(s) marked Email: Completed`);
        toast.info(bits.join(' · ') || 'Outreach history synced');
        fetchAll();
      }
    }).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: when the URL carries ?asset_id=<aid> (set by global search),
  // jump the asset carousel to that asset once assets are loaded, then strip
  // the param so a refresh doesn't lock the user there.
  useEffect(() => {
    if (!deepLinkAssetId || !assets.length) return;
    const idx = assets.findIndex(a => a.id === deepLinkAssetId);
    if (idx >= 0) {
      setAssetIndex(idx);
      // Strip the query param after the jump so subsequent navigation is
      // clean. Replace so we don't pollute history.
      const next = new URLSearchParams(searchParams);
      next.delete('asset_id');
      setSearchParams(next, { replace: true });
    }
    // assets dependency ensures we wait for the load; deepLinkAssetId fires once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkAssetId, assets]);

  // Deep-link: when the URL carries ?contact_id=<cid> OR
  // ?contact_email=<email> (set by global search → biotech_contact result),
  // scroll to that contact row in the contacts section and briefly
  // highlight it. Then strip the param.
  useEffect(() => {
    if (!deepLinkContactId && !deepLinkContactEmail) return;
    const handle = setTimeout(() => {
      let el = null;
      if (deepLinkContactId) {
        el = document.querySelector(`[data-contact-id="${deepLinkContactId}"]`);
      }
      if (!el && deepLinkContactEmail) {
        el = document.querySelector(`[data-contact-email="${deepLinkContactEmail}"]`);
      }
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400', 'bg-amber-50/60', 'transition');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-amber-400', 'bg-amber-50/60');
        }, 2500);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('contact_id');
      next.delete('contact_email');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkContactId, deepLinkContactEmail]);

  // Load pharma company names once for the Buyer CDA counterparty autosuggest.
  // Names come from pharma_contacts.company_name + investors.firm_name +
  // company_profiles.company_name (see /api/pharma-companies/names).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/pharma-companies/names`, { withCredentials: true });
        if (!cancelled && Array.isArray(res.data)) setPharmaNameSuggestions(res.data);
      } catch {
        // non-fatal — autosuggest just won't show suggestions
      }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true })
      .then(res => setPharmaContacts(res.data?.items || res.data || []))
      .catch(() => {});
    axios.get(`${API}/investors?limit=5000`, { withCredentials: true })
      .then(res => setInvestorContacts(res.data?.items || res.data || []))
      .catch(() => {});
    axios.get(`${API}/biotech-contacts?limit=10000`, { withCredentials: true })
      .then(res => setBiotechContacts(res.data?.items || res.data || []))
      .catch(() => {});
  }, []);

  // One pool per contact type — Biotech covers in-licenser biotech companies
  // (Path E) whose contacts live in biotech_contacts, not pharma/investors.
  const contactPoolFor = (type) => type === 'Pharma' ? pharmaContacts : type === 'Biotech' ? biotechContacts : investorContacts;

  // Sep 2026 — one outreach row per (contact × asset). Mirrors the backend
  // 409 guard so the modal can warn before submit.
  const findDuplicateOutreach = (c, assetId) => {
    const email = (c.contact_email || c.email || '').trim().toLowerCase();
    const sid = c.source_id || c.id || '';
    const name = (c.contact_name || c.name || '').trim().toLowerCase();
    const comp = (c.company_name || '').trim().toLowerCase();
    return allOutreach.find(o => {
      if (o.is_deleted || o.id === editingOutreachId) return false;
      if ((o.asset_id || '') !== (assetId || '')) return false;
      if (email && (o.contact_email || '').trim().toLowerCase() === email) return true;
      if (sid && o.source_id === sid) return true;
      return !email && !sid && name && comp && (o.contact_name || '').trim().toLowerCase() === name && (o.company_name || '').trim().toLowerCase() === comp;
    });
  };

  const jumpToOutreachRow = (o) => {
    setOutreachDialogOpen(false);
    setEditingOutreachId(null);
    setOutreachForm({ ...emptyOutreachForm });
    setOutreachSelectedIds([]);
    setOutreachSelectedAssetIds([]);
    setOutreachStatusFilter('All');
    setOutreachAssetFilter('__all__');
    setShowIntroOnlyGroups(true);
    setOutreachSearch(o.contact_email || o.contact_name || '');
    const gk = `${(o.company_name || '').trim().toLowerCase()}|${(o.asset_name_canonical || o.asset_name || '').trim().toLowerCase()}`;
    setExpandedOutreachGroups(prev => new Set([...prev, gk]));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="outreach-row-${o.id}"]`) || document.querySelector('[data-testid="outreach-grouped"], [data-testid="outreach-tracker"]');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-amber-400', 'bg-amber-50/60', 'transition');
      setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'bg-amber-50/60'), 2500);
    }, 400);
  };

  const VALID_STAGES = new Set(STAGES);
  const VALID_STATUSES = new Set(STATUSES);
  const VALID_EMAILING = new Set(EMAILING_STATUSES);
  const VALID_AGREEMENT = new Set(AGREEMENT_STATUSES);
  const VALID_NCD = new Set(NCD_STATUSES);

  const openMasterEdit = () => {
    setMasterForm({
      company_name: company.company_name || '',
      location: company.location || '',
      website: company.website || '',
      therapeutic_area: company.therapeutic_area || '',
      company_description: company.company_description || company.description || '',
      agreement_status: VALID_AGREEMENT.has(company.agreement_status) ? company.agreement_status : 'Not Started',
      ncd_status: VALID_NCD.has(company.ncd_status) ? company.ncd_status : 'Not Received',
      funding_stage: company.funding_stage || '',
      funding_amount: company.funding_amount || '',
      use_of_funds: company.use_of_funds || '',
      india_licensing: !!company.india_licensing,
    });
    const rawContacts = company.contacts || [];
    const validContacts = Array.isArray(rawContacts) ? rawContacts.map(c => ({
      name: c?.name || '',
      designation: c?.designation || '',
      email: c?.email || '',
      phone: c?.phone || '',
    })) : [];
    setMasterContacts(
      validContacts.length > 0 ? validContacts : [{ name: '', designation: '', email: '', phone: '' }]
    );
    setMasterDocSlots([{ type: 'Non-Confidential Deck', customLabel: '', file: null, fileName: '' }]);
    setMasterAssets(assets.map(a => ({
      id: a.id,
      asset_name: a.asset_name || '',
      stage: VALID_STAGES.has(a.stage) ? a.stage : 'Pre-Clinical',
      stage_notes: a.stage_notes || '',
      platform: a.platform || '',
      target: a.target || '',
      modality: a.modality || '',
      indication: a.indication || '',
      drug_delivery_mechanism: a.drug_delivery_mechanism || '',
      molecule_description: a.molecule_description || '',
      objectives: a.objectives || '',
      deal_interests: Array.isArray(a.deal_interests) ? a.deal_interests.filter(d => ['Out-Licensing', 'Co-Development & R&D', 'Investment / Fundraising'].includes(d)) : [],
      emailing_status: VALID_EMAILING.has(a.emailing_status) ? a.emailing_status : 'Not Started',
      status: VALID_STATUSES.has(a.status) ? a.status : 'Pipeline',
      therapeutic_area: a.therapeutic_area || '',
      is_hidden: !!a.is_hidden,
    })));
    setMasterEditOpen(true);
  };

  const toggleMyCompany = async () => {
    try {
      const res = await axios.patch(`${API}/biotech-companies/${id}/toggle-my-company`, {}, { withCredentials: true });
      setCompany(prev => ({ ...prev, is_my_company: res.data.is_my_company }));
      toast.success(res.data.is_my_company ? 'Added to My Companies' : 'Removed from My Companies');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      console.error('toggle-my-company failed', { status, detail, error: e });
      toast.error(`Failed to toggle${status ? ` (${status})` : ''}: ${detail}`);
    }
  };

  // ===== Buyer-eligibility (biotech also acts as an in-licenser/buyer) =====
  // effective = manual override if set, else data-driven auto (in-licensing OR commercial).
  const effectiveEligible = company?.effective_buyer_eligible === true
    || (company?.is_buyer_eligible === true)
    || (company?.is_buyer_eligible == null && company?.auto_buyer_eligible === true);
  const manuallyExcluded = company?.is_buyer_eligible === false;
  const cycleBuyerEligible = async () => {
    // green (eligible) -> exclude(false); rose (excluded) -> auto(null); grey -> include(true)
    let next;
    if (manuallyExcluded) next = null;
    else if (effectiveEligible) next = false;
    else next = true;
    try {
      const res = await axios.patch(`${API}/biotech-companies/${id}/buyer-eligible`, { value: next }, { withCredentials: true });
      const savedFlag = res.data.is_buyer_eligible;
      // Recompute effective eligibility locally so the icon updates instantly
      // (true/false override wins; null falls back to the data-driven auto value).
      const effective = savedFlag === true ? true : savedFlag === false ? false : (company?.auto_buyer_eligible === true);
      setCompany(prev => ({ ...prev, is_buyer_eligible: savedFlag, effective_buyer_eligible: effective }));
      toast.success(
        next === false ? 'Excluded — will not appear as an AI-mapping in-licensing target'
        : next === true ? 'Marked as in-licenser — will appear as an AI-mapping target for matching assets'
        : 'Reset to auto (categorized from CRM data)'
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update buyer eligibility');
    }
  };

  // ===== Do Not Contact =====
  const [dncDialogOpen, setDncDialogOpen] = useState(false);
  const [dncReason, setDncReason] = useState('');
  const [dncSaving, setDncSaving] = useState(false);
  const dnc = company?.do_not_contact || {};
  const isDnc = !!dnc.flagged;

  const openDncDialog = () => {
    setDncReason(dnc.reason || '');
    setDncDialogOpen(true);
  };

  const saveDnc = async () => {
    if (!dncReason.trim()) { toast.error('Please enter a reason'); return; }
    setDncSaving(true);
    try {
      const payload = {
        do_not_contact: {
          flagged: true,
          reason: dncReason.trim(),
          flagged_at: new Date().toISOString(),
        },
      };
      await axios.patch(`${API}/biotech-companies/${id}`, payload, { withCredentials: true });
      setCompany(prev => ({ ...prev, do_not_contact: payload.do_not_contact }));
      // Auto-deactivate the public share link too — when a biotech is
      // flagged Do-Not-Contact, leaving their pharma list publicly
      // accessible is the exact data-leak risk we want to plug. Best-
      // effort; we ignore the response so the DNC save isn't blocked by
      // a missing share token (returns 200 with `deactivated: false`).
      try {
        await axios.delete(`${API}/share/${id}`, { withCredentials: true });
      } catch { /* non-fatal */ }
      toast.success('Marked as Do Not Contact — share link deactivated');
      setDncDialogOpen(false);
    } catch { toast.error('Failed to update'); }
    finally { setDncSaving(false); }
  };

  const removeDnc = async () => {
    setDncSaving(true);
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, { do_not_contact: { flagged: false } }, { withCredentials: true });
      setCompany(prev => ({ ...prev, do_not_contact: { flagged: false } }));
      toast.success('Do Not Contact removed — bulk outreach re-enabled');
      setDncDialogOpen(false);
    } catch { toast.error('Failed to update'); }
    finally { setDncSaving(false); }
  };

  // ===== Next Outreach Queue =====
  // Curated "lined up for the next outreach" shortlist — separate from the broad
  // "Pending Outreach" filter. User manually flags biotechs that are closest to
  // reach so the Dashboard surfaces them at-a-glance. Reason note is optional
  // but encouraged ("Met at ChinaBio booth", "Asked to follow up next week").
  const [nextOutreachDialogOpen, setNextOutreachDialogOpen] = useState(false);
  const [nextOutreachNote, setNextOutreachNote] = useState('');
  const [nextOutreachSaving, setNextOutreachSaving] = useState(false);
  const nextOutreach = company?.next_outreach || {};
  const isNextOutreach = !!nextOutreach.flagged;

  const openNextOutreachDialog = () => {
    setNextOutreachNote(nextOutreach.note || '');
    setNextOutreachDialogOpen(true);
  };

  const saveNextOutreach = async () => {
    setNextOutreachSaving(true);
    try {
      const payload = {
        next_outreach: {
          flagged: true,
          note: nextOutreachNote.trim(),
        },
      };
      const res = await axios.patch(`${API}/biotech-companies/${id}`, payload, { withCredentials: true });
      setCompany(prev => ({ ...prev, next_outreach: res?.data?.next_outreach || payload.next_outreach }));
      toast.success(isNextOutreach ? 'Note updated' : 'Added to Next Outreach queue');
      setNextOutreachDialogOpen(false);
    } catch { toast.error('Failed to update'); }
    finally { setNextOutreachSaving(false); }
  };

  const removeNextOutreach = async () => {
    setNextOutreachSaving(true);
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, { next_outreach: { flagged: false } }, { withCredentials: true });
      setCompany(prev => ({ ...prev, next_outreach: { flagged: false } }));
      toast.success('Removed from Next Outreach queue');
      setNextOutreachDialogOpen(false);
    } catch { toast.error('Failed to update'); }
    finally { setNextOutreachSaving(false); }
  };

  // ===== Snooze: temporary outreach block =====
  // When a biotech CEO replies "contact us in approximately one year" (Gipfel
  // case 2026-06-08), we stamp `snooze.until` so all NEW outreach (campaigns,
  // bulk, AI) silently skips them until the deadline lapses. Auto-clears on
  // expiry — no nightly job needed.
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState('');
  const [snoozeReason, setSnoozeReason] = useState('');
  const [snoozeSaving, setSnoozeSaving] = useState(false);
  const snoozeRaw = company?.snooze || {};
  const isSnoozed = !!(snoozeRaw.until && new Date(snoozeRaw.until) > new Date());
  const snoozeUntilDisplay = isSnoozed ? new Date(snoozeRaw.until).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  const openSnoozeDialog = () => {
    // Default to 1 year out from today (in YYYY-MM-DD form for <input type="date">).
    let initial = '';
    if (isSnoozed && snoozeRaw.until) {
      initial = snoozeRaw.until.slice(0, 10);
    } else {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      initial = d.toISOString().slice(0, 10);
    }
    setSnoozeUntil(initial);
    setSnoozeReason(snoozeRaw.reason || '');
    setSnoozeDialogOpen(true);
  };

  const saveSnooze = async () => {
    if (!snoozeUntil) { toast.error('Pick a snooze-until date'); return; }
    const untilDate = new Date(snoozeUntil + 'T00:00:00Z');
    if (untilDate <= new Date()) { toast.error('Snooze date must be in the future'); return; }
    setSnoozeSaving(true);
    try {
      const payload = {
        snooze: {
          until: untilDate.toISOString(),
          reason: snoozeReason.trim(),
        },
      };
      const res = await axios.patch(`${API}/biotech-companies/${id}`, payload, { withCredentials: true });
      setCompany(prev => ({ ...prev, snooze: res?.data?.snooze || payload.snooze }));
      toast.success(`Snoozed until ${untilDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`);
      setSnoozeDialogOpen(false);
    } catch { toast.error('Failed to snooze'); }
    finally { setSnoozeSaving(false); }
  };

  const removeSnooze = async () => {
    setSnoozeSaving(true);
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, { snooze: {} }, { withCredentials: true });
      setCompany(prev => ({ ...prev, snooze: {} }));
      toast.success('Snooze removed — outreach re-enabled');
      setSnoozeDialogOpen(false);
    } catch { toast.error('Failed to unsnooze'); }
    finally { setSnoozeSaving(false); }
  };

  // ===== Non-Confidential Deck (NCD) upload =====
  // Lets the user upload one PDF/PPTX deck per biotech company. The deck
  // becomes available on the public status page as a download button so
  // their pharma audiences can fetch it directly.
  const ncdInputRef = useRef(null);
  const [ncdUploading, setNcdUploading] = useState(false);
  const hasNcdDeck = !!(company?.ncd_deck && company.ncd_deck.filename);

  const handleNcdUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-uploading the same file fires onChange
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('File too large (max 8 MB)'); return; }
    setNcdUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/biotech-companies/${id}/ncd-deck`, fd, { withCredentials: true });
      setCompany(prev => ({ ...prev, ncd_deck: { filename: res.data.filename, size_bytes: res.data.size_bytes } }));
      toast.success('Non-Confidential Deck uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setNcdUploading(false);
    }
  };

  const handleNcdDelete = async () => {
    if (!window.confirm('Remove the uploaded Non-Confidential Deck?')) return;
    try {
      await axios.delete(`${API}/biotech-companies/${id}/ncd-deck`, { withCredentials: true });
      setCompany(prev => ({ ...prev, ncd_deck: null }));
      toast.success('Deck removed');
    } catch { toast.error('Failed to remove'); }
  };

  const saveMasterEdit = async () => {
    try {
      const validContacts = masterContacts.filter(c => c.name?.trim() || c.email?.trim());
      // Sanitize list fields that might be strings from Excel import
      const safeDealInterests = Array.isArray(company.deal_interests) ? company.deal_interests : [];
      const safeMolecules = Array.isArray(company.molecules) ? company.molecules.map(m => ({ name: m.name || '', target: m.target || '', modality: m.modality || '', indication: m.indication || '' })) : [];
      const safeTargetCompanies = Array.isArray(company.target_companies) ? company.target_companies.map(t => ({ name: t.name || '', country: t.country || '', contact_name: t.contact_name || '', contact_email: t.contact_email || '', status: t.status || '' })) : [];

      const companyPayload = {
        company_name: masterForm.company_name,
        location: masterForm.location || '',
        website: masterForm.website || '',
        therapeutic_area: masterForm.therapeutic_area || '',
        company_description: masterForm.company_description || '',
        agreement_status: masterForm.agreement_status || 'Not Started',
        ncd_status: masterForm.ncd_status || 'Not Received',
        funding_stage: masterForm.funding_stage || '',
        funding_amount: masterForm.funding_amount || '',
        use_of_funds: masterForm.use_of_funds || '',
        india_licensing: !!masterForm.india_licensing,
        contacts: validContacts.map(c => ({ name: c.name || '', designation: c.designation || '', email: c.email || '', phone: c.phone || '' })),
        // Preserve existing fields not shown in simplified form
        molecule_description: company.molecule_description || '',
        objectives: company.objectives || '',
        company_status: company.company_status || 'Not Started',
        deal_interest: company.deal_interest || '',
        deal_interests: safeDealInterests,
        stage: company.stage || 'Pre-Clinical',
        stage_notes: company.stage_notes || '',
        notes: typeof company.notes === 'string' ? company.notes : '',
        molecules: safeMolecules,
        target_companies: safeTargetCompanies,
        description: masterForm.company_description || company.description || '',
        contact_name: validContacts[0]?.name || company.contact_name || '',
        contact_email: validContacts[0]?.email || company.contact_email || '',
        contact_phone: validContacts[0]?.phone || company.contact_phone || '',
        contact_designation: validContacts[0]?.designation || company.contact_designation || '',
      };
      console.log('Saving company payload:', JSON.stringify(companyPayload, null, 2));
      await axios.put(`${API}/biotech-companies/${id}`, companyPayload, { withCredentials: true });

      // Save asset changes
      for (const a of masterAssets) {
        if (a.id) {
          try {
            const { id: assetId, ...assetFields } = a;
            await axios.put(`${API}/assets/${assetId}`, {
              ...assetFields,
              company_id: id,
            }, { withCredentials: true });
          } catch (assetErr) {
            console.error(`Failed to update asset ${a.asset_name}:`, assetErr);
          }
        }
      }

      // Upload any new documents
      let hasNcdUpload = false;
      for (const slot of masterDocSlots) {
        if (slot.file) {
          // For "Other", use the user-typed custom label as doc_type so it
          // shows on the company page tile. Falls back to "Other" if empty.
          const effectiveType = slot.type === 'Other'
            ? ((slot.customLabel || '').trim() || 'Other')
            : slot.type;
          const formData = new FormData();
          formData.append('file', slot.file);
          formData.append('entity_type', 'biotech');
          formData.append('entity_id', id);
          formData.append('doc_type', effectiveType);
          await uploadDocumentWithDedupe(formData);
          if (slot.type === 'Non-Confidential Deck' || slot.type === 'Corporate Overview') hasNcdUpload = true;
        }
      }

      setCompany(prev => ({ ...prev, ...masterForm, contacts: validContacts }));
      setFundraisingForm({
        funding_stage: masterForm.funding_stage || '',
        funding_amount: masterForm.funding_amount || '',
        use_of_funds: masterForm.use_of_funds || '',
      });
      setMasterEditOpen(false);

      // Auto-add to My Companies when status is Active
      if (companyPayload.company_status === 'Active' && !company.is_my_company) {
        try {
          const res = await axios.patch(`${API}/biotech-companies/${id}/toggle-my-company`, {}, { withCredentials: true });
          if (res.data.is_my_company) {
            setCompany(prev => ({ ...prev, is_my_company: true }));
          }
        } catch { /* silent */ }
      }

      if (hasNcdUpload) {
        toast.success('Company updated — AI is auto-populating CRM from the uploaded deck');
      } else {
        toast.success('Company updated');
      }
      fetchAll();
    } catch (err) {
      console.error('Save error:', err?.response?.data || err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Check all fields';
      toast.error('Failed to update: ' + msg);
    }
  };

  const copyTemplate = async () => {
    const assetsList = assets.map((a, i) => {
      return `--- Asset / Molecule ${i + 1} ---
Molecule/Product Number: ${a.asset_name || ''}
Development Stage: ${a.stage || ''}
Stage Notes: ${a.stage_notes || ''}
Platform: ${a.platform || ''}
Target: ${a.target || ''}
Modality: ${a.modality || ''}
Indication: ${a.indication || ''}
Drug Delivery Mechanism: ${a.drug_delivery_mechanism || ''}
Asset Description: ${a.molecule_description || a.description || ''}
Objectives: ${a.objectives || ''}
Looking For: ${(a.deal_interests || []).join(', ') || ''}
Emailing Status: ${a.emailing_status || 'Not Started'}
Status: ${a.status || 'Pipeline'}`;
    }).join('\n\n');

    const contactsList = (company.contacts || []).map((c, i) => {
      return `--- Contact ${i + 1} ---
Full Name: ${c.name || ''}
Designation: ${c.designation || ''}
Email: ${c.email || ''}
Phone: ${c.phone || ''}`;
    }).join('\n\n');

    const template = `--- Company Information ---
Company Name: ${company.company_name || ''}
Location: ${company.location || ''}
Website: ${company.website || ''}
Therapeutic Area: ${company.therapeutic_area || ''}
Company Description: ${company.company_description || company.description || ''}

${contactsList || '--- Contact ---\nFull Name: \nDesignation: \nEmail: \nPhone: '}

${assetsList || '--- Asset / Molecule 1 ---\nMolecule/Product Number: \nDevelopment Stage: \nStage Notes: \nPlatform: \nTarget: \nModality: \nIndication: \nDrug Delivery Mechanism: \nAsset Description: \nObjectives: \nLooking For: \nEmailing Status: \nStatus: '}`;

    try {
      await navigator.clipboard.writeText(template);
      toast.success('CRM template copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const updateOutreachStatus = async (outreachId, newStatus, notes) => {
    if (notes === undefined) {
      setStatusReasonDialog({ open: true, outreachId, status: newStatus });
      setStatusReason('');
      return;
    }
    try {
      const payload = { status: newStatus, status_notes: notes || '' };
      const res = await axios.patch(`${API}/outreach/${outreachId}/status`, payload, { withCredentials: true });
      setOutreach(prev => prev.map(o => o.id === outreachId ? { ...o, ...res.data } : o));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const submitStatusReason = async () => {
    await updateOutreachStatus(statusReasonDialog.outreachId, statusReasonDialog.status, statusReason);
    setStatusReasonDialog({ open: false, outreachId: null, status: '' });
    setStatusReason('');
  };

  const applyBulkStatus = async () => {
    const { group, status } = bulkStatusDialog;
    if (!group || !status) return;
    setBulkStatusApplying(true);
    try {
      const ids = group.rows.map(o => o.id);
      const res = await axios.post(`${API}/outreach/bulk-status`, {
        outreach_ids: ids,
        status,
        status_notes: bulkStatusNote || '',
      }, { withCredentials: true });
      const updatedMap = new Map((res.data.rows || []).map(r => [r.id, r]));
      const fallbackNote = bulkStatusNote || 'Bulk updated via company-level action';
      setOutreach(prev => prev.map(o => {
        if (updatedMap.has(o.id)) return { ...o, ...updatedMap.get(o.id) };
        if (ids.includes(o.id)) return { ...o, status, status_notes: fallbackNote };
        return o;
      }));
      const n = res.data.updated || 0;
      toast.success(`${n} contact${n === 1 ? '' : 's'} → ${status}`);
      if ((res.data.errors || []).length > 0) toast.error(`${res.data.errors.length} row(s) failed to update`);
      setBulkStatusDialog({ open: false, group: null, status: '' });
      setBulkStatusNote('');
    } catch { toast.error('Bulk update failed'); }
    finally { setBulkStatusApplying(false); }
  };

  const toggleExistingContact = (contactId, type) => {
    setOutreachSelectedIds(prev => {
      const exists = prev.includes(contactId);
      const next = exists ? prev.filter(x => x !== contactId) : [...prev, contactId];
      // Mirror the first selected contact into the form so the existing submit
      // path (and any size-1 edge case) keeps working.
      if (next.length > 0) {
        const list = contactPoolFor(type);
        const c = list.find(x => x.id === next[0]);
        if (c) {
          setOutreachForm(p => ({
            ...p,
            contact_name: type === 'Investor' ? (c.contact_person || c.name) : c.name,
            contact_email: c.email || '',
            company_name: type === 'Investor' ? (c.firm_name || c.name || '') : (c.company_name || ''),
            country: c.location || '',
            designation: c.designation || '',
            contact_type: type,
            source_id: c.id,
          }));
        }
      } else {
        setOutreachForm(p => ({
          ...p,
          contact_name: '', contact_email: '', company_name: '', country: '', designation: '', source_id: '',
        }));
      }
      return next;
    });
  };

  const selectExistingContact = (contactId, type) => {
    const list = contactPoolFor(type);
    const contact = list.find(c => c.id === contactId);
    if (contact) {
      setOutreachSelectedIds([contactId]);
      setOutreachForm(prev => ({
        ...prev,
        contact_name: type === 'Investor'
          ? (contact.contact_person || contact.name)
          : contact.name,
        contact_email: contact.email || '',
        company_name: type === 'Investor' ? (contact.firm_name || contact.name || '') : (contact.company_name || ''),
        country: contact.location || '',
        designation: contact.designation || '',
        contact_type: type,
        source_id: contact.id,
      }));
    }
  };

  const handleOutreachSubmit = async (e) => {
    e.preventDefault();
    // Resolve which assets to tag — at least one "slot" must run even when
    // the user picked no assets (legacy "no specific asset" behavior). The
    // submission then becomes contacts × assets (cartesian product) so each
    // (contact × asset) pair gets its own outreach row.
    const assetSlots = outreachSelectedAssetIds.length > 0
      ? outreachSelectedAssetIds.map(aid => {
          const a = assets.find(x => x.id === aid);
          return { asset_id: aid, asset_name: a?.asset_name || '' };
        })
      : [{ asset_id: outreachForm.asset_id || '', asset_name: outreachForm.asset_name || '' }];
    try {
      if (editingOutreachId) {
        // Merge the currently-selected asset into the form payload.
        // The edit modal uses `outreachSelectedAssetIds` (multi-select)
        // for asset picking, but the PUT endpoint takes a single-asset
        // shape. Without this merge, choosing "Product X" in the edit
        // dropdown silently drops it — user report 2026-02: "Kino
        // Pharma × Vaibhav — selecting an asset in the edit modal
        // doesn't save." Take the first slot (edit is a single-row op).
        const firstSlot = assetSlots[0] || {};
        const putPayload = {
          ...outreachForm,
          asset_id: firstSlot.asset_id ?? outreachForm.asset_id ?? '',
          asset_name: firstSlot.asset_name ?? outreachForm.asset_name ?? '',
        };
        await axios.put(`${API}/outreach/${editingOutreachId}`, putPayload, { withCredentials: true });
        toast.success('Outreach updated');
      } else if (outreachMode === 'existing' && outreachSelectedIds.length > 1) {
        const type = outreachForm.contact_type;
        const list = contactPoolFor(type);
        let created = 0, dupes = 0, failed = 0;
        for (const cid of outreachSelectedIds) {
          const c = list.find(x => x.id === cid);
          if (!c) { failed++; continue; }
          for (const slot of assetSlots) {
            const payload = {
              ...outreachForm,
              asset_id: slot.asset_id,
              asset_name: slot.asset_name,
              contact_type: type,
              source_id: c.id,
              contact_name: type === 'Investor' ? (c.contact_person || c.name) : c.name,
              contact_email: c.email || '',
              company_name: type === 'Investor' ? (c.firm_name || c.name || '') : (c.company_name || ''),
              country: c.location || '',
              designation: c.designation || '',
            };
            try {
              await axios.post(`${API}/outreach`, payload, { withCredentials: true });
              created++;
            } catch (err) {
              if (err.response?.status === 409) dupes++;
              else failed++;
            }
          }
        }
        if (created) toast.success(`Added ${created} outreach row${created > 1 ? 's' : ''}`);
        if (dupes) toast(`${dupes} already tagged — skipped`, { icon: '⚠️' });
        if (failed) toast.error(`${failed} failed to save`);
      } else if (assetSlots.length > 1) {
        // Single contact, multiple assets — fan out one row per asset.
        let created = 0, dupes = 0, failed = 0;
        for (const slot of assetSlots) {
          try {
            await axios.post(`${API}/outreach`, {
              ...outreachForm, asset_id: slot.asset_id, asset_name: slot.asset_name,
            }, { withCredentials: true });
            created++;
          } catch (err) {
            if (err.response?.status === 409) dupes++;
            else failed++;
          }
        }
        if (created) toast.success(`Added ${created} outreach row${created > 1 ? 's' : ''}`);
        if (dupes) toast(`${dupes} already tagged — skipped`, { icon: '⚠️' });
        if (failed) toast.error(`${failed} failed to save`);
      } else {
        // Single contact + 0 or 1 asset slot.
        const slot = assetSlots[0];
        const res = await axios.post(`${API}/outreach`, {
          ...outreachForm, asset_id: slot.asset_id, asset_name: slot.asset_name,
        }, { withCredentials: true });
        toast.success(res.data?.upgraded_existing ? `Updated the existing intro row to "${res.data.status}"` : 'Outreach saved');
      }
      setOutreachDialogOpen(false);
      setOutreachForm({ ...emptyOutreachForm });
      setOutreachSelectedIds([]);
      setOutreachSelectedAssetIds([]);
      setEditingOutreachId(null);
      fetchAll();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || 'Outreach for this contact already exists');
      } else {
        toast.error('Failed to save outreach');
      }
    }
  };

  const handleEditOutreach = (o) => {
    setOutreachForm({
      biotech_company_id: id,
      asset_id: o.asset_id || '',
      asset_name: o.asset_name || '',
      contact_name: o.contact_name || '',
      contact_email: o.contact_email || '',
      company_name: o.company_name || '',
      country: o.country || '',
      designation: o.designation || '',
      contact_type: o.contact_type || 'Pharma',
      status: o.status || 'Asset Under Evaluation',
      notes: o.notes || '',
      source_id: o.source_id || '',
      status_notes: o.status_notes || '',
    });
    setEditingOutreachId(o.id);
    setOutreachMode('manual');
    // Editing operates on the single existing row; keep multi-select empty
    // so the asset comes from the row's `asset_id`/`asset_name` fields.
    setOutreachSelectedAssetIds([]);
    setOutreachDialogOpen(true);
  };

  const deleteOutreach = async (outreachId) => {
    try {
      await axios.delete(`${API}/outreach/${outreachId}`, { withCredentials: true });
      setOutreach(prev => prev.filter(o => o.id !== outreachId));
      setAllOutreach(prev => prev.filter(o => o.id !== outreachId));
      toast.success('Removed');
      setDeleteTarget(null);
    } catch { toast.error('Failed to delete'); setDeleteTarget(null); }
  };

  const deleteCompany = async () => {
    if (!window.confirm(`Delete "${company.company_name}" and ALL linked data (assets, outreach, AI mappings, chat history, reminders)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/biotech-companies/${id}`, { withCredentials: true });
      toast.success('Company and all linked data deleted');
      navigate('/biotech');
    } catch { toast.error('Failed to delete company'); }
  };

  // ----- Merge another biotech into this one -----
  // Search the biotech list for a record to absorb. The current company
  // becomes the survivor; the picked record is dropped (its assets,
  // contacts, outreach etc. are repointed to the survivor and its row
  // is archived to admin_audit by the backend).
  useEffect(() => {
    if (!mergeDialogOpen) return;
    const q = mergeQuery.trim();
    if (q.length < 2) { setMergeResults([]); return; }
    let cancelled = false;
    setMergeSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/biotech-companies`, {
          params: { search: q, limit: 20, source: 'all' },
          withCredentials: true,
        });
        if (cancelled) return;
        // API returns { items, total, page, pages, source_counts, orphan_count }
        // — historical fallbacks kept for resilience.
        const list = (data.items || data.companies || (Array.isArray(data) ? data : []))
          .filter(c => c.id !== id);
        setMergeResults(list);
      } catch {
        if (!cancelled) setMergeResults([]);
      } finally {
        if (!cancelled) setMergeSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mergeQuery, mergeDialogOpen, id]);

  const handleConfirmMerge = async () => {
    if (!mergePicked) return;
    setMerging(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/merge-biotech-companies`,
        { survivor_id: id, drop_id: mergePicked.id },
        { withCredentials: true },
      );
      const moved = Object.entries(data.collections_moved || {})
        .filter(([, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${v} ${k}`).join(', ');
      toast.success(
        `Merged "${mergePicked.company_name}" into this record · +${data.contacts_added || 0} contacts${moved ? ' · moved ' + moved : ''}`,
        { duration: 8000 },
      );
      setMergeDialogOpen(false);
      // Refresh detail page
      try { window.location.reload(); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const handleShareStatus = async () => {
    try {
      const res = await axios.post(`${API}/share/${id}`, {}, { withCredentials: true });
      const url = `${window.location.origin}/status/${res.data.share_token}`;
      await navigator.clipboard.writeText(url);
      window.open(url, '_blank');
      toast.success('Share link copied & opened in new tab');
    } catch { toast.error('Failed to generate share link'); }
  };

  const handleDeactivateShare = async () => {
    // Ground rule (user 2026-02-18): deactivating the share link ALWAYS
    // means we're also stopping all outreach to this biotech. The two
    // actions are semantically inseparable — there's no realistic scenario
    // where you'd kill the share link but still want to keep emailing
    // them. So one confirm → two side effects.
    if (!window.confirm(
      'Deactivate the public share link AND mark this biotech as Do Not Contact?\n\n'
      + '• Anyone holding the existing share URL (including the biotech itself) will get a 404.\n'
      + '• The biotech will be flagged Do Not Contact — bulk outreach and intros will be blocked.\n\n'
      + 'You can re-enable both later by un-flagging Do Not Contact and clicking Share again.'
    )) return;

    // 1) Kill the share link. Decoupled from the DNC patch below so a
    //    failure on one doesn't block the other — biotechs without an
    //    active share link still need to be markable as DNC, and vice
    //    versa. Each step has its own try/catch + targeted toast.
    let shareResult = null;
    try {
      const res = await axios.delete(`${API}/share/${id}`, { withCredentials: true });
      shareResult = res.data || {};
    } catch (e) {
      // Surface server-side error if any (production debugging)
      // eslint-disable-next-line no-console
      console.error('[deactivate-share] DELETE /share failed:', e?.response?.status, e?.response?.data || e?.message);
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      toast.error(`Failed to deactivate share link: ${detail}`);
      return;
    }

    // 2) Flag Do Not Contact (paired action). Independent try so a DNC
    //    failure surfaces clearly without making the user think the
    //    share link is still live.
    const dncPayload = {
      flagged: true,
      reason: company?.do_not_contact?.reason || 'Share link deactivated — outreach paused',
      source: 'manual',
      flagged_at: new Date().toISOString(),
    };
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, {
        do_not_contact: dncPayload,
      }, { withCredentials: true });
      setCompany(prev => ({ ...(prev || {}), do_not_contact: dncPayload }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[deactivate-share] PATCH DNC failed:', e?.response?.status, e?.response?.data || e?.message);
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      const shareNote = shareResult?.deactivated
        ? 'Share link is deactivated, but'
        : (shareResult?.reason ? `${shareResult.reason} — but` : 'But');
      toast.error(`${shareNote} couldn't mark biotech as Do Not Contact: ${detail}`);
      return;
    }

    // Both steps succeeded — single confirmation toast.
    if (shareResult?.deactivated) {
      toast.success('Share link deactivated + biotech marked Do Not Contact');
    } else {
      toast.success(`${shareResult?.reason || 'No active share link'} — biotech marked Do Not Contact`);
    }
  };

  // --- Send Introduction helpers ---
  const handleIntroSearch = (val) => {
    setIntroSearch(val);
    if (introSearchTimeout.current) clearTimeout(introSearchTimeout.current);
    if (val.length < 2) { setIntroSearchResults([]); setIntroSearchError(''); setIntroSearchAttempted(false); return; }
    setIntroSearching(true);
    setIntroSearchError('');
    introSearchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/india-licensing/search/crm-companies?q=${encodeURIComponent(val)}`, { withCredentials: true });
        setIntroSearchResults(res.data.results || []);
        setIntroSearchError('');
      } catch (err) {
        setIntroSearchResults([]);
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setIntroSearchError('Session expired — please refresh and sign in again.');
        } else if (status >= 500) {
          setIntroSearchError(`Server error (${status}). Try again or contact support.`);
        } else {
          setIntroSearchError(err?.message || 'Search failed. Check your connection.');
        }
      }
      finally { setIntroSearching(false); setIntroSearchAttempted(true); }
    }, 300);
  };

  const selectIntroPharma = async (companyName, contactName) => {
    setIntroPharma(companyName);
    setIntroSearch(companyName);
    setIntroSearchResults([]);
    setIntroStep(2);
    setIntroLoadingContacts(true);
    // Pre-select all visible (non-hidden) assets
    const visibleAssets = (assets || []).filter(a => !a.is_hidden);
    const asel = {};
    visibleAssets.forEach(a => { asel[a.id] = true; });
    setIntroSelectedAssetIds(asel);
    try {
      // Fetch contacts and already-sent emails in parallel
      const [contactsRes, sentRes] = await Promise.all([
        axios.get(`${API}/india-licensing/search/contacts-by-company?company=${encodeURIComponent(companyName)}`, { withCredentials: true }),
        axios.get(`${API}/biotech-companies/${id}/sent-outreach-emails`, { withCredentials: true }).catch(() => ({ data: { sent_emails: [] } })),
      ]);
      const contacts = contactsRes.data.results || [];
      const sentMap = sentRes.data.sent_assets_by_email || Object.fromEntries((sentRes.data.sent_emails || []).map(e => [e.toLowerCase(), { asset_ids: [''], asset_names: [''] }]));
      setIntroSentAssets(sentMap);
      setIntroForceResend(false);
      // Asset-aware: sent for one of the assets being introduced (legacy rows w/o asset count for all)
      const sentEmails = new Set(Object.entries(sentMap).filter(([, v]) => {
        const ids = v?.asset_ids || [];
        return ids.includes('') || ids.some(a => introAsset ? a === introAsset.id : asel[a]);
      }).map(([em]) => em));
      setIntroContacts(contacts);
      // Auto-select rules (P0 safety after Sun-hawk → Medis cross-domain leak):
      //   1. Skip contacts already emailed for this biotech.
      //   2. Skip "stale_company_match" rows — CRM tag matches but email goes
      //      to a different domain. User must explicitly tick to include.
      //   3. If contactName is supplied (came from clicking a specific person
      //      in step-1 dropdown), match by name first.
      //   4. Otherwise auto-tick everyone safe — domain-matched OR no
      //      domain-keyword scenario (e.g. searching "Pfizer" with all
      //      contacts at @pfizer.com).
      const isSafe = (c) => c.email
        && !sentEmails.has(c.email.toLowerCase())
        && !c.stale_company_match;
      const sel = {};
      if (contactName) {
        contacts.forEach((c, i) => {
          if (isSafe(c) && c.name && c.name.toLowerCase().includes(contactName.toLowerCase())) sel[i] = true;
        });
      }
      if (Object.keys(sel).length === 0) {
        contacts.forEach((c, i) => { if (isSafe(c)) sel[i] = true; });
      }
      setIntroSelected(sel);
    } catch { toast.error('Failed to load contacts'); }
    finally { setIntroLoadingContacts(false); }
  };

  const handleDraftIntro = async () => {
    const selectedContacts = introContacts.filter((_, i) => introSelected[i]);
    if (selectedContacts.length === 0) { toast.error('Select at least one contact'); return; }
    const selectedAssets = Object.entries(introSelectedAssetIds).filter(([, v]) => v).map(([k]) => k);
    setIntroDrafting(true);
    try {
      const payload = {
        pharma_company: introPharma,
        contact_name: selectedContacts[0]?.name || '',
        asset_ids: selectedAssets,
        // First draft leaves partnering_intent blank so the backend
        // auto-derives from deal_interests + objectives. The user can
        // then edit the returned string at step 3 and click Regenerate.
        partnering_intent: '',
      };
      const res = await axios.post(`${API}/biotech-companies/${id}/draft-intro`, payload, { withCredentials: true });
      setIntroSubject(res.data.subject || '');
      setIntroBody(res.data.body || '');
      setIntroIntent(res.data.partnering_intent || '');
      setIntroStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to draft email');
    }
    finally { setIntroDrafting(false); }
  };

  const regenerateIntroWithIntent = async () => {
    const selectedAssets = Object.entries(introSelectedAssetIds).filter(([, v]) => v).map(([k]) => k);
    const selectedContacts = introContacts.filter((_, i) => introSelected[i]);
    setIntroRegenerating(true);
    try {
      const res = await axios.post(`${API}/biotech-companies/${id}/draft-intro`, {
        pharma_company: introPharma,
        contact_name: selectedContacts[0]?.name || '',
        asset_ids: selectedAssets,
        partnering_intent: introIntent || '',
      }, { withCredentials: true });
      setIntroSubject(res.data.subject || '');
      setIntroBody(res.data.body || '');
      // Server may have normalized the intent — reflect that back.
      if (res.data.partnering_intent) setIntroIntent(res.data.partnering_intent);
      toast.success('Email regenerated using your partnering intent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to regenerate');
    } finally {
      setIntroRegenerating(false);
    }
  };

  // Sep 2026 · downloads go through axios (Bearer + cookie) instead of a bare
  // <a href> that only carries the cookie — on production the cookie is often
  // stale/stripped by the edge while the app stays logged in via Bearer, so
  // direct links 401'd ("not able to download documents").
  // Sep 2026 (CureLab): agreements are shown from the documents list itself, not
  // only via company.cda_document_id — an upload whose link step failed (proxy
  // timeout, cancelled duplicate prompt) was invisible although the file existed.
  const legalDocs = (kind) => (docs || []).filter(d => {
    const t = String(d.doc_type || '').trim().toLowerCase();
    if (kind === 'cda') return t === 'cda' || t === 'nda' || t === 'signed cda';
    return t === 'partnership agreement' || (/(agreement)/.test(t) && !/buyer|term\s*sheet|cda/.test(t));
  }).sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

  useEffect(() => {
    if (!company?.id || !docs?.length) return;
    const patch = {};
    const cda = legalDocs('cda')[0];
    const pa = legalDocs('partnership')[0];
    if (cda && (!company.cda_document_id || !docs.some(d => d.id === company.cda_document_id))) {
      patch.cda_document_id = cda.id;
      if (!company.cda_status || company.cda_status === 'Not Started') patch.cda_status = 'Signed';
    }
    if (pa && (!company.partnership_document_id || !docs.some(d => d.id === company.partnership_document_id))) {
      patch.partnership_document_id = pa.id;
      if (!company.partnership_status || company.partnership_status === 'Not Started') patch.partnership_status = 'Signed';
    }
    if (!Object.keys(patch).length) return;
    axios.patch(`${API}/biotech-companies/${company.id}`, patch, { withCredentials: true })
      .then(() => setCompany(prev => ({ ...prev, ...patch })))
      .catch(() => {});
  }, [company?.id, company?.cda_document_id, company?.partnership_document_id, docs]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, { responseType: 'blob', withCredentials: true });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename || 'document'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      let msg = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (!msg && err?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text())?.detail || ''; } catch { /* swallow */ }
      }
      if (err?.response?.status === 401) msg = 'Your session has expired — please log in again and retry the download.';
      toast.error(msg || 'Could not download the file — please try again or re-upload it', { duration: 8000 });
    }
  };

  const openShareDoc = (doc) => {
    setShareDoc(doc);
    setShareToEmail('');
    setShareToName('');
    setShareToSearch('');
    setShareToDropdown(false);
    const leadAsset = assets.find(a => a.asset_name) || {};
    const assetName = leadAsset.asset_name || company.company_name;
    const indication = leadAsset.indication || '';
    setShareSubject(indication ? `NCD: ${assetName} — ${indication} Overview` : `NCD: ${assetName} Overview`);
    setShareMessage('');
    setShareSending(false);
  };

  const selectShareRecipient = (contact) => {
    const firstName = (contact.name || '').split(' ')[0] || '';
    setShareToName(contact.name || '');
    setShareToEmail(contact.email || '');
    setShareToSearch('');
    setShareToDropdown(false);
    setShareMessage(`Dear ${firstName},\n\nPlease find attached the Non-Confidential Deck for ${company.company_name}.\n\nBest regards,`);
  };

  const handleShareDoc = async (testMode = false) => {
    if (!shareDoc || !shareToEmail) { toast.error('Select a recipient'); return; }
    setShareSending(true);
    try {
      await axios.post(`${API}/documents/${shareDoc.id}/share-email`, {
        to_email: testMode ? '__test__' : shareToEmail,
        to_name: shareToName,
        cc_emails: [],
        subject: shareSubject,
        message: shareMessage,
        test_mode: testMode,
      }, { withCredentials: true });
      toast.success(testMode ? 'Test email sent to your inbox' : `Sending to ${shareToName || shareToEmail}`);
      if (!testMode) setShareDoc(null);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Gmail not connected');
      else toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally { setShareSending(false); }
  };


  const handleCdaStatusChange = async (status) => {
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, { cda_status: status }, { withCredentials: true });
      setCompany(prev => ({ ...prev, cda_status: status }));
      toast.success(`CDA status updated to "${status}"`);
    } catch { toast.error('Failed to update CDA status'); }
  };

  const handleCdaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCdaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'CDA');
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      const docId = uploadedDoc?.id || uploadedDoc?.document_id || '';
      await axios.patch(`${API}/biotech-companies/${id}`, { cda_status: 'Signed', cda_document_id: docId }, { withCredentials: true });
      setCompany(prev => ({ ...prev, cda_status: 'Signed', cda_document_id: docId }));
      // Optimistic: prepend the new doc so the Documents tile renders it immediately
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success('CDA uploaded — status set to Signed');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file type — only PDF / DOCX accepted' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Failed to upload CDA';
      toast.error(msg, { duration: 8000 });
      console.error('[CDA upload error]', { status, detail, err });
    }
    finally { setCdaUploading(false); if (cdaInputRef.current) cdaInputRef.current.value = ''; }
  };

  const handlePartnershipStatusChange = async (status) => {
    try {
      await axios.patch(`${API}/biotech-companies/${id}`, { partnership_status: status }, { withCredentials: true });
      setCompany(prev => ({ ...prev, partnership_status: status }));
      toast.success(`Partnership Agreement status updated to "${status}"`);
    } catch { toast.error('Failed to update Partnership status'); }
  };

  const handlePartnershipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPartnershipUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Partnership Agreement');
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      const docId = uploadedDoc?.id || uploadedDoc?.document_id || '';
      await axios.patch(`${API}/biotech-companies/${id}`, { partnership_status: 'Signed', partnership_document_id: docId }, { withCredentials: true });
      setCompany(prev => ({ ...prev, partnership_status: 'Signed', partnership_document_id: docId }));
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success('Partnership Agreement uploaded — status set to Signed');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (server limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file type — only PDF / DOCX accepted' : '')
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Failed to upload Partnership Agreement';
      toast.error(msg, { duration: 10000 });
      console.error('[Partnership upload error]', { status, detail, err });
    }
    finally { setPartnershipUploading(false); if (partnershipInputRef.current) partnershipInputRef.current.value = ''; }
  };

  // ===== Buyer CDA (biotech ↔ pharma buyer) — supports multiple over time.
  // Each upload requires a counterparty (the pharma buyer like "Alcon") so
  // the same biotech can stack CDAs with different buyers cleanly. Stored as
  // documents with doc_type = "Buyer CDA" and counterparty_name on the doc.
  // File is optional — a tracker-only entry is allowed for "Under Discussion"
  // status before the signed PDF exists.
  const handleBuyerCdaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const cp = (buyerCdaCounterparty || '').trim();
    if (!cp) {
      toast.error('Enter the counterparty name first (e.g., "Alcon")');
      if (buyerCdaInputRef.current) buyerCdaInputRef.current.value = '';
      return;
    }
    setBuyerCdaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Buyer CDA');
      formData.append('counterparty_name', cp);
      formData.append('status', buyerCdaStatus || 'Finalised');
      if (buyerCdaSignedDate) formData.append('signed_date', buyerCdaSignedDate);
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success(`CDA with ${cp} uploaded`);
      setBuyerCdaCounterparty('');
      setBuyerCdaSignedDate('');
      setBuyerCdaStatus('Shared with Them');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (server limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file or missing field — check file type (PDF/DOCX)' : '')
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status === 500 ? 'Server error during upload — check backend logs' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Failed to upload CDA';
      toast.error(msg, { duration: 10000 });
      console.error('[Buyer CDA upload error]', { status, detail, err });
    }
    finally { setBuyerCdaUploading(false); if (buyerCdaInputRef.current) buyerCdaInputRef.current.value = ''; }
  };

  // Tracker-only entry — no file yet. Used when the CDA is being negotiated
  // but the signed PDF doesn't exist. The user can attach the file later by
  // creating another entry, or by re-uploading once signed.
  const handleBuyerCdaTrackerAdd = async () => {
    const cp = (buyerCdaCounterparty || '').trim();
    if (!cp) {
      toast.error('Enter the counterparty name first (e.g., "Alcon")');
      return;
    }
    setBuyerCdaSavingTracker(true);
    try {
      const formData = new FormData();
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Buyer CDA');
      formData.append('counterparty_name', cp);
      formData.append('status', buyerCdaStatus || 'Under Discussion');
      if (buyerCdaSignedDate) formData.append('signed_date', buyerCdaSignedDate);
      const { data: uploadedDoc } = await uploadDocumentWithDedupe(formData);
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success(`CDA tracker for ${cp} added (${buyerCdaStatus})`);
      setBuyerCdaCounterparty('');
      setBuyerCdaSignedDate('');
      setBuyerCdaStatus('Shared with Them');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status ? `Tracker add failed (HTTP ${status})` : err?.message)
        || 'Failed to add tracker entry';
      toast.error(msg, { duration: 10000 });
      console.error('[Buyer CDA tracker error]', { status, detail, err });
    }
    finally { setBuyerCdaSavingTracker(false); }
  };

  const handleBuyerCdaStatusChange = async (docId, newStatus) => {
    try {
      await axios.patch(`${API}/documents/${docId}`, { status: newStatus }, { withCredentials: true });
      // Optimistic: update local state so the pill flips immediately
      setDocs(prev => (prev || []).map(d => d.id === docId ? { ...d, status: newStatus } : d));
      toast.success(`Status → ${newStatus}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  // ── Term Sheet handlers ─────────────────────────────────────────────────
  // Multiple term sheets per counterparty are allowed (negotiation versions:
  // EN draft, ZH draft, redline v2, counterproposal, etc.). They are shown
  // newest-first inside each counterparty's engagement card.
  const handleTermSheetUploadInline = async (counterparty, file) => {
    if (!file) return;
    const cp = (counterparty || '').trim();
    if (!cp) { toast.error('Counterparty name is required'); return; }
    setTermSheetUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Term Sheet');
      formData.append('counterparty_name', cp);
      formData.append('status', 'Draft Shared');
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success(`Term sheet added for ${cp}`);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (status === 413 ? 'File too large — try a smaller PDF' : '')
        || (status ? `Upload failed (HTTP ${status})` : 'Failed to upload term sheet');
      toast.error(msg, { duration: 8000 });
    }
    finally { setTermSheetUploading(false); }
  };

  const handleCdaUploadInline = async (counterparty, file) => {
    if (!file) return;
    const cp = (counterparty || '').trim();
    if (!cp) { toast.error('Counterparty name is required'); return; }
    setBuyerCdaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Buyer CDA');
      formData.append('counterparty_name', cp);
      formData.append('status', 'Finalised');
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success(`CDA added for ${cp}`);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (status === 413 ? 'File too large — try a smaller PDF' : '')
        || (status ? `Upload failed (HTTP ${status})` : 'Failed to upload CDA');
      toast.error(msg, { duration: 8000 });
    }
    finally { setBuyerCdaUploading(false); }
  };

  const handleTermSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const cp = (termSheetCounterparty || '').trim();
    if (!cp) {
      toast.error('Enter the counterparty name first (e.g., "Sinopharm")');
      if (termSheetInputRef.current) termSheetInputRef.current.value = '';
      return;
    }
    setTermSheetUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Term Sheet');
      formData.append('counterparty_name', cp);
      formData.append('status', termSheetStatus || 'Draft Shared');
      if (termSheetSharedDate) formData.append('signed_date', termSheetSharedDate);
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      if (uploadedDoc?.id) {
        setDocs(prev => [uploadedDoc, ...(prev || [])]);
      }
      toast.success(`Term sheet for ${cp} uploaded`);
      setTermSheetCounterparty('');
      setTermSheetSharedDate('');
      setTermSheetStatus('Draft Shared');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (server limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file or missing field — check file type (PDF/DOCX)' : '')
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status === 500 ? 'Server error during upload — check backend logs' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Failed to upload term sheet';
      toast.error(msg, { duration: 10000 });
      console.error('[Term Sheet upload error]', { status, detail, err });
    }
    finally { setTermSheetUploading(false); if (termSheetInputRef.current) termSheetInputRef.current.value = ''; }
  };

  const handleTermSheetTrackerAdd = async () => {
    const cp = (termSheetCounterparty || '').trim();
    if (!cp) {
      toast.error('Enter the counterparty name first (e.g., "Sinopharm")');
      return;
    }
    setTermSheetSavingTracker(true);
    try {
      const formData = new FormData();
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', 'Term Sheet');
      formData.append('counterparty_name', cp);
      formData.append('status', termSheetStatus || 'Under Negotiation');
      if (termSheetSharedDate) formData.append('signed_date', termSheetSharedDate);
      const { data: uploadedDoc } = await uploadDocumentWithDedupe(formData);
      if (uploadedDoc?.id) {
        setDocs(prev => [uploadedDoc, ...(prev || [])]);
      }
      toast.success(`Term sheet tracker for ${cp} added (${termSheetStatus})`);
      setTermSheetCounterparty('');
      setTermSheetSharedDate('');
      setTermSheetStatus('Draft Shared');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status ? `Tracker add failed (HTTP ${status})` : err?.message)
        || 'Failed to add tracker entry';
      toast.error(msg, { duration: 10000 });
    }
    finally { setTermSheetSavingTracker(false); }
  };

  const handleTermSheetStatusChange = async (docId, newStatus) => {
    try {
      await axios.patch(`${API}/documents/${docId}`, { status: newStatus }, { withCredentials: true });
      setDocs(prev => (prev || []).map(d => d.id === docId ? { ...d, status: newStatus } : d));
      toast.success(`Term sheet → ${newStatus}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  // ── Per-CDA Data-Access Contacts ────────────────────────────────────────
  // Track ad-hoc evaluators (counterparty's MSL team / external scientific
  // advisors / extended diligence team) who get data-room access alongside
  // the signing person. Stored as an embedded array on the document so we
  // don't pollute pharma_contacts / biotech_contacts with non-CRM names.
  const handleAddDataAccessContact = async (docId, contact) => {
    try {
      const res = await axios.post(
        `${API}/documents/${docId}/data-access-contacts`,
        contact,
        { withCredentials: true },
      );
      setDocs(prev => (prev || []).map(d => {
        if (d.id !== docId) return d;
        const existing = d.data_access_contacts || [];
        const idx = existing.findIndex(c => (c.email || '').toLowerCase() === (res.data.contact.email || '').toLowerCase() && res.data.contact.email);
        const next = idx >= 0 ? [...existing.slice(0, idx), res.data.contact, ...existing.slice(idx + 1)] : [...existing, res.data.contact];
        return { ...d, data_access_contacts: next };
      }));
      toast.success(`Added ${res.data.contact.name || res.data.contact.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleRemoveDataAccessContact = async (docId, contactId) => {
    try {
      await axios.delete(
        `${API}/documents/${docId}/data-access-contacts/${contactId}`,
        { withCredentials: true },
      );
      setDocs(prev => (prev || []).map(d => d.id === docId
        ? { ...d, data_access_contacts: (d.data_access_contacts || []).filter(c => c.id !== contactId) }
        : d
      ));
    } catch { toast.error('Failed to remove'); }
  };



  // ===== Inline Documents upload — mirrors the Edit Master "Documents" slot
  // logic. Picks doc_type from DOC_TYPE_OPTIONS; if "Other", uses the typed
  // custom label as the doc_type so it shows on the tile with the right
  // pill colour. POSTs the same /documents/upload endpoint used elsewhere.
  const handleInlineDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const effectiveType = inlineDocType === 'Other'
      ? ((inlineDocCustomLabel || '').trim() || 'Other')
      : inlineDocType;
    setInlineDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'biotech');
      formData.append('entity_id', id);
      formData.append('doc_type', effectiveType);
      const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
      if (skipped) return;
      if (uploadedDoc?.id) setDocs(prev => [uploadedDoc, ...(prev || [])]);
      toast.success(`${effectiveType} uploaded`);
      setInlineDocCustomLabel('');
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (server limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file or missing field' : '')
        || (status === 401 ? 'Session expired — please re-login and retry' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Failed to upload document';
      toast.error(msg, { duration: 10000 });
      console.error('[Inline doc upload error]', { status, detail, err });
    }
    finally { setInlineDocUploading(false); if (inlineDocFileRef.current) inlineDocFileRef.current.value = ''; }
  };

  // ===== Outreach Update =====
  const openOutreachUpdate = () => {
    setOutreachUpdateOpen(true);
    setOutreachUpdateStep(1);
    setOutreachUpdateSubject('');
    setOutreachUpdateBody('');
    setOutreachUpdateContext('');
    // Set contacts from company
    const contacts = company?.contacts || [];
    setOutreachUpdateContacts(contacts);
    const sel = {};
    contacts.forEach((c, i) => { if (c.email) sel[i] = true; });
    setOutreachUpdateSelectedContacts(sel);
  };

  const handleOutreachUpdateDraft = async () => {
    setOutreachUpdateDrafting(true);
    try {
      const { data } = await axios.post(`${API}/biotech-companies/${id}/draft-outreach-update`, {
        extra_context: outreachUpdateContext,
      }, { withCredentials: true, timeout: 60000 });
      setOutreachUpdateSubject(data.subject || '');
      setOutreachUpdateBody(data.body || '');
      setOutreachUpdateStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to draft update');
    } finally { setOutreachUpdateDrafting(false); }
  };

  const handleOutreachUpdateSend = async () => {
    if (outreachUpdateSendingRef.current) return;
    const recipients = outreachUpdateContacts
      .filter((_, i) => outreachUpdateSelectedContacts[i])
      .map(c => ({ name: c.name, email: c.email }));
    if (recipients.length === 0) { toast.error('Select at least one contact'); return; }
    outreachUpdateSendingRef.current = true;
    setOutreachUpdateSending(true);
    try {
      await axios.post(`${API}/biotech-companies/${id}/send-intro`, {
        recipients,
        subject: outreachUpdateSubject,
        body: outreachUpdateBody,
        pharma_company: '',
      }, { withCredentials: true, timeout: 30000 });
      toast.success(`Sending outreach update to ${recipients.length} contact(s) — check Gmail Sent folder`);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Gmail not connected.');
      else toast.info('Email is being processed — check your Gmail Sent folder.');
    } finally {
      setOutreachUpdateSending(false);
      outreachUpdateSendingRef.current = false;
      setOutreachUpdateOpen(false);
      try { fetchAll(); } catch {}
    }
  };

  const [outreachUpdateTestSending, setOutreachUpdateTestSending] = useState(false);
  const [outreachVoiceListening, setOutreachVoiceListening] = useState(false);
  const outreachVoiceRef = useRef(null);
  const outreachVoiceWantsRef = useRef(false);

  const toggleOutreachVoice = () => {
    if (outreachVoiceListening) {
      outreachVoiceWantsRef.current = false;
      outreachVoiceRef.current?.stop();
      setOutreachVoiceListening(false);
      // Clean interim markers
      setOutreachUpdateContext(prev => prev.replace(/\u200B.*$/, '').trim());
      return;
    }
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported'); return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = true; recognition.interimResults = true; recognition.lang = 'en-US';
    outreachVoiceRef.current = recognition;
    outreachVoiceWantsRef.current = true;
    let committedIndex = 0;

    recognition.onresult = (e) => {
      let finalizedNew = '';
      let interim = '';
      for (let i = committedIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          finalizedNew += e.results[i][0].transcript;
          committedIndex = i + 1;
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      if (finalizedNew) {
        setOutreachUpdateContext(prev => {
          const clean = prev.replace(/\u200B.*$/, '').trim();
          return clean ? `${clean} ${finalizedNew.trim()}` : finalizedNew.trim();
        });
      }
      if (interim) {
        setOutreachUpdateContext(prev => {
          const clean = prev.replace(/\u200B.*$/, '').trim();
          return `${clean}\u200B ${interim}`;
        });
      }
    };
    recognition.onend = () => {
      setOutreachUpdateContext(prev => prev.replace(/\u200B.*$/, '').trim());
      if (outreachVoiceWantsRef.current) { try { recognition.start(); committedIndex = 0; } catch {} }
      else setOutreachVoiceListening(false);
    };
    recognition.onerror = () => { setOutreachVoiceListening(false); outreachVoiceWantsRef.current = false; };
    try { recognition.start(); setOutreachVoiceListening(true); } catch { toast.error('Could not start voice input'); }
  };

  const handleOutreachUpdateTestSend = async () => {
    if (!outreachUpdateSubject || !outreachUpdateBody) { toast.error('Draft email first'); return; }
    setOutreachUpdateTestSending(true);
    try {
      await axios.post(`${API}/biotech-companies/${id}/send-intro`, {
        recipients: [{ name: 'Test', email: '__TEST__' }],
        subject: `[TEST] ${outreachUpdateSubject}`,
        body: outreachUpdateBody,
        pharma_company: '',
        test_mode: true,
      }, { withCredentials: true, timeout: 30000 });
      toast.success('Test email queued — check your inbox');
    } catch { toast.info('Test email is being processed — check your inbox.'); }
    finally { setOutreachUpdateTestSending(false); }
  };




  const handleSendIntro = async (opts = {}) => {
    if (introSendingRef.current) return;
    const overrideRecipients = Array.isArray(opts.recipients) ? opts.recipients : null;
    const selectedContacts = overrideRecipients
      || introContacts.filter((_, i) => introSelected[i]).map(c => ({ name: c.name, email: c.email, id: c.id || '' }));
    if (selectedContacts.length === 0) { toast.error('No contacts selected'); return; }
    const introAssetIds = introAsset ? [introAsset.id] : Object.keys(introSelectedAssetIds).filter(k => introSelectedAssetIds[k]);
    introSendingRef.current = true;
    setIntroSending(true);
    let keepOpen = false;
    try {
      const res = await axios.post(`${API}/biotech-companies/${id}/send-intro`, {
        recipients: selectedContacts,
        subject: introSubject,
        body: introBody,
        pharma_company: introPharma,
        asset_ids: introAssetIds,
        routing_override: opts.override === true,
        force_resend: introForceResend,
      }, { withCredentials: true, timeout: 30000 });
      setIntroRoutingBlock(null);
      const skipped = res.data.skipped_already_sent || 0;
      const sending = res.data.sent || res.data.total || 0;
      if (sending === 0 && skipped > 0) {
        toast.error(
          `Nothing was sent — ${skipped === 1 ? 'this contact was' : `all ${skipped} contacts were`} already emailed about this asset for ${company?.company_name || 'this biotech'}. Tick "Re-send to already-emailed contacts" below to send anyway.`,
          { duration: 12000 }
        );
        keepOpen = true;
        return;
      } else if (skipped > 0) {
        toast.success(`Sending ${sending} email(s) — ${skipped} already sent were skipped. Check Gmail Sent folder.`);
      } else {
        toast.success(`Sending ${sending} introduction email(s) — check your Gmail Sent folder to confirm`);
      }
    } catch (err) {
      console.error('[SendIntro] Error details:', err?.response?.status, err?.response?.data, err?.code, err?.message);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.error === 'routing_policy_violation') {
        // Aug 2026 · Recipient-Rule gate — offer the correct alias or override.
        setIntroRoutingBlock(detail);
      } else if (err?.response?.status === 403) {
        toast.error('Gmail not connected. Please connect Gmail first.');
      } else if (err?.response?.status === 400 || err?.response?.status === 404) {
        toast.error(err?.response?.data?.detail || 'Request failed');
      } else {
        toast.info('Email is being processed — check your Gmail Sent folder to confirm.');
      }
    } finally {
      setIntroSending(false);
      introSendingRef.current = false;
      if (!keepOpen) setIntroOpen(false);
      try { fetchAll(); } catch {}
    }
  };

  const handleSendTestIntro = async () => {
    setIntroSendingTest(true);
    try {
      await axios.post(`${API}/biotech-companies/${id}/send-intro`, {
        recipients: [{ name: 'Test', email: '__TEST__' }],
        subject: `[TEST] ${introSubject}`,
        body: introBody,
        pharma_company: introPharma,
        test_mode: true,
      }, { withCredentials: true });
      toast.success('Test email queued — check your inbox shortly');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send test email');
    }
    finally { setIntroSendingTest(false); }
  };

  const copyOutreachSummary = () => {
    const tabData = outreach.filter(o => o.contact_type !== 'Investor');
    const label = 'Pharma';
    if (tabData.length === 0) { toast.error(`No ${label.toLowerCase()} outreach data to copy`); return; }

    // Deduplicate by company_name + country
    const grouped = {};
    for (const o of tabData) {
      const key = `${(o.company_name || '').trim().toLowerCase()}|${(o.country || '').trim().toLowerCase()}`;
      if (!grouped[key]) {
        grouped[key] = { ...o, _assets: new Set(), _notes: new Set() };
      }
      const g = grouped[key];
      if (o.asset_name) g._assets.add(o.asset_name);
      if (o.status_notes) g._notes.add(o.status_notes);
      // Keep most recent date
      if ((o.updated_at || '') > (g.updated_at || '')) g.updated_at = o.updated_at;
    }
    const sorted = Object.values(grouped)
      .map(g => ({ ...g, asset_name: [...g._assets].join(', '), status_notes: [...g._notes].join('; ') }))
      .sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));

    // HTML table for rich paste (Gmail, Outlook, etc.)
    const htmlRows = sorted.map(o => {
      const statusColor = o.status === 'Declined' ? '#e11d48' : o.status === 'Ghosted' ? '#78716c' : o.status === 'Closed - Successful' ? '#059669' : o.status === 'Awaiting Trial Data' ? '#ea580c' : '#334155';
      const notes = o.status_notes || '';
      return `<tr>
        <td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:13px">${o.company_name || '—'}</td>
        <td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:13px">${o.country || '—'}</td>
        <td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:13px">${o.asset_name || '—'}</td>
        <td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:13px;color:${statusColor};font-weight:600">${o.status || '—'}</td>
        <td style="padding:6px 12px;border:1px solid #e2e8f0;font-size:12px;color:#64748b">${notes}</td>
      </tr>`;
    }).join('');

    const html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;width:100%">
      <thead><tr style="background:#f8fafc">
        <th style="padding:8px 12px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em">Company</th>
        <th style="padding:8px 12px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em">Country</th>
        <th style="padding:8px 12px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em">Asset</th>
        <th style="padding:8px 12px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em">Status</th>
        <th style="padding:8px 12px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em">Notes</th>
      </tr></thead>
      <tbody>${htmlRows}</tbody>
    </table>`;

    // Plain text fallback
    const plain = ['Company\tCountry\tAsset\tStatus\tNotes', ...sorted.map(o => `${o.company_name || '—'}\t${o.country || '—'}\t${o.asset_name || '—'}\t${o.status || '—'}\t${o.status_notes || ''}`)].join('\n');

    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })]).then(() => {
      toast.success(`Copied ${sorted.length} ${label.toLowerCase()} outreach entries as table`);
    }).catch(() => toast.error('Failed to copy'));
  };

  // Asset handlers
  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await axios.put(`${API}/assets/${editingAsset}`, assetForm, { withCredentials: true });
        toast.success('Asset updated');
      } else {
        await axios.post(`${API}/assets`, assetForm, { withCredentials: true });
        toast.success('Asset added');
      }
      setAssetDialogOpen(false);
      setAssetForm({ ...emptyAssetForm });
      setEditingAsset(null);
      fetchAll();
    } catch {
      toast.error('Failed to save asset');
    }
  };

  const handleEditAsset = (a) => {
    setAssetForm({
      company_id: id,
      asset_name: a.asset_name || '',
      therapeutic_area: a.therapeutic_area || '',
      stage: a.stage || 'Pre-Clinical',
      stage_notes: a.stage_notes || '',
      modality: a.modality || '',
      indication: a.indication || '',
      target: a.target || '',
      platform: a.platform || '',
      drug_delivery_mechanism: a.drug_delivery_mechanism || '',
      molecule_description: a.molecule_description || '',
      deal_interests: a.deal_interests || [],
      objectives: a.objectives || '',
      ncd_status: a.ncd_status || 'Not Received',
      emailing_status: a.emailing_status || 'Not Started',
      status: a.status || a.conversation_status || 'Pipeline',
    });
    setEditingAsset(a.id);
    setAssetDialogOpen(true);
  };

  const handleDeleteAsset = async () => {
    if (!deleteAssetTarget) return;
    try {
      await axios.delete(`${API}/assets/${deleteAssetTarget.id}`, { withCredentials: true });
      toast.success('Asset deleted');
      setDeleteAssetTarget(null);
      fetchAll();
    } catch {
      toast.error('Failed to delete asset');
    }
  };

  // ===== Move asset to another company =====
  const openMoveAsset = (a) => {
    setMoveAsset(a);
    setMoveSearch('');
    setMoveResults([]);
    setMoveTarget(null);
  };

  const handleMoveSearch = (val) => {
    setMoveSearch(val);
    if (moveSearchTimer.current) clearTimeout(moveSearchTimer.current);
    if (val.trim().length < 2) { setMoveResults([]); return; }
    setMoveSearching(true);
    moveSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/biotech-companies?search=${encodeURIComponent(val.trim())}&limit=15&slim=1`, { withCredentials: true });
        const items = (data.items || data.companies || data || []).filter(c => c.id !== id);
        setMoveResults(items);
      } catch { setMoveResults([]); }
      finally { setMoveSearching(false); }
    }, 300);
  };

  const handleConfirmMove = async () => {
    if (!moveAsset || !moveTarget || movingAsset) return;
    setMovingAsset(true);
    try {
      const { data } = await axios.post(
        `${API}/assets/${moveAsset.id}/reassign-company`,
        { target_company_id: moveTarget.id },
        { withCredentials: true }
      );
      const tier = data.recommendation ? ` · AI tier: ${data.recommendation}` : '';
      toast.success(`"${data.asset_name}" moved to ${data.to_company.name} (${data.to_company.country})${tier}`, { duration: 6000 });
      setMoveAsset(null);
      setMoveTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to move asset');
    } finally { setMovingAsset(false); }
  };


  const handleToggleHidden = async (asset) => {
    try {
      const { data } = await axios.put(`${API}/assets/${asset.id}/toggle-hidden`, {}, { withCredentials: true });
      toast.success(data.is_hidden ? `${asset.asset_name} hidden from main table` : `${asset.asset_name} visible in main table`);
      fetchAll();
    } catch {
      toast.error('Failed to update');
    }
  };


  const handleMigrateAssets = async () => {
    setMigrating(true);
    try {
      const res = await axios.post(`${API}/assets/migrate/${id}`, {}, { withCredentials: true });
      if (res.data.count > 0) {
        toast.success(`Migrated ${res.data.count} asset(s) from existing data`);
        fetchAll();
      } else {
        toast.info('No data to migrate');
      }
    } catch {
      toast.error('Failed to migrate assets');
    } finally {
      setMigrating(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name?.trim() && !contactForm.email?.trim()) {
      toast.error('Please provide at least a name or email');
      return;
    }
    try {
      let updatedContacts;
      if (editingContactIdx !== null) {
        updatedContacts = [...(company.contacts || [])];
        updatedContacts[editingContactIdx] = contactForm;
      } else {
        updatedContacts = [...(company.contacts || []), contactForm];
      }
      const { _id, ...safeCompany } = company;
      const res = await axios.put(`${API}/biotech-companies/${id}`, { ...safeCompany, contacts: updatedContacts }, { withCredentials: true });
      console.log('[add-contact] OK', res?.status, res?.data?.contacts?.length, 'contacts');
      toast.success(editingContactIdx !== null ? 'Contact updated' : 'Contact added');
      setContactDialogOpen(false);
      setContactForm({ ...emptyContact });
      setEditingContactIdx(null);
      fetchAll();
    } catch (err) {
      console.error('[add-contact] FAILED', err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Failed to save contact: ${detail}`, { duration: 8000 });
    }
  };

  const handleEditContact = (idx) => {
    const ct = (company.contacts || [])[idx];
    if (ct) {
      setContactForm({ name: ct.name || '', designation: ct.designation || '', email: ct.email || '', phone: ct.phone || '' });
      setEditingContactIdx(idx);
      setContactDialogOpen(true);
    }
  };

  const handleDeleteContact = async (idx) => {
    try {
      const updatedContacts = (company.contacts || []).filter((_, i) => i !== idx);
      const { _id, ...safeCompany } = company;
      await axios.put(`${API}/biotech-companies/${id}`, { ...safeCompany, contacts: updatedContacts }, { withCredentials: true });
      toast.success('Contact removed');
      fetchAll();
    } catch {
      toast.error('Failed to remove contact');
    }
  };

  const handleMoveToPharma = async () => {
    if (!window.confirm(`Move "${company.company_name}" to Pharma Companies? This will create a pharma profile and remove it from Biotech.`)) return;
    try {
      // Create pharma company profile
      await axios.post(`${API}/company-profiles`, {
        name: company.company_name,
        website: company.website || '',
        location: company.location || '',
        description: company.description || company.company_description || '',
        therapeutic_areas: company.therapeutic_area || '',
        contacts: (company.contacts || []).map(c => ({
          name: c.name || '', email: c.email || '', designation: c.designation || '', phone: c.phone || ''
        })),
        notes: `Migrated from Biotech Companies`,
      }, { withCredentials: true });
      // Delete from biotech
      await axios.delete(`${API}/biotech-companies/${id}`, { withCredentials: true });
      toast.success(`Moved "${company.company_name}" to Pharma Companies`);
      navigate('/pharma');
    } catch (err) {
      toast.error('Failed to move: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Company not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/biotech')}>Back to list</Button>
      </div>
    );
  }

  const contacts = (company.contacts || []).filter(c => c.name || c.email);
  // Legacy single-slot address (`contact_email`) that several send paths fall
  // back to when `contacts[]` is empty. Surface it so it is never a hidden recipient.
  const legacyEmail = (company.contact_email || '').trim();
  const legacyHidden = !!legacyEmail && !contacts.some(c => (c.email || '').trim().toLowerCase() === legacyEmail.toLowerCase());
  const removeLegacyEmail = async () => {
    if (!window.confirm(`Remove ${legacyEmail} as a company contact? It will be suppressed so no outreach can go to it again.`)) return;
    try {
      const { _id, ...safeCompany } = company;
      await axios.put(`${API}/biotech-companies/${id}`, { ...safeCompany, contact_email: '', contact_name: '' }, { withCredentials: true });
      toast.success('Legacy contact removed and suppressed');
      fetchAll();
    } catch { toast.error('Failed to remove contact'); }
  };
  const molecules = company.molecules || [];
  const targetCompanies = company.target_companies || [];

  // Pending Outreach predicate, mirrors the BiotechCompaniesPage rule:
  //   - ≥1 visible asset still at 'Not Started' (or empty emailing_status)
  //   - ≥1 contact email on file
  //   - not DNC
  // When this fires, render a clickable amber badge in the header that
  // deep-links straight into AI Mapping for this biotech so the BD team
  // can kick off the campaign in one click.
  // Pending-Outreach badge — scoped to My Companies only. We don't want to
  // surface this on every prospect; it's an active-portfolio cue.
  const isPendingOutreach = (
    !!company?.is_my_company
    && !company?.do_not_contact?.flagged
    && (company.contacts || []).some(c => (c.email || '').trim())
    && (assets || []).some(a => !a.is_hidden && (!a.emailing_status || a.emailing_status === 'Not Started'))
  );

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="company-detail-page">
      {/* Feb 2026 · inline progress overlay during background PDF/deck
          enrichment. Shows "3/8 · deck.pdf" so the user can watch Gemini
          grind through their uploads in real time instead of staring at
          the toast queue. Dismisses 8s after completion. */}
      {enrichJob && enrichJob.status !== 'idle' && (
        <div
          className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/70 p-3 flex items-start gap-3"
          data-testid="enrich-progress-bar"
        >
          <Loader2 className={`w-4 h-4 mt-0.5 text-indigo-600 ${enrichJob.status === 'running' ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-sm font-semibold text-indigo-900">
                {enrichJob.status === 'done' ? 'Enrichment complete' : 'Enriching from documents…'}
              </div>
              <div className="text-xs text-indigo-800 font-mono whitespace-nowrap">
                {enrichJob.completed || 0} / {enrichJob.total || 0}
              </div>
            </div>
            <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden mt-1.5">
              <div
                className={`h-full transition-all duration-500 ${enrichJob.status === 'done' ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                style={{ width: `${enrichJob.total ? Math.round((enrichJob.completed || 0) * 100 / enrichJob.total) : 0}%` }}
              />
            </div>
            <div className="text-[11px] text-indigo-700/80 mt-1 truncate" title={enrichJob.current_document || ''}>
              {enrichJob.current_document
                ? `Processing: ${enrichJob.current_document}`
                : (enrichJob.status === 'done'
                    ? `Updated ${(enrichJob.fields_updated || []).length} field(s), added ${enrichJob.assets_created || 0} asset(s)`
                    : 'Waiting for background worker…')}
              {(enrichJob.errors || []).length > 0 && (
                <span className="ml-2 text-rose-700">· {enrichJob.errors.length} error(s)</span>
              )}
            </div>
          </div>
          {enrichJob.status === 'done' && (
            <button
              onClick={() => setEnrichJob(null)}
              className="text-indigo-500 hover:text-indigo-800 text-xs"
              data-testid="enrich-progress-dismiss"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/biotech')} className="text-slate-500 hover:text-slate-700" data-testid="back-to-list">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Feb 2026 (v7) — header responsive fix.
       * Previously: on narrower screens (< 1280px) the h1 wrapped to 3
       * lines while the right-side button rail overflowed off-screen
       * (Outreach & AI Mapping buttons got clipped). Now:
       *   • Header flex-wraps at ALL widths so buttons drop below the
       *     name row when there isn't enough horizontal room.
       *   • Left column is `min-w-0 flex-1` so the h1 can truncate/wrap
       *     naturally without pushing the right side off-screen.
       *   • h1 uses responsive font-size (2xl on mobile → 3xl on lg+)
       *     and `break-words` so very long names (e.g. "Novamab
       *     Biopharmaceuticals Co., Ltd.") don't overflow.
       *   • Right rail is now `justify-end w-full sm:w-auto` so on
       *     stacked layouts it right-aligns as one clean row rather
       *     than sprawling. */}
      <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-3 xl:gap-6 mb-8">
        <div className="min-w-0 flex-1 max-w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl xl:text-3xl font-bold tracking-tight text-slate-900 break-normal min-w-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {company.company_name}
            </h1>
            <button onClick={toggleMyCompany} className="p-1 rounded-md hover:bg-amber-50 transition-colors" title={company.is_my_company ? 'Remove from My Companies' : 'Add to My Companies'} data-testid="toggle-my-company-btn">
              <Star className={`w-5 h-5 transition-colors ${company.is_my_company ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-300'}`} />
            </button>
            <button
              onClick={openDncDialog}
              className={`p-1 rounded-md transition-colors ${isDnc ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-red-50'}`}
              title={isDnc ? 'Do Not Contact — click to edit or remove' : 'Mark as Do Not Contact'}
              data-testid="toggle-dnc-btn"
            >
              <Ban className={`w-5 h-5 transition-colors ${isDnc ? 'text-red-600' : 'text-slate-300 hover:text-red-400'}`} />
            </button>
            <button
              onClick={openNextOutreachDialog}
              className={`p-1 rounded-md transition-colors ${isNextOutreach ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-emerald-50'}`}
              title={isNextOutreach ? `Next Outreach — ${nextOutreach.note || 'click to edit / remove'}` : 'Add to Next Outreach queue'}
              data-testid="toggle-next-outreach-btn"
            >
              <Flag className={`w-5 h-5 transition-colors ${isNextOutreach ? 'fill-emerald-500 text-emerald-600' : 'text-slate-300 hover:text-emerald-500'}`} />
            </button>
            <button
              onClick={openSnoozeDialog}
              className={`p-1 rounded-md transition-colors ${isSnoozed ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-blue-50'}`}
              title={isSnoozed ? `Snoozed until ${snoozeUntilDisplay} — click to edit or remove` : 'Snooze outreach (e.g. for 1 year)'}
              data-testid="toggle-snooze-btn"
            >
              <Clock className={`w-5 h-5 transition-colors ${isSnoozed ? 'text-blue-600' : 'text-slate-300 hover:text-blue-400'}`} />
            </button>
            <button
              onClick={cycleBuyerEligible}
              className={`p-1 rounded-md transition-colors ${effectiveEligible ? 'bg-emerald-50 hover:bg-emerald-100' : manuallyExcluded ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-emerald-50'}`}
              title={
                manuallyExcluded ? 'Excluded as an AI-mapping in-licensing target (click to reset to auto)'
                : effectiveEligible ? (company?.is_buyer_eligible === true
                    ? 'In-licenser (manually flagged): appears as an AI-mapping target (click to exclude)'
                    : 'In-licenser (auto from CRM: in-licensing or commercial-stage): appears as an AI-mapping target (click to exclude)')
                : 'Not a buyer by default. Click to mark this biotech as an in-licenser/buyer for other companies\' assets'
              }
              data-testid="toggle-buyer-eligible-btn"
            >
              <Handshake className={`w-5 h-5 transition-colors ${effectiveEligible ? 'text-emerald-600' : manuallyExcluded ? 'text-rose-500' : 'text-slate-300 hover:text-emerald-500'}`} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1 rounded-md hover:bg-violet-50 transition-colors"
                  title="Reclassify this Biotech as a Pharma or Distributor (advanced — assets/mappings will be lost)"
                  data-testid="reclassify-biotech-btn"
                >
                  <ArrowRightLeft className="w-5 h-5 text-slate-300 hover:text-violet-500 transition-colors" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="text-xs">
                <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Reclassify as…</div>
                {[
                  { val: 'pharma', label: 'Pharma', testid: 'reclassify-to-pharma' },
                  { val: 'distributor', label: 'Distributor', testid: 'reclassify-to-distributor' },
                ].map(opt => (
                  <DropdownMenuItem
                    key={opt.val}
                    className="text-xs cursor-pointer"
                    data-testid={opt.testid}
                    onSelect={async () => {
                      const targetCategory = opt.val === 'distributor' ? 'regional_distributor' : 'pharma';
                      if (!window.confirm(
                        `⚠️ Reclassify "${company.company_name}" as a ${opt.label}?\n\n` +
                        `This will:\n` +
                        `  • Create a new ${opt.label} profile (name, country, website, description preserved)\n` +
                        `  • DELETE this biotech record\n` +
                        `  • Assets, AI mappings, and outreach history will NO LONGER be accessible\n\n` +
                        `Continue?`
                      )) return;
                      try {
                        const res = await axios.post(`${API}/biotech-companies/${id}/move-to-pharma`,
                          { category: targetCategory },
                          { withCredentials: true });
                        toast.success(`Reclassified as ${opt.label}`);
                        const path = targetCategory === 'regional_distributor' ? '/regional-distributors' : `/company-profile/${res.data.new_id}`;
                        window.location.href = path;
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Reclassify failed');
                      }
                    }}
                  >
                    {opt.val === 'pharma' ? (
                      <Building2 className="w-3 h-3 mr-2 text-sky-600" />
                    ) : opt.val === 'distributor' ? (
                      <Briefcase className="w-3 h-3 mr-2 text-amber-600" />
                    ) : (
                      <Briefcase className="w-3 h-3 mr-2 text-emerald-600" />
                    )}
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {isDnc && (
            <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 max-w-2xl" data-testid="dnc-banner">
              <Ban className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">Do Not Contact</div>
                <div className="text-sm text-red-700 mt-0.5">{dnc.reason || 'Flagged — no reason recorded.'}</div>
                {dnc.flagged_at && (
                  <div className="text-[10px] text-red-500/80 mt-0.5">Flagged {new Date(dnc.flagged_at).toLocaleDateString()}</div>
                )}
              </div>
              <button
                onClick={openDncDialog}
                className="text-[10px] text-red-600 hover:underline shrink-0"
                data-testid="dnc-banner-edit"
              >Edit</button>
            </div>
          )}
          {isSnoozed && (
            <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md" data-testid="snooze-banner">
              <Clock className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Snoozed until {snoozeUntilDisplay}</div>
                <div className="text-sm text-blue-700 mt-0.5">{snoozeRaw.reason || 'Outreach paused — no reason recorded.'}</div>
                <div className="text-[10px] text-blue-500/80 mt-0.5">
                  All new outreach (campaigns, bulk, AI) blocked until this date. Replies to existing threads are unaffected.
                </div>
              </div>
              <button
                onClick={openSnoozeDialog}
                className="text-[10px] text-blue-600 hover:underline shrink-0"
                data-testid="snooze-banner-edit"
              >Edit</button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {company.licensing_category && company.licensing_category !== 'unknown' && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                  company.licensing_category === 'both' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : company.licensing_category === 'in' ? 'bg-sky-50 text-sky-700 border border-sky-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}
                title="Licensing direction (from CRM data). In/Both = eligible as an AI-mapping in-licensing target by default."
                data-testid="licensing-category-chip"
              >
                <Handshake className="w-3 h-3" />
                {company.licensing_category === 'both' ? 'In + Out-Licenser' : company.licensing_category === 'in' ? 'In-Licenser' : 'Out-Licenser'}
              </span>
            )}
            {company.location && (
              <span className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="w-3.5 h-3.5" />{company.location}</span>
            )}
            {company.website && (
              <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-sky-600 hover:underline">
                <Globe className="w-3.5 h-3.5" />{company.website}
              </a>
            )}
            {company.company_status && company.company_status !== 'Not Started' && (
              <Badge className={`text-xs ${COMPANY_STATUS_COLORS[company.company_status] || 'bg-slate-100 text-slate-600'}`}>{company.company_status}</Badge>
            )}
            {isPendingOutreach && (
              <a
                href={`/ai-mapping/${id}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wide text-amber-900 bg-amber-100 border border-amber-300 hover:bg-amber-200 hover:border-amber-400 transition-colors"
                title="Pending Outreach — has freehand campaign rights but no emails sent yet. Click to start mapping in AI Mapping."
                data-testid="pending-outreach-link"
              >
                <span className="text-amber-600">⏳</span>
                Pending Outreach
                <ArrowUpRight className="w-3 h-3 opacity-70" />
              </a>
            )}
            {company.india_licensing && (
              <span className="text-lg" data-testid="india-licensing-badge" title="India Licensing">🇮🇳</span>
            )}
          </div>
          {company.therapeutic_area && (
            <TaChips value={company.therapeutic_area} />
          )}
        </div>
        <div className="flex gap-2 shrink-0 items-center flex-wrap justify-end w-full xl:w-auto xl:max-w-full">
          <Button
            onClick={openMasterEdit}
            variant="outline"
            size="icon"
            className="h-8 w-8 text-sky-500 border-sky-200 hover:bg-sky-50 hover:text-sky-600"
            title="Edit company"
            data-testid="master-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button
            onClick={() => setEnrichTextOpen(true)}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
            title="Paste email body / notes — AI will extract assets, contacts, stage, indications and merge into the profile"
            data-testid="enrich-from-text-btn"
          >
            <ClipboardPaste className="w-3.5 h-3.5 mr-1" />
            Enrich from text
          </Button>
          <Button
            onClick={handleEnrichFromDocs}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            title="Process all PDFs uploaded to this company's Documents section. Upload one first if none exist."
            disabled={enrichingDeck}
            data-testid="enrich-from-docs-btn"
          >
            {enrichingDeck ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {enrichingDeck ? 'Enriching…' : 'Enrich from documents'}
          </Button>
          <Button
            onClick={handleShareStatus}
            variant="outline"
            size="icon"
            className="h-8 w-8 text-violet-500 border-violet-200 hover:bg-violet-50 hover:text-violet-600"
            title="Share — generate / copy public status link"
            data-testid="share-company-btn"
          >
            <Share2 className="w-3.5 h-3.5" />
          </Button>
          {/* One-click deactivate: invalidates the existing public share
              link so anyone holding it (including the biotech themselves)
              gets a 404. Also flags the biotech as Do Not Contact —
              deactivating the share link and pausing outreach are paired
              actions per ground rule (user 2026-02). Distinct broken-link
              icon to differentiate from the Do Not Contact prohibition
              sign next to the company name.
              A new link can be regenerated any time by un-flagging
              Do Not Contact and clicking the share icon again. */}
          <Button
            onClick={handleDeactivateShare}
            variant="outline"
            size="icon"
            className="h-8 w-8 text-rose-500 border-rose-200 hover:bg-rose-50 hover:text-rose-600"
            title="Deactivate public share link (also marks biotech as Do Not Contact)"
            data-testid="deactivate-share-btn"
          >
            <Link2Off className="w-3.5 h-3.5" />
          </Button>
          <Button
            onClick={() => { setIntroAsset(null); setIntroOpen(true); setIntroStep(1); setIntroPharma(''); setIntroSearch(''); setIntroSearchResults([]); setIntroSearchError(''); setIntroSearchAttempted(false); setIntroContacts([]); setIntroSelected({}); setIntroSubject(''); setIntroBody(''); }}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs text-[#00709B] border-[#00709B]/30 hover:bg-[#00709B]/5"
            title="Send Introduction Email"
            data-testid="send-intro-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Introduce
          </Button>
          {outreach.length > 0 && (
            <Button
              onClick={openOutreachUpdate}
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
              title="Send Outreach Status Update"
              data-testid="outreach-update-btn"
            >
              <BarChart3 className="w-3.5 h-3.5 mr-1" /> Outreach Update
            </Button>
          )}
          <Button
            onClick={() => { setMergeDialogOpen(true); setMergeQuery(''); setMergeResults([]); setMergePicked(null); }}
            variant="outline"
            size="icon"
            className="h-8 w-8 text-violet-600 border-violet-200 hover:bg-violet-50 hover:text-violet-700"
            title="Merge another biotech record into this one"
            data-testid="merge-into-this-btn"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </Button>
          <Button
            onClick={deleteCompany}
            variant="outline"
            size="icon"
            className="h-8 w-8 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
            title="Delete"
            data-testid="delete-company-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Company Info Bar — Description, Contacts, Documents */}
      <div className="bg-white border border-slate-200 rounded-xl mb-4 shadow-sm overflow-hidden" data-testid="company-info-bar">
        {/* Description */}
        {(company.company_description || company.description) && (
          <div className="px-6 py-3 border-b border-slate-100 flex items-start gap-2" data-testid="detail-company-description">
            <p className="text-sm text-slate-600 leading-relaxed flex-1 min-w-0">{company.company_description || company.description}</p>
            <button
              onClick={async () => {
                const ok = window.confirm(
                  `Rewrite ${company.company_name}'s description, objectives, notes, differentiators, recent milestones and use of funds into a polished non-confidential company profile via AI?\n\n` +
                  `Structured fields (name, country, stage, modality, TA, funding) are NOT touched. This just cleans up duplicated phrases and stitched fragments left over from merges.`
                );
                if (!ok) return;
                try {
                  toast.info('Rewriting company profile via AI…');
                  await axios.post(`${API}/biotech-companies/${company.id}/regenerate-clean-profile`, {}, { withCredentials: true });
                  toast.success('Company profile refreshed');
                  if (typeof fetchAll === 'function') { await fetchAll(); }
                  else { window.location.reload(); }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to regenerate profile');
                }
              }}
              className="p-1.5 rounded-md hover:bg-fuchsia-100 transition-colors shrink-0"
              title="Regenerate clean company profile — rewrites description / objectives / notes / differentiators / milestones / use-of-funds into a polished narrative via Claude Sonnet 4.5. Structured fields (name, stage, modality, TA, funding) untouched."
              data-testid={`regenerate-clean-biotech-profile-${company.id}`}
            >
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" />
            </button>
          </div>
        )}
        {/* Contacts & Documents row */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* Contacts */}
          <div className="px-5 py-3" data-testid="detail-contacts">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contacts</span>
              </div>
              <button
                onClick={() => { setContactForm({ ...emptyContact }); setContactDialogOpen(true); }}
                className="flex items-center gap-1 text-[10px] font-medium text-sky-600 hover:text-sky-800 transition-colors"
                data-testid="add-contact-btn"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {contacts.length > 0 ? (
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {contacts.map((ct, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 group rounded-md px-1 py-0.5"
                    data-contact-id={ct.id || ''}
                    data-contact-email={(ct.email || '').toLowerCase()}
                  >
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-slate-500">{(ct.name || ct.email || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800 leading-tight">{ct.name || ct.email}</p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        {ct.designation && <span>{ct.designation}</span>}
                        {ct.email && <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{ct.email}<CopyEmail email={ct.email} /></span>}
                        {ct.phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{ct.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEditContact(i)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-sky-600" title="Edit contact" data-testid={`edit-contact-${i}`}>
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDeleteContact(i)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-500" title="Remove contact" data-testid={`delete-contact-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : !legacyHidden ? <p className="text-[10px] text-slate-400">No contacts added</p> : null}
            {legacyHidden && (
              <div className="mt-2 flex items-center gap-2 group rounded-md border border-amber-200 bg-amber-50 px-2 py-1" data-testid="legacy-contact-row" data-contact-email={legacyEmail.toLowerCase()}>
                <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Mail className="w-3 h-3 text-amber-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-amber-900 leading-tight">{company.contact_name || 'Company contact'} <span className="font-normal text-amber-700">· legacy address, used as fallback recipient</span></p>
                  <div className="flex items-center gap-2 text-[10px] text-amber-800">
                    <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{legacyEmail}<CopyEmail email={legacyEmail} /></span>
                  </div>
                </div>
                <button onClick={removeLegacyEmail} className="p-1 rounded hover:bg-amber-100 text-amber-600 hover:text-red-600" title="Remove & suppress this address" data-testid="delete-legacy-contact">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="px-5 py-3" data-testid="detail-documents">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Documents</span>
            </div>
            {(() => {
              // Data Room files live in their own CDA-gated section below — never mixed into company Documents.
              const dataRoomCount = docs.filter(d => d.doc_type === 'Data Room').length;
              // Documents = non-confidential materials only. Legal / deal papers
              // (CDAs, agreements, term sheets) live in their own sections.
              const isLegalDoc = (d) => /(agreement|term\s*sheet|\bcda\b)/i.test(d.doc_type || '');
              const termSheetCount = docs.filter(d => /term\s*sheet/i.test(d.doc_type || '')).length;
              const visibleDocs = docs.filter(d => !isLegalDoc(d) && d.doc_type !== 'Data Room');
              // Color-code the doc_type pill so the user can read the tile at a glance.
              const pillColor = (type) => {
                if (type === 'Non-Confidential Deck') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
                if (type === 'Corporate Overview') return 'bg-sky-100 text-sky-700 border-sky-200';
                if (type === 'Molecule-Specific Information') return 'bg-violet-100 text-violet-700 border-violet-200';
                if (type === 'Scientific Poster') return 'bg-amber-100 text-amber-700 border-amber-200';
                if (type === 'Press Release') return 'bg-rose-100 text-rose-700 border-rose-200';
                if (type === 'Analyst Report') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
                if (type === 'Clinical Data') return 'bg-teal-100 text-teal-700 border-teal-200';
                if (type === 'Publication') return 'bg-cyan-100 text-cyan-700 border-cyan-200';
                if (type === 'Patent') return 'bg-orange-100 text-orange-700 border-orange-200';
                return 'bg-slate-100 text-slate-600 border-slate-200';
              };
              return (<>
              {termSheetCount > 0 && (
                <button type="button" onClick={() => document.querySelector('[data-testid="detail-counterparty-engagement"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="mb-1 mr-3 inline-flex items-center gap-1 text-[10px] text-teal-700 hover:underline" data-testid="documents-term-sheet-pointer">
                  <FileText className="w-3 h-3" /> {termSheetCount} term sheet{termSheetCount !== 1 ? 's' : ''} under Counterparty Engagement below
                </button>
              )}
              {dataRoomCount > 0 && (
                <button type="button" onClick={() => document.querySelector('[data-testid="detail-data-room"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="mb-2 inline-flex items-center gap-1 text-[10px] text-sky-700 hover:underline" data-testid="documents-data-room-pointer">
                  <Lock className="w-3 h-3" /> {dataRoomCount} confidential file{dataRoomCount !== 1 ? 's' : ''} in the Data Room section below
                </button>
              )}
              {visibleDocs.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {visibleDocs.map((doc) => (
                  <div key={doc.id} className="inline-flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors group border border-slate-100">
                    <button type="button" onClick={() => downloadDoc(doc.id, doc.original_filename)}
                      className="inline-flex items-center gap-2 min-w-0 text-left" title="Download" data-testid={`download-doc-${doc.id}`}>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide shrink-0 ${pillColor(doc.doc_type)}`}
                        title={doc.doc_type || 'Document'}
                        data-testid={`doc-type-pill-${doc.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        {doc.doc_type || 'Document'}
                      </span>
                      <p className="text-[10px] font-medium text-slate-800 truncate max-w-[160px]">{doc.original_filename}</p>
                      <Download className="w-3 h-3 text-slate-300 group-hover:text-sky-500 shrink-0" />
                    </button>
                    <button onClick={() => openShareDoc(doc)} className="ml-1 text-[9px] text-sky-600 hover:text-sky-700 hover:bg-sky-100 rounded px-1.5 py-1 transition-colors cursor-pointer"
                      data-testid={`share-doc-${doc.id}`}>
                      <Mail className="w-3 h-3 inline mr-0.5" />Share
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('Delete this document?')) return;
                      try {
                        await axios.delete(`${API}/documents/${doc.id}`, { withCredentials: true });
                        toast.success('Document removed');
                        fetchAll();
                      } catch { toast.error('Failed to delete document'); }
                    }} className="text-[9px] text-slate-400 hover:text-red-500 hover:bg-red-50 rounded px-1 py-1 transition-colors cursor-pointer"
                      data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : <p className="text-[10px] text-slate-400 mb-3">No documents uploaded</p>}
              </>);
            })()}
            {/* Inline upload — same doc_type structure as Edit Master, so the
                user can drop in an NCD / Corporate Overview / etc. without
                opening the edit dialog. */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="inline-doc-upload-row">
              <Select value={inlineDocType} onValueChange={setInlineDocType}>
                <SelectTrigger className="h-8 text-xs w-[210px]" data-testid="inline-doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {inlineDocType === 'Other' && (
                <Input
                  value={inlineDocCustomLabel}
                  onChange={(e) => setInlineDocCustomLabel(e.target.value)}
                  placeholder="Custom label (e.g., Scientific Poster)"
                  className="h-8 text-xs w-[200px]"
                  data-testid="inline-doc-custom-label"
                />
              )}
              <input type="file" ref={inlineDocFileRef} accept=".pdf,.pptx,.ppt,.doc,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg" className="hidden"
                onChange={handleInlineDocUpload} data-testid="inline-doc-file-input" />
              <Button variant="outline" size="sm" className="h-8 text-xs" disabled={inlineDocUploading}
                onClick={() => inlineDocFileRef.current?.click()} data-testid="inline-doc-upload-btn">
                {inlineDocUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Upload Document
              </Button>
            </div>
          </div>

          {/* CDA Status & Upload */}
          <div className="px-5 py-3 border-t border-slate-100" data-testid="detail-cda">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CDA</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={company?.cda_status || 'Not Started'} onValueChange={handleCdaStatusChange}>
                <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="cda-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Not Started', 'Shared with Me', 'Shared with Them', 'Finalised', 'Signed', 'Did Not Insist'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="file" ref={cdaInputRef} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                onChange={handleCdaUpload} data-testid="cda-file-input" />
              <Button variant="outline" size="sm" className="h-8 text-xs" disabled={cdaUploading}
                onClick={() => cdaInputRef.current?.click()} data-testid="cda-upload-btn">
                {cdaUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Upload Signed CDA
              </Button>
              {legalDocs('cda').map(d => (
                <button key={d.id} type="button" onClick={() => downloadDoc(d.id, d.original_filename)}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline max-w-[260px] truncate"
                  title={`${d.original_filename || 'CDA'} · uploaded ${(d.created_at || '').slice(0, 10)}`} data-testid="cda-download-link">
                  <Download className="w-3 h-3 shrink-0" /> {legalDocs('cda').length > 1 ? (d.original_filename || 'View CDA') : 'View CDA'}
                </button>
              ))}
            </div>
          </div>

          {/* Partnership Agreement Status & Upload */}
          <div className="px-5 py-3 border-t border-slate-100" data-testid="detail-partnership">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-3.5 h-3.5 text-purple-500" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Partnership Agreement</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={company?.partnership_status || 'Not Started'} onValueChange={handlePartnershipStatusChange}>
                <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="partnership-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Not Started', 'Sent', 'Under Review', 'Finalised', 'Signed'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="file" ref={partnershipInputRef} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                onChange={handlePartnershipUpload} data-testid="partnership-file-input" />
              <Button variant="outline" size="sm" className="h-8 text-xs" disabled={partnershipUploading}
                onClick={() => partnershipInputRef.current?.click()} data-testid="partnership-upload-btn">
                {partnershipUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Upload Agreement
              </Button>
              {legalDocs('partnership').map(d => (
                <button key={d.id} type="button" onClick={() => downloadDoc(d.id, d.original_filename)}
                  className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline max-w-[260px] truncate"
                  title={`${d.original_filename || 'Agreement'} · uploaded ${(d.created_at || '').slice(0, 10)}`} data-testid="partnership-download-link">
                  <Download className="w-3 h-3 shrink-0" /> {legalDocs('partnership').length > 1 ? (d.original_filename || 'View Agreement') : 'View Agreement'}
                </button>
              ))}
            </div>
          </div>

          {/* Counterparty Engagement (CDA + Term Sheet)
              ─────────────────────────────────────────────────────────────
              Unified per-counterparty card. Each pharma engagement is one
              block showing both the CDA state and the Term Sheet state
              side-by-side so the BD lead can see the full negotiation
              status at a glance instead of scanning two separate sections.

              Backing data is unchanged: CDAs stored as documents with
              doc_type='Buyer CDA', term sheets with doc_type='Term Sheet'.
              Grouping is done client-side by counterparty_name (case-
              insensitive trim). A counterparty may have just a CDA, just
              a TS, or both. */}
          <div className="px-5 py-3 border-t border-slate-100 md:col-span-2" data-testid="detail-counterparty-engagement">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Counterparty Engagement</span>
              <span className="text-[9px] text-slate-400 font-normal">CDAs · Term Sheets</span>
              {(() => {
                const cps = new Set();
                for (const d of (docs || [])) {
                  if ((d.doc_type === 'Buyer CDA' || d.doc_type === 'Term Sheet') && d.counterparty_name) {
                    cps.add(d.counterparty_name.trim().toLowerCase());
                  }
                }
                return cps.size > 0 ? <Badge variant="secondary" className="text-[9px] h-4 px-1.5">{cps.size}</Badge> : null;
              })()}
              <span className="text-[9px] text-slate-400 italic ml-auto">multiple term sheets per counterparty supported · upload inline</span>
            </div>
            {(() => {
              // Group Buyer CDA + ALL Term Sheets per counterparty.
              // CDA: single (most recent). Term Sheets: all versions, newest-first.
              const grouped = new Map();
              for (const d of (docs || [])) {
                if (d.doc_type !== 'Buyer CDA' && d.doc_type !== 'Term Sheet') continue;
                const key = (d.counterparty_name || 'Counterparty').trim();
                const norm = key.toLowerCase();
                if (!grouped.has(norm)) grouped.set(norm, { display: key, cda: null, termSheets: [] });
                const entry = grouped.get(norm);
                if (d.doc_type === 'Buyer CDA') {
                  if (!entry.cda || (d.created_at || '') > (entry.cda.created_at || '')) entry.cda = d;
                } else {
                  entry.termSheets.push(d);
                }
              }
              // Sort term sheets newest-first inside each group
              for (const v of grouped.values()) {
                v.termSheets.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
              }
              const rows = Array.from(grouped.values());
              if (rows.length === 0) {
                return <p className="text-[10px] text-slate-400 mb-3">No counterparty engagements yet — add one below</p>;
              }
              return (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-200 mb-3 overflow-hidden bg-white" data-testid="counterparty-engagement-list">
                  {rows.map(({ display, cda, termSheets }) => {
                    const cpKey = display.toLowerCase();
                    return (
                      <div key={cpKey} className="px-3 py-2.5 hover:bg-slate-50/60 transition-colors" data-testid={`engagement-${cpKey}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs font-semibold text-slate-800 truncate" title={display}>{display}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:divide-x divide-slate-100">
                          {/* CDA sub-column — single CDA per counterparty (most recent shown) */}
                          <div className="space-y-1.5 py-1 min-w-0" data-testid={`engagement-cda-${cpKey}`}>
                            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">CDA</span>
                            {cda ? (() => {
                              const hasFile = !!cda.storage_path;
                              const curStatus = normCdaStatus(cda.status || (hasFile ? 'Finalised' : 'Under Discussion'));
                              return (
                                <div className="group flex items-center gap-2 flex-wrap">
                                  <Select value={curStatus} onValueChange={(v) => handleBuyerCdaStatusChange(cda.id, v)}>
                                    <SelectTrigger className={`h-6 text-[10px] px-2 w-[140px] border ${buyerCdaStatusPill(curStatus)}`} data-testid={`buyer-cda-status-${cda.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {BUYER_CDA_STATUS_OPTIONS.map(s => (
                                        <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {hasFile ? (
                                    <button type="button" onClick={() => downloadDoc(cda.id, cda.original_filename)}
                                      className="inline-flex items-center gap-1 min-w-0 text-[11px] font-medium text-slate-700 hover:text-emerald-700" data-testid={`download-buyer-cda-${cda.id}`}>
                                      <span className="truncate max-w-[160px]">{cda.original_filename}</span>
                                      <Download className="w-3 h-3 text-slate-400 shrink-0" />
                                    </button>
                                  ) : (
                                    <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-slate-500 hover:text-emerald-700 italic">
                                      <Upload className="w-2.5 h-2.5" /> Attach file
                                      <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                                        onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
                                          // Update existing CDA tracker with the file (re-upload as same counterparty)
                                          await handleCdaUploadInline(display, f);
                                          try { await axios.delete(`${API}/documents/${cda.id}`, { withCredentials: true }); } catch {}
                                          fetchAll();
                                        }}
                                      />
                                    </label>
                                  )}
                                  {cda.signed_date && (
                                    <span className="text-[10px] text-slate-400 shrink-0">{cda.signed_date}</span>
                                  )}
                                  <DataAccessContactsButton
                                    doc={cda}
                                    onAdd={(c) => handleAddDataAccessContact(cda.id, c)}
                                    onRemove={(cid) => handleRemoveDataAccessContact(cda.id, cid)}
                                  />
                                  <button onClick={async () => {
                                    if (!window.confirm(`Delete the ${display} CDA entry?`)) return;
                                    try {
                                      await axios.delete(`${API}/documents/${cda.id}`, { withCredentials: true });
                                      toast.success('CDA tracker removed');
                                      fetchAll();
                                    } catch { toast.error('Failed to delete'); }
                                  }} className="ml-auto text-slate-300 hover:text-red-500 hover:bg-red-50 rounded p-1 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                                    data-testid={`delete-buyer-cda-${cda.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })() : (
                              <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-emerald-600 hover:text-emerald-700 italic">
                                <Plus className="w-2.5 h-2.5" /> Upload CDA
                                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                                  onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; await handleCdaUploadInline(display, f); }}
                                />
                              </label>
                            )}
                          </div>
                          {/* Term Sheet sub-column — multiple versions stacked */}
                          <div className="space-y-1.5 py-1 md:pl-4 min-w-0" data-testid={`engagement-ts-${cpKey}`}>
                            <span className="text-[9px] font-semibold text-teal-500 uppercase tracking-wide">Term Sheet{termSheets.length > 1 ? `s (${termSheets.length})` : ''}</span>
                            {termSheets.map((ts) => {
                              const hasFile = !!ts.storage_path;
                              const curStatus = ts.status || (hasFile ? 'Finalised' : 'Draft Shared');
                              return (
                                <div key={ts.id} className="group flex items-center gap-2 flex-wrap" data-testid={`term-sheet-${ts.id}`}>
                                  <Select value={curStatus} onValueChange={(v) => handleTermSheetStatusChange(ts.id, v)}>
                                    <SelectTrigger className={`h-6 text-[10px] px-2 w-[140px] border ${termSheetStatusPill(curStatus)}`} data-testid={`term-sheet-status-${ts.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TERM_SHEET_STATUS_OPTIONS.map(s => (
                                        <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {hasFile ? (
                                    <button type="button" onClick={() => downloadDoc(ts.id, ts.original_filename)}
                                      className="inline-flex items-center gap-1 min-w-0 text-[11px] font-medium text-slate-700 hover:text-teal-700" data-testid={`download-term-sheet-${ts.id}`}>
                                      <span className="truncate max-w-[180px]">{ts.original_filename}</span>
                                      <Download className="w-3 h-3 text-slate-400 shrink-0" />
                                    </button>
                                  ) : (
                                    <span className="text-[10px] italic text-slate-400">No file — tracker only</span>
                                  )}
                                  {ts.signed_date && (
                                    <span className="text-[10px] text-slate-400 shrink-0">{ts.signed_date}</span>
                                  )}
                                  <button onClick={async () => {
                                    if (!window.confirm(`Delete this ${display} term sheet (${ts.original_filename || 'tracker'})?`)) return;
                                    try {
                                      await axios.delete(`${API}/documents/${ts.id}`, { withCredentials: true });
                                      toast.success('Term sheet removed');
                                      fetchAll();
                                    } catch { toast.error('Failed to delete'); }
                                  }} className="ml-auto text-slate-300 hover:text-red-500 hover:bg-red-50 rounded p-1 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                                    data-testid={`delete-term-sheet-${ts.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                            <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-teal-600 hover:text-teal-700 italic">
                              <Plus className="w-2.5 h-2.5" /> Upload {termSheets.length > 0 ? 'another' : ''} term sheet
                              <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg" className="hidden"
                                onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; await handleTermSheetUploadInline(display, f); }}
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Add a NEW counterparty (one form — counterparty + initial CDA tracker)
                Existing counterparties use the inline upload buttons within each card. */}
            <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5 mt-2">Add New Counterparty</div>
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="buyer-cda-upload-row">
              <div className="relative" data-testid="buyer-cda-counterparty-wrap">
                <Input
                  value={buyerCdaCounterparty}
                  onChange={(e) => { setBuyerCdaCounterparty(e.target.value); setBuyerCdaSuggestOpen(true); }}
                  onFocus={() => setBuyerCdaSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setBuyerCdaSuggestOpen(false), 150)}
                  placeholder="Counterparty (e.g., Pfizer)"
                  className="h-8 text-xs w-[180px]"
                  data-testid="buyer-cda-counterparty-input"
                  autoComplete="off"
                />
                {buyerCdaSuggestOpen && buyerCdaCounterparty.trim().length >= 1 && (() => {
                  const q = buyerCdaCounterparty.trim().toLowerCase();
                  const prefix = [];
                  const substr = [];
                  for (const n of pharmaNameSuggestions) {
                    const lower = n.toLowerCase();
                    if (lower.startsWith(q)) prefix.push(n);
                    else if (lower.includes(q)) substr.push(n);
                    if (prefix.length >= 8) break;
                  }
                  const matches = [...prefix, ...substr].slice(0, 8);
                  if (matches.length === 0) return null;
                  return (
                    <div className="absolute z-50 mt-1 w-[260px] max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid="buyer-cda-suggest-list">
                      {matches.map(n => (
                        <button
                          key={n}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); setBuyerCdaCounterparty(n); setBuyerCdaSuggestOpen(false); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 truncate"
                          data-testid={`buyer-cda-suggest-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <Select value={buyerCdaStatus} onValueChange={setBuyerCdaStatus}>
                <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="buyer-cda-status-select"><SelectValue placeholder="CDA status" /></SelectTrigger>
                <SelectContent>
                  {BUYER_CDA_STATUS_OPTIONS.map(s => (
                    <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={buyerCdaSignedDate}
                onChange={(e) => setBuyerCdaSignedDate(e.target.value)}
                className="h-8 text-xs w-[140px]"
                title="Signed date (optional)"
                data-testid="buyer-cda-date-input"
              />
              <input type="file" ref={buyerCdaInputRef} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
                onChange={handleBuyerCdaUpload} data-testid="buyer-cda-file-input" />
              <Button variant="outline" size="sm" className="h-8 text-xs" disabled={buyerCdaUploading}
                onClick={() => {
                  if (!buyerCdaCounterparty.trim()) { toast.error('Enter the counterparty name first'); return; }
                  buyerCdaInputRef.current?.click();
                }}
                data-testid="buyer-cda-upload-btn"
                title="Upload the signed/draft CDA PDF for this new counterparty">
                {buyerCdaUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Upload CDA
              </Button>
              <Button size="sm" className="h-8 text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={buyerCdaSavingTracker}
                onClick={handleBuyerCdaTrackerAdd}
                title="Add a tracker entry for this counterparty without a CDA file — useful for early-stage engagements. You can upload files inline on the card afterwards."
                data-testid="buyer-cda-tracker-btn">
                {buyerCdaSavingTracker ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Add counterparty (no file)
              </Button>
            </div>
          </div>

          {/* India Licensing Flag */}
          <div className="px-5 py-3 border-t border-slate-100" data-testid="detail-india-licensing">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!company?.india_licensing}
                onChange={async (e) => {
                  const val = e.target.checked;
                  try {
                    await axios.patch(`${API}/biotech-companies/${id}`, { india_licensing: val }, { withCredentials: true });
                    setCompany(prev => ({ ...prev, india_licensing: val }));
                    toast.success(val ? 'India Licensing enabled' : 'India Licensing disabled');
                  } catch { toast.error('Failed to update'); }
                }}
                className="w-3.5 h-3.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                data-testid="india-licensing-checkbox" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">India Licensing</span>
            </label>
          </div>

          {/* Data Room — confidential file repository, CDA-gated, shareable via tokenised link */}
          <div className="px-5 py-3 border-t border-slate-100 md:col-span-2" data-testid="detail-data-room">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Data Room</span>
              <span className="text-[9px] text-slate-400 font-normal">confidential · password-protected · CDA-gated</span>
            </div>
            <DataRoomPanel
              biotechId={id}
              biotechName={company?.company_name || ''}
              assets={assets}
            />
          </div>
        </div>
      </div>

      {/* Assets — full width horizontal scroll */}
      <Card className="border border-slate-200 shadow-sm mb-4" data-testid="detail-assets">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Assets / Molecules</h3>
              <Badge variant="secondary" className="text-[10px]">{assets.length} molecule{assets.length !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="flex items-center gap-2">
              {assets.length >= 1 && (
                <button
                  onClick={runBulkEnrich}
                  disabled={!!bulkEnrich?.running}
                  className="flex items-center gap-1 text-[10px] font-medium text-fuchsia-700 hover:text-fuchsia-900 bg-fuchsia-50 hover:bg-fuchsia-100 px-2 py-1 rounded transition-colors disabled:opacity-70 disabled:cursor-wait"
                  data-testid="enrich-all-assets-btn"
                  title="Reconcile the asset inventory against the uploaded documents, then enrich the confirmed assets"
                >
                  {bulkEnrich?.running && bulkEnrich?.reconciling
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Step 1/2 · {bulkEnrich.current}</>
                    : bulkEnrich?.running
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Enriching {bulkEnrich.done + 1 > bulkEnrich.total ? bulkEnrich.total : bulkEnrich.done + 1} / {bulkEnrich.total}{bulkEnrich.current ? ` · ${bulkEnrich.current}` : ''}</>
                    : <><Sparkles className="w-3 h-3" /> Enrich all</>}
                </button>
              )}
              {bulkEnrich?.running && (
                <button onClick={() => { bulkEnrichStopRef.current = true; }} className="text-[10px] text-slate-500 hover:text-slate-800 underline" data-testid="enrich-all-stop-btn">stop</button>
              )}
              {assets.length >= 2 && (
                <button
                  onClick={() => {
                    setAssetMergeSelected({});
                    setAssetMergePrimary('');
                    setMergeShowSingles(false);
                    setAssetMergeDialogOpen(true);
                  }}
                  className="flex items-center gap-1 text-[10px] font-medium text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors"
                  data-testid="merge-assets-btn"
                  title="Manually merge duplicate assets into one"
                >
                  <GitMerge className="w-3 h-3" />
                  Merge
                </button>
              )}
              <button
                onClick={() => setAssetRecoveryOpen(true)}
                className="flex items-center gap-1 text-[10px] font-medium text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded transition-colors"
                data-testid="recover-assets-btn"
                title="Restore assets removed by reconciliation or folded into another asset by a merge"
              >
                <RotateCcw className="w-3 h-3" />
                Recover
              </button>
              <button
                onClick={() => { setAssetForm({ ...emptyAssetForm }); setEditingAsset(null); setAssetDialogOpen(true); }}
                className="flex items-center gap-1 text-[10px] font-medium text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-2 py-1 rounded transition-colors"
                data-testid="add-asset-btn"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
          </div>
          {assets.length > 0 ? (
            <div className="relative">
              {(() => {
                const idx = Math.min(assetIndex, assets.length - 1);
                const a = assets[idx];
                return (
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-100" data-testid={`asset-card-${a.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-base font-bold text-slate-900">{a.asset_name}</p>
                          {assets.length > 1 && (
                            <span className="text-[10px] text-slate-400 font-medium">{idx + 1} of {assets.length}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <Badge variant="secondary" className={`text-[9px] ${STAGE_COLORS[a.stage] || 'bg-slate-100 text-slate-600'}`}>{a.stage}</Badge>
                          {a.stage_detail?.needs_review && (
                            <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-800" title={a.stage_detail.stage_note || 'Sources conflict on the current development stage'} data-testid={`stage-needs-review-${a.id}`}>Stage needs review</Badge>
                          )}
                          {a.stage_detail?.planned && !a.stage_detail?.needs_review && (
                            <span className="text-[10px] text-slate-500" data-testid={`stage-planned-${a.id}`}>next: {a.stage_detail.planned}</span>
                          )}
                          {Array.isArray(a.aliases) && a.aliases.length > 0 && (
                            <span className="text-[10px] text-slate-500" data-testid={`asset-aliases-${a.id}`}>also known as <b>{a.aliases.join(', ')}</b></span>
                          )}
                          {a.emailing_status && a.emailing_status !== 'Not Started' && (
                            <Badge variant="secondary" className={`text-[9px] ${a.emailing_status === 'Completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>Email: {a.emailing_status}</Badge>
                          )}
                          {a.deal_interests?.length > 0 && a.deal_interests.map(d => (
                            <Badge key={d} variant="secondary" className="text-[9px] bg-sky-50 text-sky-600">{d === 'Co-Development & R&D' ? 'Co-Dev' : d === 'Investment / Fundraising' ? 'Fundraising' : d}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2">
                        {a.is_hidden && (
                          <Badge variant="outline" className="text-[8px] px-1.5 py-0 border-slate-300 text-slate-400 self-center mr-1">Hidden</Badge>
                        )}
                        <button onClick={() => handleToggleHidden(a)}
                          className={`p-1.5 rounded-md transition-colors ${a.is_hidden ? 'hover:bg-emerald-100 text-slate-400' : 'hover:bg-slate-200 text-emerald-500'}`}
                          title={a.is_hidden ? 'Currently hidden — click to include in outreach' : 'Currently included — click to hide from outreach'}
                          data-testid={`toggle-hidden-${a.id}`}>
                          {a.is_hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => { setIntroAsset(a); setIntroStep(1); setIntroSearch(''); setIntroPharma(''); setIntroSearchResults([]); setIntroSearchError(''); setIntroSearchAttempted(false); setIntroContacts([]); setIntroSelected({}); setIntroSubject(''); setIntroBody(''); setIntroOpen(true); }}
                          className="p-1.5 rounded-md hover:bg-sky-100 transition-colors" title="Introduce this asset" data-testid={`introduce-asset-${a.id}`}>
                          <Send className="w-3.5 h-3.5 text-sky-500" />
                        </button>
                        <button onClick={() => { setAssetEnrichDialog({ asset: a }); setAssetEnrichText(''); }} className="p-1.5 rounded-md hover:bg-amber-100 transition-colors" title="Enrich this asset from pasted text (trial data, phase, indications)" data-testid={`enrich-asset-text-${a.id}`}><ClipboardPaste className="w-3.5 h-3.5 text-amber-500" /></button>
                        {/* Sep 2026 · per-asset ⭐ "regenerate from documents" removed — use "Enrich all" (reconcile → enrich) at the section header. */}
                        <button onClick={() => handleEditAsset(a)} className="p-1.5 rounded-md hover:bg-slate-200 transition-colors" data-testid={`edit-asset-${a.id}`}><Pencil className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={() => openMoveAsset(a)} className="p-1.5 rounded-md hover:bg-violet-100 transition-colors" title="Move to another company" data-testid={`move-asset-${a.id}`}><ArrowRightLeft className="w-3.5 h-3.5 text-violet-500" /></button>
                        <button onClick={() => setDeleteAssetTarget(a)} className="p-1.5 rounded-md hover:bg-red-100 transition-colors" data-testid={`delete-asset-${a.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                      {a.therapeutic_area && <div><span className="text-[10px] text-slate-400 uppercase tracking-wide">Therapeutic Area</span><div className="mt-0.5"><TaChips value={a.therapeutic_area} initial={4} /></div></div>}
                      {a.target && <div><span className="text-[10px] text-slate-400 uppercase tracking-wide">Target</span><p className="text-xs text-slate-700 font-medium">{a.target}</p></div>}
                      {a.modality && <div><span className="text-[10px] text-slate-400 uppercase tracking-wide">Modality</span><p className="text-xs text-slate-700 font-medium">{a.modality}</p></div>}
                      {a.indication && <div><span className="text-[10px] text-slate-400 uppercase tracking-wide">Indication</span><div className="mt-0.5"><TaChips value={a.indication} initial={4} /></div></div>}
                      {a.drug_delivery_mechanism && <div><span className="text-[10px] text-slate-400 uppercase tracking-wide">Drug Delivery</span><p className="text-xs text-slate-700 font-medium">{a.drug_delivery_mechanism}</p></div>}
                    </div>
                    {(a.molecule_description || a.objectives) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {a.molecule_description && (
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase tracking-wide">Description</span>
                            <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{a.molecule_description}</p>
                          </div>
                        )}
                        {a.objectives && (
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase tracking-wide">Objectives</span>
                            <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{a.objectives}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {a.partnering_status && (
                      <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`asset-partnering-status-${a.id}`}>
                        <span className="text-[10px] text-emerald-600 uppercase tracking-wide font-semibold inline-flex items-center gap-1"><Handshake className="w-3 h-3" /> Partnering Status</span>
                        <p className="text-xs text-emerald-800 leading-relaxed mt-0.5 bg-emerald-50/60 border border-emerald-100 rounded px-2 py-1.5">{a.partnering_status}</p>
                      </div>
                    )}
                    {/* Client-supplied "Already in touch with" pharmas for THIS asset —
                        suppresses them from AI mapping for THIS asset only; feeds
                        the global Known Active Licensees signal for other runs. */}
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`asset-client-excluded-section-${a.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Ban className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech is already in touch with</span>
                          <span className="text-[10px] text-slate-400">— for THIS asset; suppressed from AI mapping, feeds global KAL signal</span>
                        </div>
                        <button
                          onClick={() => {
                            const current = (a.client_excluded_pharmas || []).join(', ');
                            const input = window.prompt(
                              `Pharmas the biotech is ALREADY in touch with for "${a.asset_name}".\nComma-separated. Example: Solventum, ConvaTec, J&J, Sino Biopharm`,
                              current
                            );
                            if (input === null) return;
                            const pharmas = input.split(',').map(s => s.trim()).filter(Boolean);
                            axios.patch(`${API}/assets/${a.id}/client-excluded-pharmas`, { pharmas }, { withCredentials: true })
                              .then(res => {
                                setAssets(prev => prev.map(x => x.id === a.id ? { ...x, client_excluded_pharmas: res.data.client_excluded_pharmas } : x));
                                toast.success(`Saved ${res.data.count} pharma${res.data.count !== 1 ? 's' : ''} for ${a.asset_name}`);
                              })
                              .catch(e => toast.error(e.response?.data?.detail || 'Save failed'));
                          }}
                          className="text-[10px] font-medium text-sky-600 hover:text-sky-800 transition-colors flex items-center gap-1 shrink-0"
                          data-testid={`edit-asset-client-excluded-${a.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                          {(a.client_excluded_pharmas || []).length ? 'Edit' : 'Add'}
                        </button>
                      </div>
                      {(a.client_excluded_pharmas || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(a.client_excluded_pharmas || []).map((p, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[10px] text-orange-700 font-medium" data-testid={`asset-client-excluded-pharma-${a.id}-${i}`}>
                              {p}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-400 italic">None set.</p>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Navigation arrows */}
              {assets.length > 1 && (
                <>
                  <button
                    onClick={() => setAssetIndex(i => Math.max(0, i - 1))}
                    disabled={assetIndex === 0}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-slate-700 hover:shadow-lg transition-all disabled:opacity-30 disabled:cursor-default z-10"
                    data-testid="assets-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setAssetIndex(i => Math.min(assets.length - 1, i + 1))}
                    disabled={assetIndex >= assets.length - 1}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-slate-700 hover:shadow-lg transition-all disabled:opacity-30 disabled:cursor-default z-10"
                    data-testid="assets-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No molecules added yet</p>
              {(company.molecules?.length > 0 || company.therapeutic_area || company.molecule_description) && (
                <button
                  onClick={handleMigrateAssets}
                  disabled={migrating}
                  className="mt-2 text-[10px] font-medium text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors"
                  data-testid="migrate-assets-btn"
                >
                  {migrating ? 'Importing...' : 'Import from Profile'}
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fundraising Profile */}
      <div className="bg-white border border-slate-200 rounded-xl mb-4 shadow-sm overflow-hidden" data-testid="fundraising-profile">
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Package className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Fundraising Profile</span>
          </div>
          {!editingFundraising ? (
            <div className="flex items-center gap-2">
              <button onClick={async () => {
                setAutoPopulating(true);
                try {
                  const res = await axios.post(`${API}/ai/auto-fundraising/${id}`, {}, { withCredentials: true });
                  const f = res.data.fundraising || {};
                  setCompany(prev => ({ ...prev, funding_stage: f.funding_stage || prev.funding_stage, funding_amount: f.funding_amount || prev.funding_amount, use_of_funds: f.use_of_funds || prev.use_of_funds }));
                  setFundraisingForm(prev => ({ funding_stage: f.funding_stage || prev.funding_stage, funding_amount: f.funding_amount || prev.funding_amount, use_of_funds: f.use_of_funds || prev.use_of_funds }));
                  toast.success(`Auto-populated ${res.data.updated_fields?.length || 0} fields`);
                } catch (e) { toast.error(e.response?.data?.detail || 'Auto-populate failed'); }
                finally { setAutoPopulating(false); }
              }} disabled={autoPopulating} className="text-[10px] text-violet-500 hover:text-violet-700 flex items-center gap-1 disabled:opacity-50" data-testid="auto-fundraising-btn">
                {autoPopulating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {autoPopulating ? 'Analyzing...' : 'AI Auto-fill'}
              </button>
              <button onClick={() => setEditingFundraising(true)} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="edit-fundraising-btn">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={async () => {
                try {
                  await axios.put(`${API}/biotech-companies/${id}`, {
                    ...company,
                    funding_stage: fundraisingForm.funding_stage,
                    funding_amount: fundraisingForm.funding_amount,
                    use_of_funds: fundraisingForm.use_of_funds,
                  }, { withCredentials: true });
                  setCompany(prev => ({ ...prev, ...fundraisingForm }));
                  setEditingFundraising(false);
                  toast.success('Fundraising profile updated');
                } catch (e) { toast.error('Failed to update'); }
              }} className="text-[10px] text-emerald-600 hover:text-emerald-700 flex items-center gap-1 font-medium" data-testid="save-fundraising-btn">
                <Check className="w-3 h-3" /> Save
              </button>
              <button onClick={() => { setEditingFundraising(false); setFundraisingForm({ funding_stage: company.funding_stage || '', funding_amount: company.funding_amount || '', use_of_funds: company.use_of_funds || '' }); }} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1">
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100">
          <div className="px-5 py-3">
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Funding Stage</label>
            {editingFundraising ? (
              <Select value={fundraisingForm.funding_stage} onValueChange={v => setFundraisingForm(p => ({ ...p, funding_stage: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="funding-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>
                  {['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Bridge', 'IPO', 'Grant', 'Debt', 'Other'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-medium text-slate-700">{company.funding_stage || <span className="text-slate-300 italic">Not set</span>}</p>
            )}
          </div>
          <div className="px-5 py-3">
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Target Amount</label>
            {editingFundraising ? (
              <Input value={fundraisingForm.funding_amount} onChange={e => setFundraisingForm(p => ({ ...p, funding_amount: e.target.value }))} placeholder="e.g., $5M, $10-15M" className="h-8 text-xs" data-testid="funding-amount-input" />
            ) : (
              <p className="text-sm font-medium text-slate-700">{company.funding_amount || <span className="text-slate-300 italic">Not set</span>}</p>
            )}
          </div>
          <div className="px-5 py-3">
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Use of Funds</label>
            {editingFundraising ? (
              <Input value={fundraisingForm.use_of_funds} onChange={e => setFundraisingForm(p => ({ ...p, use_of_funds: e.target.value }))} placeholder="e.g., Phase 2 trials, manufacturing scale-up" className="h-8 text-xs" data-testid="use-of-funds-input" />
            ) : (
              <p className="text-sm font-medium text-slate-700">{company.use_of_funds || <span className="text-slate-300 italic">Not set</span>}</p>
            )}
          </div>
        </div>
      </div>

      {/* Master Edit Company Dialog */}
      <Dialog open={masterEditOpen} onOpenChange={setMasterEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Company</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Update company details, contacts, molecules, and upload documents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">

            {/* Company Information */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Company Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input value={masterForm.company_name || ''} onChange={e => setMasterForm(p => ({ ...p, company_name: e.target.value }))} data-testid="master-company-name" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <CountrySelect value={masterForm.location || ''} onChange={v => setMasterForm(p => ({ ...p, location: v }))} testId="master-location" />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input value={masterForm.website || ''} onChange={e => setMasterForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" data-testid="master-website" />
                </div>
                <div className="space-y-2">
                  <Label>Therapeutic Area</Label>
                  <Input value={masterForm.therapeutic_area || ''} onChange={e => setMasterForm(p => ({ ...p, therapeutic_area: e.target.value }))} placeholder="e.g. Oncology, CNS, Rare Disease" data-testid="master-therapeutic-area" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Company Description</Label>
                <Textarea value={masterForm.company_description || ''} onChange={e => setMasterForm(p => ({ ...p, company_description: e.target.value }))} rows={2} placeholder="Brief description of the company and their platform" data-testid="master-description" />
              </div>
            </div>

            <Separator />

            {/* Status */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Status</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agreement Status</Label>
                  <Select value={masterForm.agreement_status || 'Not Started'} onValueChange={v => setMasterForm(p => ({ ...p, agreement_status: v }))}>
                    <SelectTrigger data-testid="master-agreement-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AGREEMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>NCD Status</Label>
                  <Select value={masterForm.ncd_status || 'Not Received'} onValueChange={v => setMasterForm(p => ({ ...p, ncd_status: v }))}>
                    <SelectTrigger data-testid="master-ncd-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NCD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Regional Licensing */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Regional Licensing</p>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="master-india-licensing-toggle">
                <input type="checkbox" checked={!!masterForm.india_licensing} onChange={e => setMasterForm(p => ({ ...p, india_licensing: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500" />
                <span className="text-sm text-slate-700">India Licensing</span>
                <span className="text-[10px] text-slate-400">— Target all Indian pharma contacts for this biotech</span>
              </label>
            </div>

            <Separator />

            {/* Fundraising Profile */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Fundraising Profile</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Funding Stage</Label>
                  <Select value={masterForm.funding_stage || ''} onValueChange={v => setMasterForm(p => ({ ...p, funding_stage: v }))}>
                    <SelectTrigger data-testid="master-funding-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent>
                      {['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Bridge', 'IPO', 'Grant', 'Debt', 'Other'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Amount</Label>
                  <Input value={masterForm.funding_amount || ''} onChange={e => setMasterForm(p => ({ ...p, funding_amount: e.target.value }))} placeholder="e.g., $5M, $10-15M" data-testid="master-funding-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Use of Funds</Label>
                  <Input value={masterForm.use_of_funds || ''} onChange={e => setMasterForm(p => ({ ...p, use_of_funds: e.target.value }))} placeholder="e.g., Phase 2 trials, scale-up" data-testid="master-use-of-funds" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Documents Upload (up to 3) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Documents ({masterDocSlots.length}/5)
                </p>
                {masterDocSlots.length < 5 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setMasterDocSlots(prev => [...prev, { type: 'Non-Confidential Deck', customLabel: '', file: null, fileName: '' }])} className="text-sky-600 hover:text-sky-700 h-7 text-xs" data-testid="master-add-doc">
                    <Plus className="w-3 h-3 mr-1" /> Add Document
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {masterDocSlots.map((slot, idx) => (
                  <div key={idx} className="relative bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="w-8 h-8 rounded-md bg-sky-50 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-sky-600" />
                      </div>
                      <Select value={slot.type} onValueChange={v => setMasterDocSlots(prev => prev.map((s, i) => i === idx ? { ...s, type: v } : s))}>
                        <SelectTrigger className="w-[200px] h-8 text-xs" data-testid={`master-doc-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOC_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {slot.type === 'Other' && (
                        <Input
                          value={slot.customLabel || ''}
                          onChange={e => setMasterDocSlots(prev => prev.map((s, i) => i === idx ? { ...s, customLabel: e.target.value } : s))}
                          placeholder="Label (e.g. Pipeline Overview)"
                          className="w-[200px] h-8 text-xs"
                          data-testid={`master-doc-custom-label-${idx}`}
                        />
                      )}
                      <input type="file" ref={masterFileRefs[idx]} onChange={e => { const f = e.target.files?.[0]; if (f) setMasterDocSlots(prev => prev.map((s, i) => i === idx ? { ...s, file: f, fileName: f.name } : s)); }} className="hidden" accept=".pdf,.pptx,.ppt,.doc,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg" data-testid={`master-doc-input-${idx}`} />
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => masterFileRefs[idx]?.current?.click()} data-testid={`master-choose-file-${idx}`}>
                        <Upload className="w-3 h-3 mr-1.5" />
                        {slot.fileName || 'Choose File'}
                      </Button>
                      {slot.fileName && (
                        <Badge variant="secondary" className="text-[10px] max-w-[120px] truncate">{slot.fileName}</Badge>
                      )}
                      {masterDocSlots.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setMasterDocSlots(prev => prev.filter((_, i) => i !== idx))} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 shrink-0 ml-auto" data-testid={`master-remove-doc-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Previously uploaded documents — categorized */}
              {docs.length > 0 && (
                <div className="mt-3 space-y-3">
                  {[
                    // "Documents" is a catch-all: anything that's not a CDA or
                    // Partnership Agreement lands here, including custom-labeled
                    // "Other" docs (e.g. "Pipeline Overview", "PK Data").
                    { label: 'Documents', match: (t) => t !== 'CDA' && t !== 'Partnership Agreement', icon: <FileText className="w-3.5 h-3.5 text-sky-500" /> },
                    { label: 'CDA', match: (t) => t === 'CDA', icon: <FileText className="w-3.5 h-3.5 text-blue-500" /> },
                    { label: 'Partnership Agreement', match: (t) => t === 'Partnership Agreement', icon: <FileText className="w-3.5 h-3.5 text-purple-500" /> },
                  ].map(section => {
                    const sectionDocs = docs.filter(d => section.match(d.doc_type || ''));
                    if (sectionDocs.length === 0) return null;
                    return (
                      <div key={section.label}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          {section.icon}
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{section.label} ({sectionDocs.length})</p>
                        </div>
                        {sectionDocs.map((doc) => {
                          const pillClass = (() => {
                            const t = doc.doc_type;
                            if (t === 'Non-Confidential Deck') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
                            if (t === 'Corporate Overview') return 'bg-sky-100 text-sky-700 border-sky-200';
                            if (t === 'Molecule-Specific Information') return 'bg-violet-100 text-violet-700 border-violet-200';
                            if (t === 'CDA') return 'bg-blue-100 text-blue-700 border-blue-200';
                            if (t === 'Partnership Agreement') return 'bg-purple-100 text-purple-700 border-purple-200';
                            if (t === 'Scientific Poster') return 'bg-amber-100 text-amber-700 border-amber-200';
                            return 'bg-slate-100 text-slate-600 border-slate-200';
                          })();
                          return (
                          <div key={doc.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-slate-100 mb-1">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide shrink-0 ${pillClass}`}
                              data-testid={`dialog-doc-type-pill-${doc.id}`}
                            >
                              <FileText className="w-3 h-3" />
                              {doc.doc_type || 'Document'}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-800 truncate">{doc.original_filename}</p>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
                                try {
                                  await axios.delete(`${API}/documents/${doc.id}`, { withCredentials: true });
                                  toast.success('Document removed');
                                  fetchAll();
                                } catch { toast.error('Failed to delete'); }
                              }}
                              data-testid={`master-remove-doc-${doc.id}`}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Separator />

            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Contacts ({masterContacts.length})
                </p>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setMasterContacts(prev => [...prev, { name: '', designation: '', email: '', phone: '' }])} className="text-sky-600 hover:text-sky-700 h-7 text-xs" data-testid="master-add-contact">
                    <Plus className="w-3 h-3 mr-1" /> Add Contact
                  </Button>
              </div>
              <div className="space-y-4">
                {masterContacts.map((ct, idx) => {
                  const isDragging = draggedContactIdx === idx;
                  const isDragOver = dragOverContactIdx === idx && draggedContactIdx !== null && draggedContactIdx !== idx;
                  return (
                  <div
                    key={idx}
                    draggable={masterContacts.length > 1}
                    onDragStart={(e) => {
                      // Only initiate a drag when grabbed via the handle. The
                      // drag handle calls e.dataTransfer.setData; bare clicks
                      // on the input fields shouldn't trigger reorder.
                      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                      setDraggedContactIdx(idx);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                      if (draggedContactIdx !== null && draggedContactIdx !== idx) {
                        setDragOverContactIdx(idx);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverContactIdx === idx) setDragOverContactIdx(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = draggedContactIdx;
                      const to = idx;
                      setDraggedContactIdx(null);
                      setDragOverContactIdx(null);
                      if (from === null || from === to) return;
                      setMasterContacts(prev => {
                        const next = [...prev];
                        const [moved] = next.splice(from, 1);
                        next.splice(to, 0, moved);
                        return next;
                      });
                    }}
                    onDragEnd={() => {
                      setDraggedContactIdx(null);
                      setDragOverContactIdx(null);
                    }}
                    className={`relative bg-slate-50 rounded-lg p-4 border transition-all ${
                      isDragging ? 'opacity-40 border-sky-300' :
                      isDragOver ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' :
                      'border-slate-100'
                    }`}
                    data-testid={`master-contact-card-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {masterContacts.length > 1 && (
                          <span
                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 -ml-1 select-none"
                            title="Drag to reorder"
                            data-testid={`master-contact-drag-${idx}`}
                          >
                            <GripVertical className="w-4 h-4" />
                          </span>
                        )}
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs font-medium text-slate-600">Contact {idx + 1}</span>
                      </div>
                      {masterContacts.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setMasterContacts(prev => prev.filter((_, i) => i !== idx))} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" data-testid={`master-remove-contact-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input placeholder="Full Name" value={ct.name || ''} onChange={e => setMasterContacts(prev => prev.map((c, i) => i === idx ? { ...c, name: e.target.value } : c))} className="h-9 text-sm" data-testid={`master-contact-name-${idx}`} />
                      <Input placeholder="Designation (e.g. CEO, CSO)" value={ct.designation || ''} onChange={e => setMasterContacts(prev => prev.map((c, i) => i === idx ? { ...c, designation: e.target.value } : c))} className="h-9 text-sm" data-testid={`master-contact-designation-${idx}`} />
                      <Input placeholder="Email" type="email" value={ct.email || ''} onChange={e => setMasterContacts(prev => prev.map((c, i) => i === idx ? { ...c, email: e.target.value } : c))} className="h-9 text-sm" data-testid={`master-contact-email-${idx}`} />
                      <Input placeholder="Phone" value={ct.phone || ''} onChange={e => setMasterContacts(prev => prev.map((c, i) => i === idx ? { ...c, phone: e.target.value } : c))} className="h-9 text-sm" data-testid={`master-contact-phone-${idx}`} />
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Assets / Molecules */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Assets {masterAssets.length > 1 ? `(${masterAssets.length} molecules)` : ''}
                </p>
              </div>
              {masterAssets.length > 0 ? (
                <div className="space-y-4">
                  {masterAssets.map((asset, idx) => (
                    <div key={asset.id || idx} className={`relative bg-slate-50 rounded-lg border overflow-hidden transition-colors ${asset.is_hidden ? 'border-slate-200 opacity-60' : 'border-slate-100'}`}>
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-100/80 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-3.5 h-3.5 text-violet-500" />
                          <span className="text-xs font-medium text-slate-600">{masterAssets.length > 1 ? `Molecule ${idx + 1}` : 'Asset'}</span>
                          {asset.is_hidden && <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-slate-300 text-slate-500">Hidden from outreach</Badge>}
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Include this asset in campaigns, list stats, and public status link">
                          <input
                            type="checkbox"
                            checked={!asset.is_hidden}
                            onChange={() => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, is_hidden: !a.is_hidden } : a))}
                            className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                            data-testid={`master-asset-include-${idx}`}
                          />
                          <span className="text-[11px] font-medium text-slate-600">Include in outreach</span>
                        </label>
                      </div>
                      <div className="divide-y divide-slate-200">
                        {/* Molecule Name */}
                        <div className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                          <span className="text-sm font-medium text-slate-700">Molecule/Product Number</span>
                          <Input
                            placeholder="Enter molecule/product number..."
                            value={asset.asset_name || ''}
                            onChange={e => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, asset_name: e.target.value } : a))}
                            className="h-8 text-sm border-slate-200"
                            data-testid={`master-asset-name-${idx}`}
                          />
                        </div>
                        {/* Development Stage */}
                        <div className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                          <span className="text-sm font-medium text-slate-700">Development Stage</span>
                          <div className="flex gap-2">
                            <Select value={asset.stage || 'Pre-Clinical'} onValueChange={v => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, stage: v } : a))}>
                              <SelectTrigger className="h-8 text-sm w-[160px]" data-testid={`master-asset-stage-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Stage notes..."
                              value={asset.stage_notes || ''}
                              onChange={e => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, stage_notes: e.target.value } : a))}
                              className="h-8 text-sm border-slate-200 flex-1"
                              data-testid={`master-asset-stage-notes-${idx}`}
                            />
                          </div>
                        </div>
                        {/* Remaining parameter fields */}
                        {PARAMETER_FIELDS.filter(f => f !== 'Molecule/Product Number').map((field) => {
                          const fieldKey = field === 'Platform' ? 'platform' : field === 'Target' ? 'target' : field === 'Modality' ? 'modality' : field === 'Indication' ? 'indication' : field === 'Drug Delivery Mechanism' ? 'drug_delivery_mechanism' : field;
                          return (
                            <div key={field} className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                              <span className="text-sm font-medium text-slate-700">{field}</span>
                              <Input
                                placeholder={`Enter ${field.toLowerCase()}...`}
                                value={asset[fieldKey] || ''}
                                onChange={e => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, [fieldKey]: e.target.value } : a))}
                                className="h-8 text-sm border-slate-200"
                                data-testid={`master-asset-${fieldKey}-${idx}`}
                              />
                            </div>
                          );
                        })}
                        {/* Per-molecule: Description, Objectives, Looking For, Emailing, Status */}
                        <div className="px-4 py-3 bg-white/30 space-y-3 border-t border-slate-200">
                          <div className="space-y-1.5">
                            <span className="text-xs font-medium text-slate-600">Asset Description</span>
                            <Textarea
                              value={asset.molecule_description || ''}
                              onChange={e => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, molecule_description: e.target.value } : a))}
                              rows={2}
                              placeholder="Description of this molecule, asset, or technology"
                              className="text-sm border-slate-200"
                              data-testid={`master-asset-desc-${idx}`}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <span className="text-xs font-medium text-slate-600">What They Are Trying to Achieve</span>
                            <Textarea
                              value={asset.objectives || ''}
                              onChange={e => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, objectives: e.target.value } : a))}
                              rows={2}
                              placeholder="e.g. Out-licensing for Phase 2, seeking co-development partner"
                              className="text-sm border-slate-200"
                              data-testid={`master-asset-objectives-${idx}`}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <span className="text-xs font-medium text-slate-600">Looking For</span>
                              <div className="space-y-1.5 pt-0.5">
                                {DEAL_INTEREST_OPTIONS.map(opt => {
                                  const interests = asset.deal_interests || [];
                                  const checked = interests.includes(opt.value);
                                  return (
                                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          const next = checked ? interests.filter(v => v !== opt.value) : [...interests, opt.value];
                                          setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, deal_interests: next } : a));
                                        }}
                                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                        data-testid={`master-asset-${opt.value.toLowerCase().replace(/[^a-z]/g, '-')}-${idx}`}
                                      />
                                      <span className="text-xs text-slate-700">{opt.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <span className="text-xs font-medium text-slate-600">Emailing</span>
                              <Select value={asset.emailing_status || 'Not Started'} onValueChange={v => setMasterAssets(prev => prev.map((a, i) => i === idx ? { ...a, emailing_status: v } : a))}>
                                <SelectTrigger className="h-8 text-sm" data-testid={`master-asset-emailing-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {EMAILING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No assets — use the AI assistant to upload a deck and auto-populate</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setMasterEditOpen(false)} data-testid="master-edit-cancel">Cancel</Button>
              <Button onClick={saveMasterEdit} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="master-edit-save">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Asset Dialog */}
      <Dialog open={assetDialogOpen} onOpenChange={(open) => { setAssetDialogOpen(open); if (!open) { setEditingAsset(null); setAssetForm({ ...emptyAssetForm }); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingAsset ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Define a molecule or asset for this company.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssetSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Molecule / Product Number *</Label>
              <Input value={assetForm.asset_name} onChange={e => setAssetForm(p => ({ ...p, asset_name: e.target.value }))} required placeholder="e.g., ABC-123, NAI002" data-testid="asset-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Development Stage</Label>
                <Select value={assetForm.stage} onValueChange={v => setAssetForm(p => ({ ...p, stage: v }))}>
                  <SelectTrigger data-testid="asset-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Stage Notes</Label>
                <Input value={assetForm.stage_notes} onChange={e => setAssetForm(p => ({ ...p, stage_notes: e.target.value }))} placeholder="e.g., Phase 2 planned for 2026" data-testid="asset-stage-notes" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Platform</Label>
                <Input value={assetForm.platform} onChange={e => setAssetForm(p => ({ ...p, platform: e.target.value }))} placeholder="e.g., ADC, Gene Therapy" data-testid="asset-platform" />
              </div>
              <div className="space-y-2">
                <Label>Target</Label>
                <Input value={assetForm.target} onChange={e => setAssetForm(p => ({ ...p, target: e.target.value }))} placeholder="e.g., PD-L1, IGF1R" data-testid="asset-target" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Modality</Label>
                <Input value={assetForm.modality} onChange={e => setAssetForm(p => ({ ...p, modality: e.target.value }))} placeholder="e.g., mAb, Small Molecule" data-testid="asset-modality" />
              </div>
              <div className="space-y-2">
                <Label>Indication</Label>
                <Input value={assetForm.indication} onChange={e => setAssetForm(p => ({ ...p, indication: e.target.value }))} placeholder="e.g., NSCLC, RA" data-testid="asset-indication" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Drug Delivery Mechanism</Label>
              <Input value={assetForm.drug_delivery_mechanism} onChange={e => setAssetForm(p => ({ ...p, drug_delivery_mechanism: e.target.value }))} placeholder="e.g., IV, Oral, Subcutaneous" data-testid="asset-delivery" />
            </div>
            <div className="space-y-2">
              <Label>Therapeutic Area</Label>
              <Input value={assetForm.therapeutic_area} onChange={e => setAssetForm(p => ({ ...p, therapeutic_area: e.target.value }))} placeholder="e.g., Oncology, Immunology, CNS" data-testid="asset-therapeutic-area" />
            </div>
            <div className="space-y-2">
              <Label>Asset Description</Label>
              <Textarea value={assetForm.molecule_description} onChange={e => setAssetForm(p => ({ ...p, molecule_description: e.target.value }))} rows={2} placeholder="Description of this molecule..." data-testid="asset-description" />
            </div>
            <div className="space-y-2">
              <Label>Objectives</Label>
              <Textarea value={assetForm.objectives} onChange={e => setAssetForm(p => ({ ...p, objectives: e.target.value }))} rows={2} placeholder="What are the goals for this asset?" data-testid="asset-objectives" />
            </div>
            <div className="space-y-2">
              <Label>Looking For</Label>
              <div className="flex flex-wrap gap-3 pt-1">
                {DEAL_INTEREST_OPTIONS.map(opt => {
                  const interests = assetForm.deal_interests || [];
                  const checked = interests.includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked ? interests.filter(v => v !== opt.value) : [...interests, opt.value];
                          setAssetForm(p => ({ ...p, deal_interests: next }));
                        }}
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-xs text-slate-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Emailing</Label>
                <Select value={assetForm.emailing_status || 'Not Started'} onValueChange={v => setAssetForm(p => ({ ...p, emailing_status: v }))}>
                  <SelectTrigger data-testid="asset-emailing-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Not Started', 'In Progress', 'Completed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-600 text-white hover:bg-violet-700" data-testid="asset-submit-btn">{editingAsset ? 'Update Asset' : 'Add Asset'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Asset Confirm */}
      <AlertDialog open={!!deleteAssetTarget} onOpenChange={(open) => !open && setDeleteAssetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteAssetTarget?.asset_name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAsset} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-asset">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Asset to Another Company */}
      <Dialog open={!!moveAsset} onOpenChange={(open) => { if (!open) { setMoveAsset(null); setMoveTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="move-asset-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-violet-500" /> Move Asset
            </DialogTitle>
            <DialogDescription>
              Re-point <span className="font-medium text-slate-700">"{moveAsset?.asset_name}"</span> to a different biotech company. The AI assessment &amp; score stay attached to the asset — only its owning company changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {!moveTarget ? (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={moveSearch} onChange={e => handleMoveSearch(e.target.value)}
                    placeholder="Search biotech company by name…" className="pl-8 h-9 text-sm" autoFocus data-testid="move-search-input" />
                  {moveSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
                </div>
                {moveResults.length > 0 && (
                  <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-50">
                    {moveResults.map((c) => (
                      <button key={c.id} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2.5"
                        onClick={() => setMoveTarget(c)} data-testid={`move-result-${c.id}`}>
                        <Building2 className="w-3.5 h-3.5 text-cyan-600 shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-slate-800 truncate">{c.company_name}</span>
                          {c.country && <span className="block text-[10px] text-slate-400">{c.country}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {moveSearch.trim().length >= 2 && !moveSearching && moveResults.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-2">No companies match "{moveSearch}"</p>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 flex items-center justify-between">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">{moveTarget.company_name}</span>
                  {moveTarget.country && <span className="block text-[10px] text-slate-500">{moveTarget.country}</span>}
                </span>
                <button className="text-[11px] text-violet-700 hover:underline shrink-0" onClick={() => setMoveTarget(null)} data-testid="move-change-target">Change</button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMoveAsset(null); setMoveTarget(null); }} data-testid="move-cancel">Cancel</Button>
            <Button onClick={handleConfirmMove} disabled={!moveTarget || movingAsset} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-move-asset">
              {movingAsset ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />}
              Move asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Enrich all · Step 1 — Asset inventory reconciliation review */}
      <Dialog open={!!reconcile} onOpenChange={(open) => { if (!open && !reconcile?.applying) setReconcile(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-assets-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-fuchsia-600" /> Confirm the asset inventory</DialogTitle>
            <DialogDescription>
              The uploaded documents were read to check which recorded assets are genuinely {reconcile?.company_name || 'this company'}'s own programs.
              Mention ≠ ownership — molecules that only appear in competitive-landscape, comparator or market slides are proposed for removal.
              Removed assets are archived and their outreach history is preserved. Untick anything you want to keep.
            </DialogDescription>
          </DialogHeader>
          {reconcile?.summary && <p className="text-xs text-slate-600 italic border-l-2 border-fuchsia-300 pl-3" data-testid="reconcile-summary">{reconcile.summary}</p>}
          {reconcile?.sources?.length > 0 && <p className="text-[10px] text-slate-400">Sources: {reconcile.sources.join(' · ')}</p>}
          <div className="space-y-1.5 mt-2">
            {(reconcile?.verdicts || []).map(v => {
              const isOwn = v.classification === 'own';
              const isAlias = !!v.merge_into_asset_id;
              const checked = isAlias ? (reconcile.selectedMerges || new Set()).has(v.asset_id) : reconcile.selected.has(v.asset_id);
              const label = { own: 'Company asset', alias_of: `Alias of ${v.merge_into_name || 'canonical asset'}`, competitor: 'Competitor', comparator: 'Comparator', standard_of_care: 'Standard of care', combination_partner: 'Combination partner', platform_technology: 'Platform / technology', third_party_landscape: 'Landscape mention', benchmark_only: 'Benchmark only', unknown: 'Not found in documents' }[v.classification] || v.classification;
              const prog = v.program || {};
              const st = prog.stage || {};
              const toggle = (e) => setReconcile(r => {
                const key = isAlias ? 'selectedMerges' : 'selected';
                const s = new Set(r[key] || []); e.target.checked ? s.add(v.asset_id) : s.delete(v.asset_id);
                return { ...r, [key]: s };
              });
              const tone = isOwn ? 'border-emerald-200 bg-emerald-50/50' : isAlias && checked ? 'border-sky-200 bg-sky-50/60' : checked ? 'border-rose-200 bg-rose-50/60' : 'border-slate-200 bg-white';
              return (
                <label key={v.asset_id} className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${tone}`} data-testid={`reconcile-row-${v.asset_id}`}>
                  <input type="checkbox" disabled={isOwn || reconcile.applying} checked={!isOwn && checked} onChange={toggle}
                    className="mt-1 w-3.5 h-3.5 rounded border-slate-300" data-testid={`reconcile-${isAlias ? 'merge' : 'remove'}-${v.asset_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${isOwn ? 'text-emerald-800' : checked ? (isAlias ? 'text-sky-800' : 'text-rose-800 line-through decoration-rose-300') : 'text-slate-800'}`}>{v.asset_name}</span>
                      <Badge className={`text-[9px] px-1.5 py-0 ${isOwn ? 'bg-emerald-100 text-emerald-700' : isAlias ? 'bg-sky-100 text-sky-700' : v.classification === 'unknown' ? 'bg-slate-100 text-slate-600' : 'bg-rose-100 text-rose-700'}`}>{label}</Badge>
                      {isAlias && checked && <span className="text-[10px] text-sky-700">→ merged into <b>{v.merge_into_name}</b>, name kept as alias</span>}
                      <span className="text-[9px] text-slate-400 uppercase">{v.confidence} confidence</span>
                      {v.phase && <span className="text-[10px] text-slate-500">{v.phase}</span>}
                    </div>
                    {v.evidence && <p className="text-[11px] text-slate-600 mt-0.5">{v.evidence}</p>}
                    {isOwn && (prog.aliases?.length > 0 || st.current || st.needs_review) && (
                      <div className="mt-1 text-[10px] text-emerald-900/80 space-y-0.5" data-testid={`reconcile-program-${v.asset_id}`}>
                        {prog.aliases?.length > 0 && <div>Also known as: <b>{prog.aliases.join(', ')}</b></div>}
                        {(st.current || st.completed || st.planned) && (
                          <div>
                            Stage — current: <b>{st.current || '—'}</b>{st.completed && <> · completed: {st.completed}</>}{st.planned && <> · planned: {st.planned}</>}
                            {st.needs_review && <Badge className="ml-1 bg-amber-100 text-amber-800 text-[9px] px-1 py-0">Needs review</Badge>}
                          </div>
                        )}
                        {st.stage_note && <div className="italic text-slate-500">{st.stage_note}</div>}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          {(reconcile?.missing_own_assets || []).length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900" data-testid="reconcile-missing">
              <span className="font-semibold">Own programs found in the documents but not recorded:</span>{' '}
              {reconcile.missing_own_assets.map(m => m.asset_name).join(', ')} — add them via <b>+ Add</b> after this step.
            </div>
          )}
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setReconcile(null)} disabled={reconcile?.applying} data-testid="reconcile-cancel">Cancel</Button>
            <Button onClick={applyReconcileAndEnrich} disabled={reconcile?.applying} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="reconcile-apply">
              {reconcile?.applying ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Applying…</> : `Remove ${reconcile?.selected?.size || 0} · merge ${reconcile?.selectedMerges?.size || 0} · keep ${(reconcile?.verdicts || []).length - (reconcile?.selected?.size || 0) - (reconcile?.selectedMerges?.size || 0)} · then enrich`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Asset Merge Dialog */}
      <AssetRecoveryDialog open={assetRecoveryOpen} onOpenChange={setAssetRecoveryOpen} companyId={id} onRestored={fetchAll} />
      <Dialog open={assetMergeDialogOpen} onOpenChange={(open) => { setAssetMergeDialogOpen(open); if (!open) { setAssetMergeSelected({}); setAssetMergePrimary(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-amber-600" />
              Merge Duplicate Assets
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Select the assets that are duplicates and pick one to keep as primary. Outreach history, mappings, and documents on the others will be rewired to the primary. The merged-into rows will be deleted permanently.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto mt-2">
            {duplicateGroups.length > 0 && (
              <div className="space-y-2" data-testid="merge-duplicate-groups">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Likely duplicates · {duplicateGroups.length} group{duplicateGroups.length !== 1 ? 's' : ''} (same name)</p>
                  {duplicateGroups.length > 1 && (
                    <button type="button" disabled={assetMerging}
                      onClick={async () => {
                        const total = duplicateGroups.reduce((n, g) => n + g.members.length - 1, 0);
                        if (!window.confirm(`Merge all ${duplicateGroups.length} groups in one go?\n\nIn each group the most complete row is kept as primary and the other ${total} duplicate row${total !== 1 ? 's' : ''} are merged into it (outreach, mappings and documents rewired). This cannot be undone.`)) return;
                        setAssetMerging(true);
                        let ok = 0, failed = 0, deleted = 0;
                        for (const g of duplicateGroups) {
                          const score = (a) => (a.molecule_description || a.description || '').length + (a.stage ? 50 : 0) + (a.indication || '').length + (a.aliases?.length || 0) * 20;
                          const primary = [...g.members].sort((x, y) => score(y) - score(x))[0];
                          const siblings = g.members.filter(m => m.id !== primary.id).map(m => m.id);
                          if (!siblings.length) continue;
                          try {
                            const { data } = await axios.post(`${API}/asset-dedupe/manual-merge`, { primary_id: primary.id, sibling_ids: siblings }, { withCredentials: true });
                            ok += 1; deleted += data.deleted_count || siblings.length;
                          } catch (e) { failed += 1; }
                        }
                        setAssetMerging(false);
                        toast[failed ? 'warning' : 'success'](`Merged ${ok} group${ok !== 1 ? 's' : ''} · ${deleted} duplicate row${deleted !== 1 ? 's' : ''} folded into their primaries${failed ? ` · ${failed} group${failed !== 1 ? 's' : ''} failed` : ''}`, { duration: 8000 });
                        setAssetMergeDialogOpen(false); setAssetMergeSelected({}); setAssetMergePrimary('');
                        await fetchAll();
                      }}
                      className="text-[10px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 px-2.5 py-1 rounded flex items-center gap-1"
                      data-testid="merge-all-groups-btn">
                      {assetMerging ? <><Loader2 className="w-3 h-3 animate-spin" /> Merging…</> : <><GitMerge className="w-3 h-3" /> Merge all {duplicateGroups.length} groups</>}
                    </button>
                  )}
                </div>
                {duplicateGroups.map(g => {
                  const allSel = g.members.every(m => assetMergeSelected[m.id]) && Object.keys(assetMergeSelected).length === g.members.length;
                  return (
                    <div key={g.key} className={`rounded-lg border p-2 ${allSel ? 'border-amber-400 bg-amber-50/60' : 'border-amber-200 bg-amber-50/20'}`} data-testid={`merge-group-${g.key}`}>
                      <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
                        <p className="text-xs font-semibold text-slate-800">{g.label} <span className="text-slate-500 font-normal">· {g.members.length} rows</span></p>
                        <button type="button" onClick={() => selectDuplicateGroup(g)} className="text-[10px] font-medium text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 px-2 py-0.5 rounded" data-testid={`merge-select-group-${g.key}`}>
                          {allSel ? 'Selected' : 'Select group'}
                        </button>
                      </div>
                      <div className="space-y-1.5">{g.members.map(a => renderMergeRow(a))}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {(() => {
              const singles = assets.filter(a => !groupedAssetIds.has(a.id));
              if (!singles.length) return null;
              return (
                <div className="pt-1">
                  <button type="button" onClick={() => setMergeShowSingles(s => !s)} className="text-[10px] font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="merge-toggle-singles">
                    {mergeShowSingles ? '▾' : '▸'} {duplicateGroups.length ? `Other assets (${singles.length}) — no name match` : `All assets (${singles.length})`}
                  </button>
                  {(mergeShowSingles || !duplicateGroups.length) && <div className="space-y-2 mt-2">{singles.map(a => renderMergeRow(a))}</div>}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssetMergeDialogOpen(false)} disabled={assetMerging} data-testid="merge-cancel-btn">Cancel</Button>
            <Button
              onClick={async () => {
                const selectedIds = Object.keys(assetMergeSelected);
                if (selectedIds.length < 2) { toast.error('Pick at least 2 assets to merge'); return; }
                if (!assetMergePrimary || !assetMergeSelected[assetMergePrimary]) { toast.error('Mark one of the selected assets as Primary (the one to keep)'); return; }
                const siblings = selectedIds.filter(x => x !== assetMergePrimary);
                if (!window.confirm(`Merge ${siblings.length} asset${siblings.length !== 1 ? 's' : ''} into the primary? This deletes the merged-in rows. This action cannot be undone.`)) return;
                try {
                  setAssetMerging(true);
                  const { data } = await axios.post(`${API}/asset-dedupe/manual-merge`, {
                    primary_id: assetMergePrimary,
                    sibling_ids: siblings,
                  }, { withCredentials: true });
                  // Feb 2026 · manual_merge_assets now auto-cleans the primary via
                  // Claude so the merged asset reads as a polished narrative rather
                  // than stitched fragments. Reflect that in the toast so the user
                  // knows a cleanup happened (and can regenerate again later via
                  // the Sparkles button on the asset card if they don't like it).
                  const cleaned = data.ai_cleanup?.applied;
                  toast.success(
                    `Merged ${data.deleted_count} asset${data.deleted_count !== 1 ? 's' : ''} into primary` +
                    (cleaned ? ' · profile auto-cleaned into polished narrative' : ''),
                    { duration: 8000 },
                  );
                  setAssetMergeDialogOpen(false);
                  setAssetMergeSelected({});
                  setAssetMergePrimary('');
                  await fetchAll();
                } catch (e) {
                  const detail = e?.response?.data?.detail || e?.message || 'Merge failed';
                  toast.error(`Merge failed: ${detail}`);
                } finally {
                  setAssetMerging(false);
                }
              }}
              disabled={assetMerging || Object.keys(assetMergeSelected).length < 2 || !assetMergePrimary}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="merge-confirm-btn"
            >
              {assetMerging ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Merging...</> : <>Merge {Object.keys(assetMergeSelected).filter(x => x !== assetMergePrimary).length} into Primary</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={(open) => { setContactDialogOpen(open); if (!open) { setContactForm({ ...emptyContact }); setEditingContactIdx(null); } }}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingContactIdx !== null ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">{editingContactIdx !== null ? 'Update contact details.' : 'Add a new contact for this company.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid="contact-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Designation</Label>
              <Input value={contactForm.designation} onChange={e => setContactForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g., VP Business Development" data-testid="contact-designation-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@company.com" data-testid="contact-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 8900" data-testid="contact-phone-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-600 text-white hover:bg-sky-700" data-testid="contact-submit-btn">{editingContactIdx !== null ? 'Update Contact' : 'Add Contact'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Target Companies */}
      {targetCompanies.length > 0 && (
        <Card className="border border-slate-200 shadow-sm mb-8" data-testid="detail-targets">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Target Companies for Out-Licensing</h3>
              <Badge variant="secondary" className="text-xs">{targetCompanies.length}</Badge>
            </div>
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1.2fr_100px_2fr] px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Country</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Rationale</span>
              </div>
              <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
                {targetCompanies.map((tc, i) => (
                  <div key={i} className="grid grid-cols-[1.2fr_100px_2fr] px-4 py-2.5 items-start gap-2 hover:bg-slate-50/50">
                    <p className="text-xs font-medium text-slate-900">{tc.company_name}</p>
                    <div className="flex items-center gap-1 text-xs text-slate-600"><Globe className="w-3 h-3 text-slate-400" />{tc.country}</div>
                    <p className="text-xs text-slate-500 leading-relaxed">{tc.rationale}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outreach Tracker */}
      <Card className="border border-slate-200 shadow-sm" data-testid="detail-outreach">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-sky-500" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Outreach Tracker</h3>
            </div>
            <Badge variant="secondary" className="text-xs">{outreach.length} conversation{outreach.length !== 1 ? 's' : ''}</Badge>
            {bulkSendCount > 0 && (
              <button type="button" onClick={() => setIncludeBulkSends(v => !v)}
                className={`text-[11px] px-2 py-1 rounded border transition-colors ${includeBulkSends ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-sky-700 border-sky-300 hover:bg-sky-50'}`}
                data-testid="toggle-bulk-sends" title="Emails sent in bulk from AI Studio (no account workflow)">
                {includeBulkSends ? 'Showing' : 'Show'} {bulkSendCount.toLocaleString()} AI Studio send{bulkSendCount === 1 ? '' : 's'}{includeBulkSends ? ' · hide' : ''}
              </button>
            )}
            <div className="flex-1" />
            <Select value={outreachStatusFilter} onValueChange={setOutreachStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="outreach-status-filter">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Feb 2026 (v7) — Asset filter. */}
            <Select value={outreachAssetFilter} onValueChange={setOutreachAssetFilter}>
              <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="outreach-asset-filter">
                <SelectValue placeholder="Filter asset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Assets</SelectItem>
                {Array.from(new Set(outreach.map(o => (o.asset_name_canonical || o.asset_name || '').trim()).filter(Boolean))).sort().map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* View-mode toggle: Grouped (Pharma × Asset opportunity) vs Contact. */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                onClick={() => setOutreachViewMode('grouped')}
                className={`px-2.5 py-1.5 text-[11px] transition-colors ${outreachViewMode === 'grouped' ? 'bg-sky-50 text-sky-700 font-medium' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="outreach-view-grouped"
              >Grouped</button>
              <button
                onClick={() => setOutreachViewMode('contact')}
                className={`px-2.5 py-1.5 text-[11px] border-l border-slate-200 transition-colors ${outreachViewMode === 'contact' ? 'bg-sky-50 text-sky-700 font-medium' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="outreach-view-contact"
              >Contact</button>
            </div>
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                className="pl-8 h-8 text-xs"
                placeholder="Search..."
                value={outreachSearch}
                onChange={e => setOutreachSearch(e.target.value)}
                data-testid="outreach-search"
              />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={copyOutreachSummary} data-testid="copy-outreach-summary">
              <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy Summary
            </Button>
            <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800" onClick={() => {
              const currentAsset = assets.length > 0 ? assets[Math.min(assetIndex, assets.length - 1)] : null;
              setOutreachForm({ ...emptyOutreachForm, asset_id: currentAsset?.id || '', asset_name: currentAsset?.asset_name || '' });
              // Pre-select the currently-viewed asset in the new multi-select.
              setOutreachSelectedAssetIds(currentAsset?.id ? [currentAsset.id] : []);
              setOutreachMode('existing');
              setContactSearchTerm('');
              setContactDropdownOpen(false);
              setOutreachDialogOpen(true);
              // Lazy-load contacts only when dialog opens
              if (pharmaContacts.length === 0) {
                axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true })
                  .then(res => {
                    const items = res.data?.items || res.data || [];
                    setPharmaContacts(items);
                  })
                  .catch(() => {});
              }
              if (investorContacts.length === 0) {
                axios.get(`${API}/investors?limit=5000`, { withCredentials: true })
                  .then(res => {
                    const items = res.data?.items || res.data || [];
                    setInvestorContacts(items);
                  })
                  .catch(() => {});
              }
            }} data-testid="add-outreach-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Outreach
            </Button>
          </div>

          {/* Pharma + Fundraising tabs (Investors module re-launched Oct 2026) */}
          <div className="flex items-center gap-1 mb-4 border-b border-slate-100">
            {[
              { key: 'pharma', label: 'Pharma', count: outreach.filter(o => o.contact_type !== 'Investor').length },
              { key: 'fundraising', label: 'Fundraising', count: fundraisingCount },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setOutreachTab(tab.key)}
                className={`px-4 py-2 text-xs font-semibold transition-colors border-b-2 ${outreachTab === tab.key ? 'border-sky-500 text-sky-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                data-testid={`outreach-tab-${tab.key}`}
              >
                {tab.label} <span className="ml-1.5 text-[10px] font-normal">({tab.count})</span>
              </button>
            ))}
          </div>

          {outreachTab === 'fundraising' && <FundraisingTrack biotechId={id} onCountChange={setFundraisingCount} />}

          {outreachTab !== 'fundraising' && (() => {
            const term = outreachSearch.toLowerCase();
            const filtered = outreach
              .filter(o =>
                o.contact_type !== 'Investor' &&
                (outreachStatusFilter === 'All' || o.status === outreachStatusFilter) &&
                (outreachAssetFilter === '__all__' || (o.asset_name_canonical || o.asset_name || '').trim() === outreachAssetFilter) &&
                (!term ||
                (o.contact_name || '').toLowerCase().includes(term) ||
                (o.contact_email || '').toLowerCase().includes(term) ||
                (o.company_name || '').toLowerCase().includes(term) ||
                (o.country || '').toLowerCase().includes(term) ||
                (o.designation || '').toLowerCase().includes(term))
              );

            if (filtered.length === 0) {
              return (
                <div className="text-center py-8">
                  <Send className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">{outreach.length === 0 ? 'No outreach conversations yet' : 'No results matching your search'}</p>
                  {outreach.length === 0 && <p className="text-xs text-slate-400 mt-1">Add outreach from the company list page</p>}
                  {outreach.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setOutreachSearch(''); setOutreachStatusFilter('All'); setOutreachAssetFilter('__all__'); setShowIntroOnlyGroups(true); }}
                      className="mt-2 text-xs font-medium text-sky-700 hover:underline"
                      data-testid="outreach-clear-filters"
                    >
                      Clear filters — show all {outreach.filter(o => o.contact_type !== 'Investor').length} rows
                    </button>
                  )}
                </div>
              );
            }

            // ── Grouped view (Feb 2026 v7 · default) ────────────────────
            // ONE card per (Pharma × Asset) opportunity, expandable to
            // reveal every contact at that opportunity. Matches how the
            // dashboard funnel counts BD opportunities (1 opportunity =
            // 1 pharma + 1 asset, regardless of how many contacts were
            // reached).
            if (outreachViewMode === 'grouped') {
              const groups = new Map();
              filtered.forEach(o => {
                const key = `${(o.company_name || '').trim().toLowerCase()}|${(o.asset_name_canonical || o.asset_name || '').trim().toLowerCase()}`;
                if (!groups.has(key)) groups.set(key, { key, company: o.company_name || '—', asset: o.asset_name_canonical || o.asset_name || '—', country: o.country || '', rows: [] });
                groups.get(key).rows.push(o);
              });
              // Sep 2026 (user): companies whose latest status is only
              // "Introduction Made" (email sent, no engagement yet) are noise
              // here — hide them by default; the campaign log has them.
              const _latestOf = (g) => ([...g.rows].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0] || {}).status || '';
              const allGroups = Array.from(groups.values());
              const introOnlyGroups = outreachStatusFilter === 'All' ? allGroups.filter(g => _latestOf(g) === 'Introduction Made') : [];
              const groupArr = allGroups
                .filter(g => showIntroOnlyGroups || outreachStatusFilter !== 'All' || _latestOf(g) !== 'Introduction Made')
                .sort((a, b) => a.company.localeCompare(b.company) || a.asset.localeCompare(b.asset));
              const toggleGroup = (k) => setExpandedOutreachGroups(prev => {
                const n = new Set(prev);
                if (n.has(k)) n.delete(k); else n.add(k);
                return n;
              });
              return (
                <div className="border border-slate-100 rounded-lg overflow-hidden" data-testid="outreach-grouped">
                  {introOnlyGroups.length > 0 && (
                    <div className="px-4 py-1.5 bg-slate-50/60 border-b border-slate-100 text-[11px] text-slate-500 flex items-center gap-2" data-testid="intro-only-note">
                      <span>{introOnlyGroups.length} compan{introOnlyGroups.length === 1 ? 'y' : 'ies'} with only "Introduction Made" (email sent, no engagement yet) {showIntroOnlyGroups ? 'shown' : 'hidden'}.</span>
                      <button type="button" onClick={() => setShowIntroOnlyGroups(v => !v)} className="text-sky-700 hover:underline" data-testid="toggle-intro-only">{showIntroOnlyGroups ? 'Hide' : 'Show'}</button>
                    </div>
                  )}
                  {groupArr.length === 0 && (
                    <p className="px-4 py-6 text-center text-xs text-slate-400" data-testid="outreach-no-engaged">No engaged companies yet — all outreach so far is at "Introduction Made".</p>
                  )}
                  <div className="grid grid-cols-[1fr_0.7fr_1fr_100px_200px_60px] px-4 py-2 bg-slate-50 border-b border-slate-100">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Country</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Asset</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contacts</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Latest Status</span>
                    <span></span>
                  </div>
                  <div className="divide-y divide-slate-50 max-h-[600px] overflow-y-auto">
                    {groupArr.map(g => {
                      const expanded = expandedOutreachGroups.has(g.key);
                      // Latest status = most-recently updated row's status.
                      const sortedByDate = [...g.rows].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                      const latest = sortedByDate[0];
                      return (
                        <div key={g.key} data-testid={`outreach-group-${g.key}`}>
                          <div
                            onClick={() => toggleGroup(g.key)}
                            role="button"
                            tabIndex={0}
                            className="w-full grid grid-cols-[1fr_0.7fr_1fr_100px_200px_60px] px-4 py-3 items-center gap-2 hover:bg-slate-50/50 text-left cursor-pointer"
                            data-testid={`outreach-group-toggle-${g.key}`}
                          >
                            <p className="text-xs font-medium text-slate-900">{g.company}</p>
                            <p className="text-xs text-slate-500">{g.country || '—'}</p>
                            <div>
                              {g.asset && g.asset !== '—' ? (
                                <Badge variant="secondary" className="text-[9px] bg-violet-50 text-violet-700 whitespace-normal">{g.asset}</Badge>
                              ) : (
                                <span className="text-[10px] text-slate-300">—</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 tabular-nums">{g.rows.length}</p>
                            <div onClick={e => e.stopPropagation()} title="Set status for ALL contacts in this group">
                              <Select value="" onValueChange={(v) => { setBulkStatusNote(''); setBulkStatusDialog({ open: true, group: g, status: v }); }}>
                                <SelectTrigger className="h-7 text-xs border-dashed" data-testid={`bulk-status-select-${g.key}`}>
                                  <Badge className={`text-[10px] ${OUTREACH_COLORS[latest?.status] || 'bg-slate-100 text-slate-600'}`}>{latest?.status || '—'}</Badge>
                                </SelectTrigger>
                                <SelectContent>
                                  {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <span className="text-[10px] text-slate-400 justify-self-end">{expanded ? '▾' : '▸'}</span>
                          </div>
                          {expanded && (
                            <div className="bg-slate-50/40 border-t border-slate-100">
                              <div className="grid grid-cols-[1fr_1fr_150px_0.6fr_0.8fr_70px] px-4 py-2 text-[9px] font-semibold text-slate-400 uppercase tracking-wider">
                                <span>Contact</span>
                                <span>Email</span>
                                <span>Status</span>
                                <span>Date</span>
                                <span>Notes</span>
                                <span></span>
                              </div>
                              {g.rows.map(o => (
                                <div key={o.id} className="grid grid-cols-[1fr_1fr_150px_0.6fr_0.8fr_70px] px-4 py-2.5 items-center gap-2 border-t border-slate-100" data-testid={`outreach-row-${o.id}`}>
                                  <div>
                                    <p className="text-xs font-medium text-slate-900">{o.contact_name}</p>
                                    {o.designation && <p className="text-[10px] text-slate-400">{o.designation}</p>}
                                  </div>
                                  <p className="text-xs text-slate-500 flex items-center gap-1 break-all">{o.contact_email ? <><CopyEmail email={o.contact_email} />{o.contact_email}</> : '—'}</p>
                                  <Select value={o.status} onValueChange={(v) => updateOutreachStatus(o.id, v)}>
                                    <SelectTrigger className="h-7 text-xs" data-testid={`status-select-${o.id}`}>
                                      <Badge className={`text-[10px] ${OUTREACH_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>{o.status}</Badge>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-[11px] text-slate-500 whitespace-nowrap" title={o.created_at ? new Date(o.created_at).toLocaleString() : ''}>
                                    {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                  </p>
                                  <p className={`text-[10px] italic leading-tight ${o.status === 'Declined' ? 'text-rose-500' : o.status === 'Ghosted' ? 'text-stone-500' : o.status === 'Awaiting Trial Data' ? 'text-orange-500' : 'text-slate-500'}`}>{o.status_notes || '—'}</p>
                                  <div className="flex items-center gap-0.5">
                                    {(o.email_body || o.email_subject) && (
                                      <Button variant="ghost" size="sm" onClick={() => setViewEmailRecord(o)} title="View sent email copy" className="h-7 w-7 p-0 text-slate-400 hover:text-violet-600 hover:bg-violet-50" data-testid={`view-email-outreach-${o.id}`}>
                                        <Mail className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => handleEditOutreach(o)} className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600 hover:bg-sky-50" data-testid={`edit-outreach-${o.id}`}>
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(o)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-outreach-${o.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_0.7fr_0.5fr_0.7fr_1fr_150px_0.6fr_0.8fr_70px] px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Country</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Asset</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Email</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Notes</span>
                <span></span>
              </div>
              <div className="divide-y divide-slate-50 max-h-[500px] overflow-y-auto">
                {filtered.map((o) => (
                  <div key={o.id} className="grid grid-cols-[1fr_0.7fr_0.5fr_0.7fr_1fr_150px_0.6fr_0.8fr_70px] px-4 py-3 items-center gap-2 hover:bg-slate-50/50" data-testid={`outreach-row-${o.id}`}>
                    <div>
                      <p className="text-xs font-medium text-slate-900">{o.contact_name}</p>
                      {o.designation && <p className="text-[10px] text-slate-400">{o.designation}</p>}
                    </div>
                    <p className="text-xs text-slate-700">{o.company_name}</p>
                    <p className="text-xs text-slate-500">{o.country || '—'}</p>
                    <div>
                      {o.asset_name ? (
                        <Badge variant="secondary" className="text-[9px] bg-violet-50 text-violet-700 whitespace-normal">{o.asset_name_canonical || o.asset_name}</Badge>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1 break-all">{o.contact_email ? <><CopyEmail email={o.contact_email} />{o.contact_email}</> : '—'}</p>
                    <Select value={o.status} onValueChange={(v) => updateOutreachStatus(o.id, v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`status-select-${o.id}`}>
                        <Badge className={`text-[10px] ${OUTREACH_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>{o.status}</Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`outreach-date-${o.id}`} title={o.created_at ? new Date(o.created_at).toLocaleString() : ''}>
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </p>
                    <p className={`text-[10px] italic leading-tight ${o.status === 'Declined' ? 'text-rose-500' : o.status === 'Ghosted' ? 'text-stone-500' : o.status === 'Awaiting Trial Data' ? 'text-orange-500' : 'text-slate-500'}`}>{o.status_notes || '—'}</p>
                    <div className="flex items-center gap-0.5">
                      {(o.email_body || o.email_subject) && (
                        <Button variant="ghost" size="sm" onClick={() => setViewEmailRecord(o)} title="View sent email copy" className="h-7 w-7 p-0 text-slate-400 hover:text-violet-600 hover:bg-violet-50" data-testid={`view-email-outreach-${o.id}`}>
                          <Mail className="w-3 h-3" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEditOutreach(o)} className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600 hover:bg-sky-50" data-testid={`edit-outreach-${o.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(o)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-outreach-${o.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })()}
        </CardContent>
      </Card>

      {viewEmailRecord && <SentEmailDialog record={viewEmailRecord} onClose={() => setViewEmailRecord(null)} />}

      {/* Delete Outreach Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Remove Outreach Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the outreach entry for <span className="font-semibold text-slate-700">{deleteTarget?.contact_name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteOutreach(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Reason Dialog */}
      <Dialog open={statusReasonDialog.open} onOpenChange={(open) => { if (!open) { setStatusReasonDialog({ open: false, outreachId: null, status: '' }); setStatusReason(''); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {statusReasonDialog.status} — Add a Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-500">Any notes for this status change? (optional)</p>
            <Textarea
              value={statusReason}
              onChange={e => setStatusReason(e.target.value)}
              rows={3}
              placeholder="Add a note..."
              data-testid="input-status-reason"
              autoFocus
            />
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => { setStatusReasonDialog({ open: false, outreachId: null, status: '' }); setStatusReason(''); }}>Cancel</Button>
              <Button onClick={submitStatusReason} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-status-reason-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Group Status Dialog */}
      <Dialog open={bulkStatusDialog.open} onOpenChange={(open) => { if (!open) { setBulkStatusDialog({ open: false, group: null, status: '' }); setBulkStatusNote(''); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bulk Update — {bulkStatusDialog.status}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">
              Apply <Badge className={`text-[10px] ${OUTREACH_COLORS[bulkStatusDialog.status] || 'bg-slate-100 text-slate-600'}`}>{bulkStatusDialog.status}</Badge> to all{' '}
              <strong>{bulkStatusDialog.group?.rows?.length || 0}</strong> contact{(bulkStatusDialog.group?.rows?.length || 0) === 1 ? '' : 's'} at{' '}
              <strong>{bulkStatusDialog.group?.company}</strong>
              {bulkStatusDialog.group?.asset && bulkStatusDialog.group.asset !== '—' && <> for <strong>{bulkStatusDialog.group.asset}</strong></>}?
            </p>
            <div className="max-h-32 overflow-y-auto rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2 space-y-1" data-testid="bulk-status-contact-list">
              {(bulkStatusDialog.group?.rows || []).map(o => (
                <p key={o.id} className="text-[11px] text-slate-600">
                  {o.contact_name || o.contact_email || '—'}
                  <span className="text-slate-400"> · currently {o.status || '—'}</span>
                </p>
              ))}
            </div>
            <Textarea
              value={bulkStatusNote}
              onChange={e => setBulkStatusNote(e.target.value)}
              rows={3}
              placeholder="Optional note (applied to every contact)..."
              data-testid="input-bulk-status-note"
              autoFocus
            />
            <p className="text-[10px] text-slate-400">This overwrites each contact's current status. An audit note is stamped on every record.</p>
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => { setBulkStatusDialog({ open: false, group: null, status: '' }); setBulkStatusNote(''); }}>Cancel</Button>
              <Button onClick={applyBulkStatus} disabled={bulkStatusApplying} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-bulk-status-btn">
                {bulkStatusApplying ? 'Applying...' : `Apply to ${bulkStatusDialog.group?.rows?.length || 0} contact${(bulkStatusDialog.group?.rows?.length || 0) === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aug 2026 · Recipient-Rule gate dialog — shown when send-intro is
          blocked because the pharma has a routing rule for this asset's TA */}
      <Dialog open={!!introRoutingBlock} onOpenChange={(o) => { if (!o) setIntroRoutingBlock(null); }}>
        <DialogContent className="max-w-lg" data-testid="routing-block-dialog">
          {introRoutingBlock?.policy === 'company_stage_gate' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <ShieldAlert className="w-4 h-4" /> Stage Gate — {introRoutingBlock?.contact_company}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3" data-testid="stage-gate-block">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">{introRoutingBlock?.contact_company}</span> only in-licenses <span className="font-semibold">{introRoutingBlock?.company_min_stage}+</span> assets. This is a hard policy — it cannot be overridden from here.
                </p>
                <div className="rounded-md border border-rose-200 bg-rose-50/50 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 mb-1">Below the gate</p>
                  {(introRoutingBlock?.blocked_assets || []).map((b, i) => (
                    <p key={i} className="text-xs text-slate-700">{b.asset_name || b.id} — <span className="text-rose-600">{b.stage || 'stage unknown'}</span></p>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Untick these assets in Step 2 to introduce the rest, or lift the gate on the {introRoutingBlock?.contact_company} Company Profile if their policy has changed.</p>
                <div className="flex justify-end pt-1">
                  <Button onClick={() => setIntroRoutingBlock(null)} data-testid="stage-gate-close">Back to intro</Button>
                </div>
              </div>
            </>
          ) : (
          <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ShieldAlert className="w-4 h-4" /> Recipient Rule — {introRoutingBlock?.pharma_company}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{introRoutingBlock?.message}</p>
            {(introRoutingBlock?.blocked || []).length > 0 && (
              <div className="rounded-md border border-rose-200 bg-rose-50/50 px-3 py-2 max-h-40 overflow-y-auto">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 mb-1">Blocked — declared a different scope</p>
                {(introRoutingBlock?.blocked || []).map((b, i) => (
                  <p key={i} className="text-xs text-slate-700">{b.name ? `${b.name} — ` : ''}{b.email}
                    {b.reason && <span className="text-[10px] text-rose-500 block ml-2">{b.reason}</span>}
                  </p>
                ))}
              </div>
            )}
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-2 max-h-40 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">Will receive the email</p>
              {(introRoutingBlock?.allowed || introRoutingBlock?.rule_recipients || []).map((r, i) => (
                <p key={i} className="text-xs text-slate-700">{r.name ? `${r.name} — ` : ''}{r.email}</p>
              ))}
              {((introRoutingBlock?.allowed || introRoutingBlock?.rule_recipients || []).length === 0) && (
                <p className="text-xs text-slate-400">No allowed recipients — add the rule address or override.</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIntroRoutingBlock(null)}>Cancel</Button>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                disabled={introSending}
                onClick={() => { setIntroRoutingBlock(null); handleSendIntro({ override: true, recipients: null }); }}
                data-testid="routing-override-btn"
              >
                Override & send anyway
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={introSending || ((introRoutingBlock?.allowed || introRoutingBlock?.rule_recipients || []).length === 0)}
                onClick={() => {
                  const src = (introRoutingBlock?.allowed?.length ? introRoutingBlock.allowed : introRoutingBlock?.rule_recipients) || [];
                  const reps = src.map(r => ({ name: r.name || '', email: r.email, id: '' }));
                  setIntroRoutingBlock(null);
                  handleSendIntro({ recipients: reps });
                }}
                data-testid="routing-use-required-btn"
              >
                Send to allowed recipient(s) ({(introRoutingBlock?.allowed || introRoutingBlock?.rule_recipients || []).length})
              </Button>
            </div>
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Outreach Dialog */}
      <Dialog open={outreachDialogOpen} onOpenChange={(open) => { if (!open) { setOutreachDialogOpen(false); setEditingOutreachId(null); setOutreachForm({ ...emptyOutreachForm }); setOutreachSelectedIds([]); setOutreachSelectedAssetIds([]); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingOutreachId ? 'Edit Outreach' : 'Add Outreach'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOutreachSubmit} className="space-y-4 mt-2">
            {/* Mode toggle — hide when editing */}
            {!editingOutreachId && (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={outreachMode === 'existing' ? 'default' : 'outline'} onClick={() => setOutreachMode('existing')} className="text-xs">From Existing Contact</Button>
              <Button type="button" size="sm" variant={outreachMode === 'manual' ? 'default' : 'outline'} onClick={() => setOutreachMode('manual')} className="text-xs">Manual Entry</Button>
            </div>
            )}

            {outreachMode === 'existing' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Contact Type</Label>
                  <Select value={outreachForm.contact_type} onValueChange={v => { setOutreachForm(p => ({ ...p, contact_type: v, contact_name: '', contact_email: '', company_name: '', country: '', designation: '', source_id: '' })); setOutreachSelectedIds([]); setContactSearchTerm(''); setContactDropdownOpen(false); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pharma">Pharma</SelectItem>
                      <SelectItem value="Biotech">Biotech (In-licenser)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Contact</Label>
                  <div className="relative" data-testid="select-existing-contact">
                    <div className="flex items-center border border-slate-200 rounded-md h-9 px-3 gap-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
                      <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <input
                        ref={contactSearchInputRef}
                        type="text"
                        placeholder="Type to search by name, company, or email..."
                        value={contactSearchTerm}
                        onChange={e => { setContactSearchTerm(e.target.value); setContactDropdownOpen(true); }}
                        onFocus={() => setContactDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setContactDropdownOpen(false), 250)}
                        className="flex-1 text-sm outline-none bg-transparent placeholder:text-slate-400"
                        data-testid="contact-search-input"
                      />
                      {outreachForm.source_id && (
                        <button type="button" onClick={() => {
                          setOutreachForm(p => ({ ...p, contact_name: '', contact_email: '', company_name: '', country: '', designation: '', source_id: '' }));
                          setContactSearchTerm('');
                        }} className="text-slate-400 hover:text-slate-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {outreachForm.source_id && !contactDropdownOpen && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {outreachForm.contact_name}{outreachForm.company_name ? ` · ${outreachForm.company_name}` : ''}
                      </p>
                    )}
                    {contactDropdownOpen && (() => {
                      const pool = contactPoolFor(outreachForm.contact_type);
                      const allContacts = outreachForm.contact_type === 'Investor'
                        ? [...pool].sort((a, b) => (a.firm_name || a.name || '').localeCompare(b.firm_name || b.name || ''))
                        : [...pool].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                      if (allContacts.length === 0) {
                        return (
                          <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-4 text-center">
                            <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto mb-1" />
                            <p className="text-xs text-slate-400">Loading contacts...</p>
                          </div>
                        );
                      }
                      const term = contactSearchTerm.toLowerCase().trim();
                      // Sep 2026 (Auzone: typed hsw@auzonebio.com, stored hsw@szauzone.com):
                      // exact substring first; if nothing matches, fall back to token
                      // matching (mailbox part, name fragments, company) so a slightly
                      // different domain still finds the person.
                      const _hay = (c) => [c.name, c.firm_name, c.company_name, c.contact_person, c.email, c.designation].map(v => (v || '').toLowerCase()).join(' | ');
                      const _tokens = term.split(/[\s@.,;:/]+/).filter(t => t.length >= 2 && !['com', 'net', 'org', 'cn', 'co', 'io', 'www', 'bio', 'inc', 'ltd'].includes(t));
                      let partialMatch = false;
                      let filtered = term ? allContacts.filter(c => _hay(c).includes(term)) : allContacts.slice(0, 200);
                      if (term && filtered.length === 0 && _tokens.length) {
                        filtered = allContacts.filter(c => { const h = _hay(c); return _tokens.every(t => h.includes(t)); });
                        if (filtered.length === 0) filtered = allContacts.filter(c => { const h = _hay(c); return _tokens.some(t => h.includes(t)); });
                        partialMatch = filtered.length > 0;
                      }
                      return (
                        <div
                          className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[250px] overflow-y-auto"
                          style={{ top: '100%' }}
                          onMouseDown={e => e.preventDefault()}
                        >
                          {term && (
                            <p className="text-[10px] text-slate-400 px-3 py-1.5 border-b border-slate-100 bg-slate-50 rounded-t-lg">
                              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{contactSearchTerm}"{partialMatch ? ' · no exact match — showing close matches (name / mailbox / company)' : ''}
                            </p>
                          )}
                          {!term && (
                            <p className="text-[10px] text-slate-400 px-3 py-1.5 border-b border-slate-100 bg-slate-50 rounded-t-lg">
                              Showing first 50 · type to search {allContacts.length} contacts
                            </p>
                          )}
                          <div className="max-h-[200px] overflow-y-auto">
                            {filtered.length === 0 ? (
                              <p className="py-4 text-center text-sm text-slate-400">No contacts found for "{contactSearchTerm}"</p>
                            ) : (
                              filtered.slice(0, 200).map(c => {
                                const isSelected = outreachSelectedIds.includes(c.id);
                                return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    toggleExistingContact(c.id, outreachForm.contact_type);
                                    // Keep dropdown open so the user can pick more
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center gap-2 transition-colors ${isSelected ? 'bg-sky-50' : ''}`}
                                  data-testid={`contact-option-${c.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    readOnly
                                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 shrink-0 pointer-events-none"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-slate-800 truncate">
                                      {outreachForm.contact_type === 'Investor' ? (c.firm_name || c.name) : c.name}
                                    </p>
                                    <p className="text-xs text-slate-400 truncate">
                                      {outreachForm.contact_type === 'Pharma'
                                        ? `${c.company_name || ''} ${c.email ? '· ' + c.email : ''}`
                                        : `${c.contact_person ? c.contact_person + ' · ' : ''}${c.email || ''}`
                                      }
                                    </p>
                                  </div>
                                </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {outreachSelectedIds.length > 0 && (
                    <div className="bg-sky-50 rounded-md p-3 border border-sky-100 space-y-1.5">
                      <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide">{outreachSelectedIds.length} contact{outreachSelectedIds.length > 1 ? 's' : ''} selected</p>
                      <div className="flex flex-wrap gap-1.5">
                        {outreachSelectedIds.map(cid => {
                          const list = contactPoolFor(outreachForm.contact_type);
                          const c = list.find(x => x.id === cid);
                          if (!c) return null;
                          const display = outreachForm.contact_type !== 'Investor'
                            ? (c.name || c.email)
                            : (c.contact_person || c.firm_name || c.name);
                          return (
                            <span key={cid} className="inline-flex items-center gap-1 bg-white border border-sky-200 rounded-full px-2 py-0.5 text-[11px] text-sky-700">
                              {display}
                              <button
                                type="button"
                                className="hover:text-rose-600"
                                onClick={() => toggleExistingContact(cid, outreachForm.contact_type)}
                                aria-label={`Remove ${display}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {outreachMode === 'manual' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input value={outreachForm.contact_name} onChange={e => setOutreachForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Name" required />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={outreachForm.contact_email} onChange={e => setOutreachForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="Email" />
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <CompanySelect
                    value={outreachForm.company_name}
                    onChange={v => setOutreachForm(p => ({ ...p, company_name: v }))}
                    onPickRich={({ name, country }) => setOutreachForm(p => ({
                      ...p,
                      company_name: name,
                      // Only auto-fill country if (a) the picked record carries one and
                      // (b) the user hasn't already set their own — never overwrite.
                      country: (country && !p.country) ? country : p.country,
                    }))}
                    placeholder="Search or type company..."
                    testId="outreach-company-select"
                  />
                </div>
                <div className="space-y-2 relative">
                  <Label>Country</Label>
                  <CountrySelect value={outreachForm.country} onChange={v => setOutreachForm(p => ({ ...p, country: v }))} testId="outreach-country-select" />
                </div>
                <div className="space-y-2">
                  <Label>Contact Type</Label>
                  <Select value={outreachForm.contact_type} onValueChange={v => setOutreachForm(p => ({ ...p, contact_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pharma">Pharma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={outreachForm.designation} onChange={e => setOutreachForm(p => ({ ...p, designation: e.target.value }))} placeholder="Designation" />
                </div>
              </div>
            )}

            {/* Asset Selector — always show when assets exist. Multi-select:
                pick one OR many assets to tag the outreach against. Each
                selected asset will create its own outreach row on submit so
                you can track the pipeline status per asset independently. */}
            {assets.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Linked Asset(s) / Molecule(s) <span className="text-[10px] text-slate-400 font-normal">(pick one or many)</span></Label>
                  {outreachSelectedAssetIds.length > 0 && (
                    <button type="button" className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                      onClick={() => setOutreachSelectedAssetIds([])}
                      data-testid="outreach-assets-clear">Clear all</button>
                  )}
                </div>
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100 bg-white" data-testid="outreach-asset-multi-select">
                  {assets.map(a => {
                    const checked = outreachSelectedAssetIds.includes(a.id);
                    return (
                      <label key={a.id}
                        className={`flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-sky-50 ${checked ? 'bg-sky-50/60' : ''}`}
                        data-testid={`outreach-asset-option-${a.id}`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setOutreachSelectedAssetIds(prev =>
                            checked ? prev.filter(x => x !== a.id) : [...prev, a.id]
                          )}
                          className="mt-0.5 shrink-0"
                          data-testid={`outreach-asset-checkbox-${a.id}`} />
                        <span className="flex-1 min-w-0">
                          <span className="block text-slate-800 truncate">{a.asset_name}</span>
                          {a.stage && <span className="block text-[10px] text-slate-400">{a.stage}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500">
                  {outreachSelectedAssetIds.length === 0
                    ? 'No asset selected — a single outreach row will be created with no specific asset tag.'
                    : outreachSelectedAssetIds.length === 1
                      ? '1 asset selected — 1 outreach row will be created.'
                      : `${outreachSelectedAssetIds.length} assets selected — ${outreachSelectedAssetIds.length} outreach row${outreachSelectedAssetIds.length > 1 ? 's' : ''} will be created (one per asset).`}
                </p>
              </div>
            )}

            {!editingOutreachId && (() => {
              const slots = outreachSelectedAssetIds.length > 0 ? outreachSelectedAssetIds : [''];
              const people = outreachMode === 'existing'
                ? outreachSelectedIds.map(cid => contactPoolFor(outreachForm.contact_type).find(x => x.id === cid)).filter(Boolean)
                : (outreachForm.contact_name || outreachForm.contact_email ? [outreachForm] : []);
              const dups = [];
              people.forEach(p => slots.forEach(aid => { const d = findDuplicateOutreach(p, aid); if (d) dups.push(d); }));
              if (!dups.length) return null;
              const introOnly = dups.filter(d => d.status === 'Introduction Made' && outreachForm.status !== 'Introduction Made');
              const blocked = dups.filter(d => !introOnly.includes(d));
              return (
                <div className={`rounded-md border px-3 py-2 space-y-1.5 ${blocked.length ? 'border-amber-300 bg-amber-50' : 'border-sky-200 bg-sky-50'}`} data-testid="outreach-duplicate-warning">
                  {blocked.length > 0 && (
                    <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {blocked.length === 1 ? 'This outreach already exists' : `${blocked.length} of these outreach rows already exist`} — duplicates will be blocked.
                    </p>
                  )}
                  {introOnly.length > 0 && (
                    <p className="text-xs font-medium text-sky-800 flex items-center gap-1.5" data-testid="outreach-duplicate-upgrade-note">
                      <Check className="w-3.5 h-3.5" />
                      An intro email was already sent to {introOnly.length === 1 ? 'this contact' : `${introOnly.length} of these contacts`} — saving will update that intro row to "{outreachForm.status}" instead of creating a duplicate.
                    </p>
                  )}
                  {dups.map(d => (
                    <div key={d.id} className="flex items-center justify-between gap-2 text-[11px] text-amber-900" data-testid={`outreach-duplicate-${d.id}`}>
                      <span className="truncate">{d.contact_name || d.contact_email}{d.asset_name ? ` · ${d.asset_name}` : ''} · {d.status}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ''}</span>
                      {outreach.some(o => o.id === d.id)
                        ? <button type="button" onClick={() => jumpToOutreachRow(d)} className="shrink-0 text-sky-700 hover:underline font-medium" data-testid={`outreach-duplicate-jump-${d.id}`}>View existing</button>
                        : <span className="shrink-0 text-amber-700/70 italic" data-testid={`outreach-duplicate-hidden-${d.id}`}>bulk intro · hidden from tracker</span>}
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Status</Label>
              {/* Aug 2026 (v2) · SelectItem clicks inside a Dialog not
                  committing the value was a React 19 × Radix interaction
                  bug. Fixed properly by upgrading all @radix-ui/* packages
                  to aligned latest versions + pointer-events-auto/z-[60]
                  hardening in ui/select.jsx. (The earlier modal={false}
                  prop on <Select> was a no-op — Select.Root has no such
                  prop — and was removed.) */}
              <Select value={outreachForm.status} onValueChange={v => setOutreachForm(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status Note <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea value={outreachForm.status_notes || ''} onChange={e => setOutreachForm(p => ({ ...p, status_notes: e.target.value }))} rows={2} placeholder="Add a note..." />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOutreachDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-slate-900 text-white hover:bg-slate-800"
                disabled={!editingOutreachId && outreachMode === 'existing' ? outreachSelectedIds.length === 0 : !outreachForm.contact_name}
                data-testid="submit-outreach-btn"
              >
                {editingOutreachId
                  ? 'Save Changes'
                  : (outreachMode === 'existing' && outreachSelectedIds.length > 1
                      ? `Add ${outreachSelectedIds.length} Outreach`
                      : 'Add Outreach')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Introduction Dialog */}
      <Dialog open={introOpen} onOpenChange={setIntroOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="intro-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {introStep === 1 && 'Send Introduction'}
              {introStep === 2 && `Select Contacts at ${introPharma}`}
              {introStep === 3 && 'Preview & Send'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {introStep === 1 && (introAsset
                ? `Introduce ${introAsset.asset_name} (${company?.company_name}) to a pharma company`
                : `Introduce ${company?.company_name} to a pharma company`)}
              {introStep === 2 && 'Choose which contacts should receive the introduction email'}
              {introStep === 3 && 'Review the AI-drafted email, edit if needed, then send via Gmail'}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Pick pharma company */}
          {introStep === 1 && (
            <div className="space-y-3 py-2">
              <Label className="text-xs font-medium text-slate-600">Search Pharma Company</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={introSearch} onChange={e => handleIntroSearch(e.target.value)}
                  placeholder="Type company or person name..." className="pl-8 h-9 text-sm" autoFocus data-testid="intro-search-pharma" />
                {introSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
              {introSearchResults.length > 0 && (
                <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto">
                  {introSearchResults.map((r, i) => (
                    <button key={i} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-0"
                      onClick={() => selectIntroPharma(r.company, r.contact_name || '')} data-testid={`intro-pharma-${i}`}>
                      {r.contact_name ? <User className="w-3.5 h-3.5 text-violet-500 shrink-0" /> : <Building2 className="w-3.5 h-3.5 text-[#00709B] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        {r.contact_name ? (
                          <span className="truncate"><span className="font-medium">{r.contact_name}</span> <span className="text-slate-400">— {r.company}</span></span>
                        ) : (
                          <span className="font-medium truncate">{r.company}</span>
                        )}
                      </div>
                      {r.country && <span className="text-[10px] text-slate-400 shrink-0">{r.country}</span>}
                    </button>
                  ))}
                </div>
              )}
              {/* Feedback row: error, empty-state, or manual fallback */}
              {introSearchError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700" data-testid="intro-search-error">
                  {introSearchError}
                </div>
              )}
              {!introSearchError && !introSearching && introSearchAttempted && introSearchResults.length === 0 && introSearch.trim().length >= 2 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-600 space-y-2" data-testid="intro-search-empty">
                  <div>
                    No CRM match for <span className="font-medium text-slate-800">"{introSearch}"</span>. The pharma may not be in your DB yet.
                  </div>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => selectIntroPharma(introSearch.trim(), '')} data-testid="intro-use-as-typed">
                    Use "{introSearch.trim()}" as the pharma name
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select assets + contacts */}
          {introStep === 2 && (
            <div className="space-y-3 py-2">
              {introLoadingContacts ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  {/* Asset selection */}
                  {(assets || []).filter(a => !a.is_hidden).length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="text-xs font-medium text-slate-600">Select Assets to Highlight</Label>
                        <span className="text-[10px] text-slate-400">{Object.values(introSelectedAssetIds).filter(Boolean).length} selected</span>
                      </div>
                      <div className="border border-slate-200 rounded-lg max-h-36 overflow-y-auto divide-y divide-slate-50">
                        {(assets || []).filter(a => !a.is_hidden).map(a => (
                          <label key={a.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`intro-asset-${a.id}`}>
                            <Checkbox checked={!!introSelectedAssetIds[a.id]}
                              onCheckedChange={v => setIntroSelectedAssetIds(prev => ({ ...prev, [a.id]: v }))} />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-800">{a.asset_name}</span>
                              {a.therapeutic_area && <span className="text-xs text-slate-400 ml-2">{a.therapeutic_area}</span>}
                            </div>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{a.stage || '—'}</Badge>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact selection */}
                  {introContacts.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-slate-500">No contacts with email found at {introPharma}</p>
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => setIntroStep(1)}>Pick another company</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium text-slate-600">
                          Select Contacts ({Object.values(introSelected).filter(Boolean).length} of {introContacts.filter(c => c.email).length})
                          {introAlreadySent.size > 0 && (
                            <span className="text-[10px] text-emerald-600 ml-2 font-normal">
                              {introContacts.filter(c => c.email && introAlreadySent.has(c.email.toLowerCase())).length} already emailed
                            </span>
                          )}
                        </Label>
                        <Button variant="ghost" size="sm" className="text-xs h-6 px-2"
                          onClick={() => {
                            const unsent = introContacts.filter(c => c.email && !introAlreadySent.has((c.email || '').toLowerCase()));
                            const allUnsentSelected = unsent.every((c) => {
                              const idx = introContacts.indexOf(c);
                              return introSelected[idx];
                            });
                            const sel = {};
                            introContacts.forEach((c, i) => {
                              if (c.email && !introAlreadySent.has((c.email || '').toLowerCase())) {
                                sel[i] = !allUnsentSelected;
                              }
                            });
                            setIntroSelected(sel);
                          }}>
                          Select All Unsent
                        </Button>
                      </div>
                      <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-50">
                        {introContacts.map((c, i) => {
                          const alreadySent = c.email && introAlreadySent.has(c.email.toLowerCase());
                          const stale = !!c.stale_company_match;
                          return (
                          <label key={i} className={`flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer ${!c.email ? 'opacity-40' : ''} ${alreadySent ? 'bg-emerald-50/50' : ''} ${stale ? 'bg-amber-50/40' : ''}`}>
                            <input type="checkbox" checked={!!introSelected[i]} disabled={!c.email}
                              onChange={() => setIntroSelected(prev => ({ ...prev, [i]: !prev[i] }))}
                              className="rounded border-slate-300" data-testid={`intro-contact-${i}`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 truncate">
                                {c.name}
                                {alreadySent && <span className="ml-2 text-[10px] font-medium text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">Sent</span>}
                                {stale && <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" title={c.note}>Different domain</span>}
                              </p>
                              <p className="text-[10px] text-slate-400 truncate">{c.email || 'No email'}{c.designation ? ` · ${c.designation}` : ''}</p>
                              {stale && c.note && (
                                <p className="text-[10px] text-amber-700 mt-0.5 truncate" data-testid={`intro-contact-warning-${i}`}>{c.note}</p>
                              )}
                            </div>
                          </label>
                          );
                        })}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => setIntroStep(1)} className="h-8 text-xs">Back</Button>
                        <Button size="sm" onClick={handleDraftIntro} disabled={introDrafting || Object.values(introSelected).filter(Boolean).length === 0}
                          className="h-8 text-xs bg-[#0A1128] text-white hover:bg-slate-800 ml-auto" data-testid="intro-draft-btn">
                          {introDrafting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Drafting...</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Draft Email</>}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Preview & Send */}
          {introStep === 3 && (
            <div className="space-y-3 py-2">
              {/* Outreach Intention / Partnering Objective editor.
                  Feb 2026 · pre-filled from asset.deal_interests +
                  asset.objectives; user can edit or add custom notes
                  ("Position as US regional licensing", "Do not mention
                  fundraising", etc.) then click Regenerate to re-draft
                  the email with this exact intent. */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="intro-intent-box">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <Label className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Outreach Intention / Partnering Objective
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={regenerateIntroWithIntent}
                    disabled={introRegenerating}
                    data-testid="intro-regenerate-with-intent"
                  >
                    {introRegenerating
                      ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Regenerating…</>
                      : <><Sparkles className="w-3 h-3 mr-1" /> Regenerate email with this intent</>}
                  </Button>
                </div>
                <Textarea
                  value={introIntent}
                  onChange={(e) => setIntroIntent(e.target.value)}
                  className="text-xs min-h-[70px] bg-white/70 border-amber-200 focus-visible:ring-amber-400"
                  placeholder="e.g. Position this as a U.S. regional licensing opportunity. Focus on commercialization partnerships. Do not mention fundraising."
                  data-testid="intro-intent-textarea"
                />
                <p className="text-[10px] text-amber-700/80 mt-1.5">
                  Pre-filled from this biotech&apos;s deal interests and asset objectives. Edit to
                  add custom framing (e.g. &quot;US-only regional licensing&quot;, &quot;co-development&quot;) before
                  regenerating. The email answers: (1) why this asset may fit; (2) what the biotech
                  is seeking; (3) the next step.
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Subject</Label>
                <Input value={introSubject} onChange={e => setIntroSubject(e.target.value)} className="mt-1 h-9 text-sm" data-testid="intro-subject" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Body</Label>
                <Textarea value={introBody} onChange={e => setIntroBody(e.target.value)}
                  className="mt-1 text-sm min-h-[200px] font-serif leading-relaxed" data-testid="intro-body" />
              </div>
              <p className="text-[10px] text-slate-400">
                Sending to {Object.values(introSelected).filter(Boolean).length} contact(s) at {introPharma}. "Dear [Name]," will be personalized per recipient.
              </p>
              {introAlreadySent.size > 0 && (() => {
                const selectedAlreadySent = introContacts.filter((c, i) => introSelected[i] && c.email && introAlreadySent.has(c.email.toLowerCase()));
                if (selectedAlreadySent.length === 0) return null;
                const _label = (c) => {
                  const names = (introSentAssets[c.email.toLowerCase()]?.asset_names || []).filter(Boolean);
                  return `${c.name || c.email}${names.length ? ` (${names.slice(0, 2).join(', ')}${names.length > 2 ? '…' : ''})` : ''}`;
                };
                return (
                  <div className={`rounded-md border px-3 py-2 text-[11px] ${introForceResend ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="intro-already-sent-box">
                    <p className={introForceResend ? 'text-amber-800' : 'text-slate-600'}>
                      {selectedAlreadySent.length} selected contact(s) already received an intro for this asset — {introForceResend ? 'they WILL be emailed again.' : 'the server will skip them and nothing goes out to them.'}
                    </p>
                    <p className="text-slate-500 mt-0.5 truncate">{selectedAlreadySent.map(_label).join(' · ')}</p>
                    <label className="flex items-center gap-2 mt-1.5 cursor-pointer text-slate-700 font-medium">
                      <input type="checkbox" checked={introForceResend} onChange={e => setIntroForceResend(e.target.checked)} data-testid="intro-force-resend" />
                      Re-send to already-emailed contacts
                    </label>
                  </div>
                );
              })()}
              {/* Final-confirmation recipient banner — shows the EXACT emails that will receive
                  this introduction. Caught a real cross-domain leak (Sun-hawk → Medis) where stale
                  CRM tagging slipped @medicinepharma.com contacts into the recipient list. Always
                  visible at step 3 so the user can never miss it. */}
              {(() => {
                const finalRecipients = introContacts.filter((c, i) => introSelected[i] && c.email);
                if (finalRecipients.length === 0) return null;
                const offDomain = finalRecipients.filter(c => c.stale_company_match);
                return (
                  <div className={`rounded-lg border p-2.5 ${offDomain.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="intro-final-recipients">
                    <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${offDomain.length > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                      {offDomain.length > 0 ? '⚠ Review Recipients' : 'Recipients'} ({finalRecipients.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {finalRecipients.map((c, i) => (
                        <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${c.stale_company_match ? 'bg-amber-200 text-amber-900' : 'bg-white text-slate-700 border border-slate-200'}`} data-testid={`intro-final-recipient-${i}`}>
                          {c.email}
                        </span>
                      ))}
                    </div>
                    {offDomain.length > 0 && (
                      <p className="text-[10px] text-amber-700 mt-1.5">
                        {offDomain.length} recipient(s) have an email domain that doesn't match {introPharma}. Go back to Step 2 to untick if this is unintentional.
                      </p>
                    )}
                  </div>
                );
              })()}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setIntroStep(2)} className="h-8 text-xs">Back</Button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={handleSendTestIntro} disabled={introSendingTest || !introSubject.trim()}
                  className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="intro-test-btn">
                  {introSendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
                  Send Test to Me
                </Button>
                <Button size="sm" onClick={handleSendIntro} disabled={introSending}
                  className="h-8 text-xs bg-[#00709B] text-white hover:bg-[#005a7a]" data-testid="intro-send-btn">
                  {introSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Send className="w-3.5 h-3.5 mr-1" /> Send via Gmail</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Outreach Update Dialog */}
      <Dialog open={outreachUpdateOpen} onOpenChange={setOutreachUpdateOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="outreach-update-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {outreachUpdateStep === 1 ? 'Outreach Status Update' : 'Preview & Send'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {outreachUpdateStep === 1
                ? `Send a status update to ${company?.company_name} about your outreach efforts`
                : 'Review the email, then send or test'}
            </DialogDescription>
          </DialogHeader>

          {outreachUpdateStep === 1 ? (
            <div className="space-y-3 py-2">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-600">Additional Context (optional)</Label>
                  <button onClick={toggleOutreachVoice}
                    className={`p-1.5 rounded-lg transition-colors ${outreachVoiceListening ? 'bg-red-500 text-white animate-pulse' : 'text-slate-400 hover:text-[#00709B] hover:bg-slate-50'}`}
                    title={outreachVoiceListening ? 'Stop recording' : 'Voice input'} data-testid="outreach-voice-btn">
                    {outreachVoiceListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {outreachVoiceListening && outreachUpdateContext && (
                  <div className="mb-1.5 p-2 bg-slate-50 border border-slate-200 rounded-lg max-h-[120px] overflow-y-auto">
                    <p className="text-[10px] text-red-500 font-medium mb-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> Listening...
                    </p>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{outreachUpdateContext.replace(/\u200B/g, '')}</p>
                  </div>
                )}
                <Textarea value={outreachUpdateContext.replace(/\u200B/g, '')} onChange={e => setOutreachUpdateContext(e.target.value)}
                  placeholder="e.g., I met their BD head at BIO JPM, Leo Pharma also showed interest..."
                  className="mt-1 text-sm min-h-[80px] max-h-[200px]" data-testid="outreach-context-input" />
                <p className="text-[10px] text-slate-400 mt-1">The AI will pull all outreach data from the CRM automatically. Add any extra notes here.</p>
              </div>
              <div className="flex justify-end pt-1">
                <Button size="sm" onClick={handleOutreachUpdateDraft} disabled={outreachUpdateDrafting}
                  className="h-8 text-xs bg-[#00709B] text-white hover:bg-[#005a7a]" data-testid="outreach-draft-btn">
                  {outreachUpdateDrafting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Drafting...</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Draft Update</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {/* Select contacts to send to */}
              <div>
                <Label className="text-xs font-medium text-slate-600">Send To</Label>
                <div className="mt-1.5 space-y-1 max-h-28 overflow-y-auto border border-slate-200 rounded-lg p-2">
                  {outreachUpdateContacts.filter(c => c.email).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">No contacts with email found</p>
                  ) : outreachUpdateContacts.filter(c => c.email).map((c, i) => (
                    <label key={i} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox checked={!!outreachUpdateSelectedContacts[i]}
                        onCheckedChange={v => setOutreachUpdateSelectedContacts(prev => ({ ...prev, [i]: v }))} />
                      <span className="text-sm text-slate-700">{c.name || c.email}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{c.email}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label className="text-xs font-medium text-slate-600">Subject</Label>
                <Input value={outreachUpdateSubject} onChange={e => setOutreachUpdateSubject(e.target.value)}
                  className="mt-1 h-9 text-sm" data-testid="outreach-subject" />
              </div>

              {/* Body */}
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Body</Label>
                <Textarea value={outreachUpdateBody} onChange={e => setOutreachUpdateBody(e.target.value)}
                  className="mt-1 text-sm min-h-[200px] font-serif leading-relaxed" data-testid="outreach-body" />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setOutreachUpdateStep(1)} className="h-8 text-xs">Back</Button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={handleOutreachUpdateTestSend} disabled={outreachUpdateTestSending}
                  className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="outreach-test-btn">
                  {outreachUpdateTestSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Mail className="w-3.5 h-3.5 mr-1" /> Test to Me</>}
                </Button>
                <Button size="sm" onClick={handleOutreachUpdateSend} disabled={outreachUpdateSending}
                  className="h-8 text-xs bg-[#00709B] text-white hover:bg-[#005a7a]" data-testid="outreach-send-btn">
                  {outreachUpdateSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Send className="w-3.5 h-3.5 mr-1" /> Send via Gmail</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share NCD Dialog */}
      <Dialog open={!!shareDoc} onOpenChange={v => !v && setShareDoc(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="share-doc-dialog" aria-describedby="share-doc-desc">
          <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Share Document
          </DialogTitle>
          <p id="share-doc-desc" className="text-xs text-slate-500 -mt-2">Send <span className="font-medium text-slate-700">{shareDoc?.original_filename}</span> via Gmail</p>

          <div className="space-y-3 mt-2">
            {/* TO: Recipient with autocomplete */}
            <div>
              <Label className="text-xs">To *</Label>
              {shareToEmail ? (
                <div className="mt-1 flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                  <span className="text-xs text-sky-700 font-medium flex-1">{shareToName} &lt;{shareToEmail}&gt;</span>
                  <button onClick={() => { setShareToEmail(''); setShareToName(''); setShareToSearch(''); setShareMessage(''); }}
                    className="text-sky-400 hover:text-sky-600 cursor-pointer"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Input value={shareToSearch} onChange={e => { setShareToSearch(e.target.value); setShareToDropdown(true); }}
                    onFocus={() => setShareToDropdown(true)} onBlur={() => setTimeout(() => setShareToDropdown(false), 200)}
                    placeholder="Search pharma contact..." className="h-8 text-sm" autoFocus data-testid="share-to-search" />
                  {shareToDropdown && shareToSearch.length > 0 && (() => {
                    const term = shareToSearch.toLowerCase();
                    const matches = pharmaContacts.filter(c =>
                      c.email && (
                        (c.name || '').toLowerCase().includes(term) ||
                        (c.company_name || '').toLowerCase().includes(term) ||
                        (c.email || '').toLowerCase().includes(term)
                      )
                    ).slice(0, 8);
                    if (matches.length === 0) return null;
                    return (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[200px] overflow-y-auto" style={{ top: '100%' }}
                        onMouseDown={e => e.preventDefault()}>
                        {matches.map((c, i) => (
                          <button key={i} className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-50 cursor-pointer"
                            onClick={() => selectShareRecipient(c)}>
                            <p className="text-xs font-medium text-slate-800">{c.name || 'No name'}</p>
                            <p className="text-[10px] text-slate-400">{c.company_name || ''} · {c.email}</p>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={shareSubject} onChange={e => setShareSubject(e.target.value)}
                className="h-8 text-sm mt-1" data-testid="share-subject" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea value={shareMessage} onChange={e => setShareMessage(e.target.value)}
                className="mt-1 w-full text-sm border border-slate-200 rounded-lg p-2.5 min-h-[80px] resize-y focus:outline-none focus:ring-1 focus:ring-sky-300"
                data-testid="share-message" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShareDoc(null)} className="h-8 text-xs">Cancel</Button>
            <Button variant="outline" size="sm" onClick={() => handleShareDoc(true)} disabled={shareSending || !shareToEmail}
              className="h-8 text-xs" data-testid="share-test-btn">
              Test Mail
            </Button>
            <Button size="sm" onClick={() => handleShareDoc(false)} disabled={shareSending || !shareToEmail}
              className="h-8 text-xs bg-[#00709B] text-white hover:bg-[#005a7a]" data-testid="share-send-btn">
              {shareSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Do Not Contact Dialog */}
      <Dialog open={dncDialogOpen} onOpenChange={setDncDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="dnc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Ban className="w-5 h-5" /> {isDnc ? 'Edit Do Not Contact' : 'Mark as Do Not Contact'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Blocks this company from all bulk outreach. Their CRM data stays intact — only outbound email is prevented.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Reason (quote the decline email or add a short note)</Label>
              <Textarea
                value={dncReason}
                onChange={e => setDncReason(e.target.value)}
                placeholder='e.g., "At this point in time we have no plans to further out-license any of our products." — Bryan Roecklein, CBO, Apr 22 2026'
                className="mt-1 text-sm min-h-[100px]"
                data-testid="dnc-reason-input"
              />
            </div>
            {isDnc && dnc.flagged_at && (
              <p className="text-[10px] text-slate-400">Originally flagged {new Date(dnc.flagged_at).toLocaleString()}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {isDnc ? (
              <Button
                variant="outline"
                size="sm"
                onClick={removeDnc}
                disabled={dncSaving}
                className="h-8 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
                data-testid="dnc-remove-btn"
              >
                Remove DNC flag
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDncDialogOpen(false)} className="h-8 text-xs" data-testid="dnc-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                onClick={saveDnc}
                disabled={dncSaving || !dncReason.trim()}
                className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
                data-testid="dnc-save-btn"
              >
                {dncSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving...</> : <><Ban className="w-3.5 h-3.5 mr-1" /> {isDnc ? 'Update' : 'Mark Do Not Contact'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snooze Dialog — temporary outreach pause (auto-expires) */}
      <Dialog open={snoozeDialogOpen} onOpenChange={setSnoozeDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="snooze-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Clock className="w-5 h-5" /> {isSnoozed ? 'Edit Snooze' : 'Snooze Outreach'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pauses all new outreach to this biotech (campaigns, bulk, AI) until the snooze-until date. Auto-clears once the date passes. Use this when a biotech asks you to follow up later (e.g. &quot;contact us in approximately one year&quot;).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Snooze until</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="date"
                  value={snoozeUntil}
                  onChange={e => setSnoozeUntil(e.target.value)}
                  className="h-9 px-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="snooze-until-input"
                />
                <div className="flex gap-1">
                  {[
                    { label: '3mo', months: 3 },
                    { label: '6mo', months: 6 },
                    { label: '1yr', months: 12 },
                  ].map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        const d = new Date();
                        d.setMonth(d.getMonth() + p.months);
                        setSnoozeUntil(d.toISOString().slice(0, 10));
                      }}
                      className="h-7 px-2 text-[11px] rounded-md border border-slate-200 hover:bg-blue-50 hover:border-blue-300 text-slate-600"
                      data-testid={`snooze-preset-${p.label}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Reason (optional — quote the reply)</Label>
              <Textarea
                value={snoozeReason}
                onChange={e => setSnoozeReason(e.target.value)}
                placeholder='e.g., "We will contact you again in approximately one year once we confirm the PCC." — Xuefei Zhou, CEO, Jun 8 2026'
                className="mt-1 text-sm min-h-[80px]"
                data-testid="snooze-reason-input"
              />
            </div>
            {isSnoozed && snoozeRaw.flagged_at && (
              <p className="text-[10px] text-slate-400">Snoozed on {new Date(snoozeRaw.flagged_at).toLocaleString()}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {isSnoozed ? (
              <Button
                variant="outline"
                size="sm"
                onClick={removeSnooze}
                disabled={snoozeSaving}
                className="h-8 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
                data-testid="snooze-remove-btn"
              >
                Unsnooze
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSnoozeDialogOpen(false)} className="h-8 text-xs" data-testid="snooze-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                onClick={saveSnooze}
                disabled={snoozeSaving || !snoozeUntil}
                className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="snooze-save-btn"
              >
                {snoozeSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving...</> : <><Clock className="w-3.5 h-3.5 mr-1" /> {isSnoozed ? 'Update Snooze' : 'Snooze'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Next Outreach Queue dialog */}
      <Dialog open={nextOutreachDialogOpen} onOpenChange={setNextOutreachDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="next-outreach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Flag className="w-5 h-5" /> {isNextOutreach ? 'Edit Next Outreach' : 'Add to Next Outreach'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Surfaces this biotech in the Dashboard <span className="font-medium text-slate-700">"Lined up for next outreach"</span> card so you don't lose track. Different from the broad "Pending Outreach" pool — this is your curated short list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Note <span className="text-slate-400 font-normal">(why are they next-up?)</span></Label>
              <Textarea
                value={nextOutreachNote}
                onChange={e => setNextOutreachNote(e.target.value)}
                placeholder={`e.g., "Met at ChinaBio booth — said to follow up next week with our women's health deck"`}
                className="mt-1 text-sm min-h-[90px]"
                data-testid="next-outreach-note-input"
              />
            </div>
            {isNextOutreach && nextOutreach.flagged_at && (
              <p className="text-[10px] text-slate-400">Flagged {new Date(nextOutreach.flagged_at).toLocaleString()}{nextOutreach.flagged_by ? ` by ${nextOutreach.flagged_by}` : ''}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {isNextOutreach ? (
              <Button
                variant="outline"
                size="sm"
                onClick={removeNextOutreach}
                disabled={nextOutreachSaving}
                className="h-8 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
                data-testid="next-outreach-remove-btn"
              >
                Remove from queue
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setNextOutreachDialogOpen(false)} className="h-8 text-xs" data-testid="next-outreach-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                onClick={saveNextOutreach}
                disabled={nextOutreachSaving}
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="next-outreach-save-btn"
              >
                {nextOutreachSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving...</> : <><Flag className="w-3.5 h-3.5 mr-1" /> {isNextOutreach ? 'Update note' : 'Add to queue'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-violet-600" />
              Merge another record into "{company?.company_name}"
            </DialogTitle>
            <DialogDescription>
              Pick a duplicate biotech record. Its assets, contacts, outreach,
              campaigns, and documents move into this record. The duplicate is
              archived (recoverable from admin audit log).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              autoFocus
              placeholder="Search by name (e.g. S2N.Bio)"
              value={mergeQuery}
              onChange={e => { setMergeQuery(e.target.value); setMergePicked(null); }}
              data-testid="merge-search-input"
            />
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100 bg-white">
              {mergeSearching ? (
                <div className="p-3 text-center text-xs text-slate-500"><Loader2 className="w-4 h-4 mx-auto animate-spin mb-1" />Searching…</div>
              ) : mergeQuery.trim().length < 2 ? (
                <div className="p-4 text-center text-xs text-slate-400">Type at least 2 characters</div>
              ) : mergeResults.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-500">No other biotechs match.</div>
              ) : (
                mergeResults.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setMergePicked(r)}
                    className={`w-full text-left px-3 py-2 hover:bg-violet-50 transition ${mergePicked?.id === r.id ? 'bg-violet-100 ring-1 ring-violet-400' : ''}`}
                    data-testid={`merge-result-${r.id}`}
                  >
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {mergePicked?.id === r.id && <Check className="w-3.5 h-3.5 text-violet-600" />}
                      {r.company_name}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {r.country || '—'} · {(r.company_description || r.description || '').slice(0, 80)}
                    </div>
                  </button>
                ))
              )}
            </div>
            {mergePicked && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
                <div className="font-semibold flex items-center gap-1">
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                  Merge plan
                </div>
                <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                  <li>Survivor: <strong>{company?.company_name}</strong> (this record)</li>
                  <li>Absorbed and archived: <strong>{mergePicked.company_name}</strong></li>
                  <li>All assets, contacts, outreach, campaigns, documents from
                      "{mergePicked.company_name}" move to this record.</li>
                  <li>Empty fields on this record fill from the absorbed record.</li>
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setMergeDialogOpen(false)} data-testid="merge-cancel-btn">Cancel</Button>
            <Button
              disabled={!mergePicked || merging}
              onClick={handleConfirmMerge}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="merge-confirm-btn"
            >
              {merging ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Merging…</> : 'Confirm Merge'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enrich from Text — paste email body / notes, AI extracts, you review, then it saves */}
      <Dialog open={enrichTextOpen} onOpenChange={(o) => { if (!o) { setEnrichTextOpen(false); setEnrichText(''); setEnrichPreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="enrich-from-text-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ClipboardPaste className="w-4 h-4 text-amber-600" /> {enrichPreview ? 'Review extracted intelligence' : 'Enrich profile from pasted text'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {enrichPreview
                ? 'Untick anything you don\'t want saved. The full pasted text is stored on the company as an audit record either way.'
                : 'Paste an email body, meeting notes, or any free-form snippet from this biotech. The AI extracts assets, contacts, stage, partnering intent, funding etc. — you review everything before it\'s saved.'}
            </DialogDescription>
          </DialogHeader>
          {!enrichPreview ? (
            <>
              <Textarea
                value={enrichText}
                onChange={(e) => setEnrichText(e.target.value)}
                placeholder="Paste the email content here — e.g. 'Our lead asset XYZ-001 is currently in Phase 2 for dry AMD… We are actively exploring regional and global partnering opportunities…'"
                className="min-h-[220px] text-xs"
                data-testid="enrich-text-textarea"
              />
              <div className="flex items-center justify-between gap-2 text-[10px] text-slate-400">
                <span>{enrichText.length.toLocaleString()} chars</span>
                <span>Nothing is saved until you confirm on the review screen.</span>
              </div>
            </>
          ) : (
            <div className="space-y-3 text-xs" data-testid="enrich-review-screen">
              {Object.values(enrichPreview.company || {}).some((v) => (v || '').toString().trim()) && (
                <div className="border border-slate-200 rounded-md p-2.5">
                  <label className="flex items-center gap-2 font-semibold text-slate-800">
                    <input type="checkbox" checked={enrichSel.company}
                      data-testid="enrich-review-company-check"
                      onChange={(e) => setEnrichSel({ ...enrichSel, company: e.target.checked })} />
                    Company profile updates
                  </label>
                  <div className="mt-1.5 space-y-0.5 text-slate-600">
                    {Object.entries(enrichPreview.company || {}).filter(([, v]) => (v || '').toString().trim()).map(([k, v]) => (
                      <div key={k}><span className="text-slate-400">{k.replace(/_/g, ' ')}:</span> {String(v).slice(0, 260)}</div>
                    ))}
                  </div>
                </div>
              )}
              {(enrichPreview.contacts || []).length > 0 && (
                <div className="border border-slate-200 rounded-md p-2.5">
                  <div className="font-semibold text-slate-800 mb-1">Contacts</div>
                  {(enrichPreview.contacts || []).map((c, i) => (
                    <label key={i} className="flex items-center gap-2 text-slate-600 py-0.5">
                      <input type="checkbox" checked={!!enrichSel.contacts[i]}
                        data-testid={`enrich-review-contact-${i}`}
                        onChange={(e) => setEnrichSel({ ...enrichSel, contacts: enrichSel.contacts.map((v, j) => (j === i ? e.target.checked : v)) })} />
                      <span className="font-medium text-slate-800">{c.name}</span>
                      {c.designation ? ` · ${c.designation}` : ''}{c.email ? ` · ${c.email}` : ''}
                    </label>
                  ))}
                </div>
              )}
              {(enrichPreview.assets || []).map((a, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-2.5">
                  <label className="flex items-center gap-2 font-semibold text-slate-800">
                    <input type="checkbox" checked={!!enrichSel.assets[i]}
                      data-testid={`enrich-review-asset-${i}`}
                      onChange={(e) => setEnrichSel({ ...enrichSel, assets: enrichSel.assets.map((v, j) => (j === i ? e.target.checked : v)) })} />
                    {a.asset_name}
                    <span className={`text-[9px] font-semibold uppercase rounded px-1 py-0.5 ${a._existing ? 'bg-slate-100 text-slate-500 border border-slate-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                      {a._existing ? 'Update' : 'New'}
                    </span>
                    {a.stage ? <span className="text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">{a.stage}</span> : null}
                  </label>
                  <div className="mt-1.5 space-y-0.5 text-slate-600">
                    {a.indication && <div><span className="text-slate-400">indication:</span> {a.indication}</div>}
                    {a.target && <div><span className="text-slate-400">target:</span> {a.target}</div>}
                    {a.modality && <div><span className="text-slate-400">modality:</span> {a.modality}</div>}
                    {a.partnering_status && (
                      <div className="text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-1" data-testid={`enrich-review-partnering-${i}`}>
                        <span className="font-semibold">Partnering status:</span> {a.partnering_status}
                      </div>
                    )}
                    {a.stage_notes && <div><span className="text-slate-400">stage notes:</span> {String(a.stage_notes).slice(0, 220)}</div>}
                    {a.molecule_description && <div className="text-slate-500">{String(a.molecule_description).slice(0, 260)}…</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            {!enrichPreview ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEnrichTextOpen(false); setEnrichText(''); }} data-testid="enrich-text-cancel-btn">Cancel</Button>
                <Button
                  onClick={handleEnrichFromText}
                  disabled={enrichingFromText || enrichText.trim().length < 30}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="enrich-text-submit-btn"
                >
                  {enrichingFromText ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Extract &amp; Review</>}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setEnrichPreview(null)} disabled={enrichingFromText} data-testid="enrich-review-back-btn">Back</Button>
                <Button
                  onClick={handleEnrichApply}
                  disabled={enrichingFromText}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="enrich-review-confirm-btn"
                >
                  {enrichingFromText ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : <><Check className="w-3.5 h-3.5 mr-1.5" /> Confirm &amp; Save</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset-scoped Enrich from Text */}
      <Dialog open={!!assetEnrichDialog} onOpenChange={(o) => { if (!o) { setAssetEnrichDialog(null); setAssetEnrichText(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-enrich-from-text-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ClipboardPaste className="w-4 h-4 text-amber-600" /> Enrich asset from pasted text
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Paste trial data, phase updates, indications or any free-form notes about
              <span className="font-semibold text-slate-700"> {assetEnrichDialog?.asset?.asset_name}</span>.
              The AI merges them into this asset only — the company profile and other assets are untouched.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={assetEnrichText}
            onChange={(e) => setAssetEnrichText(e.target.value)}
            placeholder="e.g. 'AK112 met its primary PFS endpoint in the Phase III HARMONi-2 trial vs pembrolizumab in PD-L1+ NSCLC; NMPA approval expanded to first-line non-squamous NSCLC. Additional Phase III in TNBC ongoing (NCT0xxxxxxx).'"
            className="min-h-[220px] text-xs"
            data-testid="asset-enrich-text-textarea"
          />
          <div className="flex items-center justify-between gap-2 text-[10px] text-slate-400">
            <span>{assetEnrichText.length.toLocaleString()} chars</span>
            <span>Existing values are preserved — only new detail is merged in.</span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => { setAssetEnrichDialog(null); setAssetEnrichText(''); }} data-testid="asset-enrich-cancel-btn">Cancel</Button>
            <Button
              onClick={handleEnrichAssetFromText}
              disabled={assetEnriching || assetEnrichText.trim().length < 20}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="asset-enrich-submit-btn"
            >
              {assetEnriching ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Enriching…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Enrich Asset</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aug 2026 · Deck-grounded profile regeneration preview (⭐ button) */}
      <Dialog open={!!regenDialog} onOpenChange={(o) => { if (!o && !regenDialog?.applying) setRegenDialog(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="regen-grounded-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="w-4 h-4 text-fuchsia-500" /> Regenerate Profile from Documents — {regenDialog?.asset?.asset_name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Grounded strictly in your uploaded documents — no AI background knowledge, no web. Nothing is saved until you click Apply.
            </DialogDescription>
          </DialogHeader>
          {regenDialog?.loading && (
            <div className="py-10 flex flex-col items-center gap-2 text-sm text-slate-500" data-testid="regen-loading">
              <Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" />
              Reading uploaded documents and drafting a grounded profile…
            </div>
          )}
          {regenDialog?.error && (
            <div className="py-4 px-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="regen-error">
              {regenDialog.error}
            </div>
          )}
          {regenDialog?.preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Sources used</span>
                {(regenDialog.sources || []).map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">{s}</span>
                ))}
              </div>
              {!regenDialog.assetFound && (
                <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-700" data-testid="regen-not-found">
                  The uploaded documents don't appear to contain information about this specific asset, so no profile was generated. Upload a deck that covers {regenDialog.asset?.asset_name}, or use "Enrich from text" to paste material.
                </div>
              )}
              {regenDialog.assetFound && (
                <>
                  {[
                    { k: 'molecule_description', ck: 'molecule_description', label: 'Description' },
                    { k: 'objectives', ck: 'objectives', label: 'Objectives' },
                    { k: 'notes', ck: 'notes', label: 'Notes' },
                    { k: 'indication_clean', ck: 'indication', label: 'Indication' },
                    { k: 'differentiation', ck: 'differentiation', label: 'Differentiation' },
                  ].filter(f => (regenDialog.preview[f.k] || '').trim()).map(f => (
                    <div key={f.k} className="border border-slate-200 rounded-md overflow-hidden">
                      <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wide font-semibold text-slate-500">{f.label}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                        <div className="p-3">
                          <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Current</div>
                          <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{(regenDialog.current?.[f.ck] || '').trim() || '(empty)'}</p>
                        </div>
                        <div className="p-3 bg-emerald-50/30">
                          <div className="text-[9px] uppercase tracking-wide text-emerald-600 mb-1">From documents</div>
                          <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{regenDialog.preview[f.k]}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(regenDialog.preview.source_quotes || []).length > 0 && (
                    <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Supporting snippets from your documents</div>
                      {regenDialog.preview.source_quotes.map((q, i) => (
                        <p key={i} className="text-[11px] text-slate-600 italic leading-snug">“{q}”</p>
                      ))}
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-end gap-3 pt-1">
                <Button variant="outline" disabled={regenDialog.applying} onClick={() => setRegenDialog(null)}>Cancel</Button>
                {regenDialog.assetFound && (
                  <Button onClick={applyRegenGrounded} disabled={regenDialog.applying} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="regen-apply-btn">
                    {regenDialog.applying ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Applying…</> : 'Apply to asset'}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-slate-400">Applying backs up the current version on the asset (last 5 kept). Structured fields (phase, modality, TA) are never touched.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Enrichment Report modal — shown AFTER "Enrich from Documents" finishes.
          Feb 2026 · gives the operator a full per-doc breakdown of what was
          filled, what was overwritten because AI confidence hit 0.9+, what was
          skipped because existing CRM data was already strong, and which
          assets were fuzzy-matched vs newly created. */}
      <Dialog open={enrichReportOpen} onOpenChange={setEnrichReportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="enrich-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="w-4 h-4 text-amber-600" /> Enrichment Report
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Per-document breakdown of what the AI extracted, filled, or overwrote based on confidence.
              Fields with existing strong CRM data are only replaced when the AI is ≥90% confident.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(enrichReport || []).map((r, idx) => (
              <div key={idx} className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid={`enrich-report-doc-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-800 truncate">{r.document || '(unnamed)'}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                  {(r.fields_filled || []).length > 0 && (
                    <div>
                      <div className="text-emerald-700 font-semibold">Filled ({r.fields_filled.length})</div>
                      <div className="text-slate-600">{r.fields_filled.join(', ')}</div>
                    </div>
                  )}
                  {(r.fields_overwrote_high_conf || []).length > 0 && (
                    <div>
                      <div className="text-amber-700 font-semibold">Overwrote high-conf ({r.fields_overwrote_high_conf.length})</div>
                      <div className="text-slate-600">{r.fields_overwrote_high_conf.join(', ')}</div>
                    </div>
                  )}
                  {(r.fields_skipped_strong_existing || []).length > 0 && (
                    <div>
                      <div className="text-slate-500 font-semibold">Skipped (existing strong)</div>
                      <div className="text-slate-500">{r.fields_skipped_strong_existing.join(', ')}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sky-700 font-semibold">Assets updated ({(r.assets_updated || []).length})</div>
                    <div className="text-slate-600">
                      {(r.assets_updated || []).length === 0 ? '—' : r.assets_updated.map(a => `${a.name} (${a.score}%)`).join(', ')}
                    </div>
                  </div>
                  {r.assets_matched_no_change > 0 && (
                    <div>
                      <div className="text-slate-500 font-semibold">Matched, no change</div>
                      <div className="text-slate-500">{r.assets_matched_no_change} asset(s)</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!enrichReport || enrichReport.length === 0) && (
              <div className="text-xs text-slate-500 text-center py-6">No detailed report available for this run.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEnrichReportOpen(false)} data-testid="enrich-report-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
